import { Fragment, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Minus, Loader2, Eye, RotateCcw, FileImage, FilePlus2, Upload, X, Trash2, Sparkles, Film, RefreshCw, Globe, ImagePlus, Image as ImageIcon, Languages, Download, Mail, ShieldCheck, ShieldOff, KeyRound, Megaphone, Pin, PinOff, ChevronDown, ShieldAlert, Briefcase, UserX, Truck, Phone, History, Paperclip, Link2 } from "lucide-react";
import { TwoFactorCard } from "../components/admin/TwoFactorCard";
import { SmsEtapesBox } from "../components/SmsEtapesBox";
import { StallionLabel } from "../components/admin/StallionLabel";

// TOUTES les sections de l'admin ouvrables à un vendeur (demande du propriétaire :
// « ajouter toutes les sections à cocher ») avec choix PAR PAGE lecteur/éditeur
const VENDEUR_SECTION_LABELS = {
  apercu: "Aperçu", soumissions: "Ventes & Soumissions", commandes: "Commandes",
  paniers: "Paniers", rapports: "Rapports", produits: "Gestion boutique", forfaits: "Forfaits",
  occasions: "Occasions", zones: "Catalogues", clients: "Clients",
  prospects: "Prospects", avis: "Avis", assistant: "Clavardage en direct", blogue: "Blogue", campagnes: "Campagnes",
  publicites: "Publicités IA", motscles: "Mots-clés", backlinks: "Backlinks",
  pages: "Pages (contenu)", parametres: "Paramètres", realisations: "Réalisations",
  rebonds: "Rebonds",
};
const VENDEUR_SECTION_GROUPS = [
  ["Ventes", ["soumissions", "commandes", "paniers", "rapports"]],
  ["Boutique & catalogues", ["produits", "zones", "forfaits", "occasions"]],
  ["Clients", ["clients", "prospects", "avis", "rebonds"]],
  ["Marketing", ["blogue", "campagnes", "publicites", "motscles", "backlinks", "pages", "realisations"]],
  ["Système", ["apercu", "parametres"]],
];
import { api, API_BASE, fmt, formatApiError } from "../lib/api";
import { fmtDateMtl, fmtDateTimeMtl, dayKeyMtl, TZ_MONTREAL } from "../lib/dateMtl";
import { itemUnitPrice, unitPrice } from "../lib/pricing";
import { useAuth } from "../context/AuthContext";
import { FadeUp } from "../components/motion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { catalogueLabel, CATALOGUE_NAMES, FAMILY_NAMES, catalogueFamily, catalogueShortLabel, SPORTS, plur } from "../lib/localize";
import { translations } from "../i18n/translations";
import { SECTION_DEFS } from "../lib/shopSettings";
import CatalogueZonesEditor from "../components/admin/CatalogueZonesEditor";
import ContentEditor from "../components/admin/ContentEditor";
import AdsManager from "../components/admin/AdsManager";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)
import AdminOccasions from "../components/admin/AdminOccasions";
import RealisationsTab from "../components/admin/RealisationsTab";
import LiveVisitors from "../components/admin/LiveVisitors";
import CarteVisiteurs from "../components/admin/CarteVisiteurs";
import ThemeSaisonCard from "../components/admin/ThemeSaisonCard";
import EngagedVisitors from "../components/admin/EngagedVisitors";
import ProspectStats from "../components/admin/ProspectStats";
import QuoteRequestsTab from "../components/admin/QuoteRequestsTab";
import CallRequestsCard from "../components/admin/CallRequestsCard";
import { buildCallbackHours } from "../components/CallRequestDialog";
import TaxReportTab from "../components/admin/TaxReportTab";
import ForfaitStats from "../components/admin/ForfaitStats";
import ForfaitSoumissions from "../components/admin/ForfaitSoumissions";
import CatalogueUpload from "../components/admin/CatalogueUpload";
import SeoPositions from "../components/admin/SeoPositions";
import IndexationCard from "../components/admin/IndexationCard";
import TraductionCard from "../components/admin/TraductionCard";
import ChatbotStats from "../components/admin/ChatbotStats";
import MessengerBotStats from "../components/admin/MessengerBotStats";
import TonRobotCard from "../components/admin/TonRobotCard";
import MotsClesTourneeCard from "../components/admin/MotsClesTourneeCard";
import PositionsGoogleCard from "../components/admin/PositionsGoogleCard";
import RechercheFusionClients from "../components/admin/RechercheFusionClients";
import { CompteurBienvenue } from "../components/admin/CompteurBienvenue";
import WebhookStatutChip from "../components/admin/WebhookStatutChip";
import ProtectionPhotosCard from "../components/admin/ProtectionPhotosCard";
import SignatureLivraison from "../components/admin/SignatureLivraison";
import FaqEnrichi from "../components/admin/FaqEnrichi";
import AdminReviews from "../components/admin/AdminReviews";
import AnalyticsCard from "../components/admin/AnalyticsCard";
import RelancesStats from "../components/admin/RelancesStats";
import RapportRetards from "../components/admin/RapportRetards";
import PrixCoutantCard from "../components/admin/PrixCoutantCard";
import LogoFichiersCard from "../components/admin/LogoFichiersCard";
import VendeurPrixCoutantCard from "../components/admin/VendeurPrixCoutantCard";
import RendezVousAdminCard from "../components/admin/RendezVousAdminCard";
import CodesCollisionsCard from "../components/admin/CodesCollisionsCard";
import Staff2FACard from "../components/admin/Staff2FACard";
import HotProducts from "../components/admin/HotProducts";
import ExpandableText from "../components/admin/ExpandableText";
import GravureLignes from "../components/GravureLignes";
import CampaignsTab from "../components/admin/CampaignsTab";
import ProspectsScraper from "../components/admin/ProspectsScraper";
import RebondsTab from "../components/admin/RebondsTab";
import DelivrabiliteCard from "../components/admin/DelivrabiliteCard";
import BacklinksTab from "../components/admin/BacklinksTab";
import BulkProductTools from "../components/admin/BulkProductTools";
import { KEYWORDS_EN, KEYWORDS_FR, mergeKeywords } from "../components/SEO";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { SEO } from "../components/SEO";

export const STATUS_LABELS = {
  en_attente_paiement: "En attente de paiement",
  payee: "Payée",
  en_production: "En production",
  expediee: "Expédiée",
  livree: "Livrée",
  annulee: "Annulée",
  remboursee: "Remboursée",
};

export function StatusBadge({ status, label }) {  const styles = {
    en_attente_paiement: "border-amber-400/40 text-amber-300 bg-amber-400/10",
    payee: "border-gold/50 text-gold bg-gold/10",
    en_production: "border-sky-400/40 text-sky-300 bg-sky-400/10",
    expediee: "border-violet-400/40 text-violet-300 bg-violet-400/10",
    livree: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10",
    annulee: "border-red-400/40 text-red-300 bg-red-400/10",
    remboursee: "border-orange-400/40 text-orange-300 bg-orange-400/10",
  };
  return (
    <span data-testid={`status-badge-${status}`} className={`text-xs px-3 py-1.5 rounded-full border ${styles[status] || ""}`}>
      {label || STATUS_LABELS[status] || status}
    </span>
  );
}


// Champs communs aux fenêtres « Ajouter un code » et « Modifier le code »
const PROMO_INPUT = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none";
const PROMO_LABEL = "block text-[10px] uppercase tracking-widest text-ivory/65 mb-1";

function PromoFormFields({ form, setForm, idPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="sm:col-span-2">
        <span className={PROMO_LABEL}>Nom du code</span>
        <input data-testid={`${idPrefix}-code`} required minLength={2} maxLength={30} placeholder="ex. WELCOME10"
          value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
          className={`${PROMO_INPUT} uppercase tracking-wider`} />
      </label>
      <label>
        <span className={PROMO_LABEL}>Rabais</span>
        <div className="flex gap-2">
          <select data-testid={`${idPrefix}-kind`} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none">
            <option value="pct">%</option>
            <option value="fixed">$</option>
          </select>
          <input data-testid={`${idPrefix}-value`} required type="number" step="0.01" min="0.01" max={form.kind === "pct" ? 100 : 10000}
            placeholder={form.kind === "pct" ? "Rabais %" : "Rabais $"}
            value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
            className={`flex-1 min-w-0 ${PROMO_INPUT}`} />
        </div>
      </label>
      <label>
        <span className={PROMO_LABEL}>Portée</span>
        <select data-testid={`${idPrefix}-scope`} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
          className={PROMO_INPUT}>
          <option value="order">Commande complète</option>
          <option value="products">Produits seulement</option>
          <option value="inscription">Inscriptions seulement</option>
        </select>
      </label>
      <label className="sm:col-span-2">
        <span className={PROMO_LABEL}>Condition</span>
        <select data-testid={`${idPrefix}-condition`} value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
          className={PROMO_INPUT}>
          <option value="aucune">Condition : aucune</option>
          <option value="inscription">Sur inscription</option>
          <option value="distribution">Ajout à l'info-lettre (courriel)</option>
          <option value="referencement">Sur référencement</option>
          <option value="autre">Autre (à préciser)</option>
        </select>
      </label>
      {form.condition === "autre" && (
        <label className="sm:col-span-2">
          <span className={PROMO_LABEL}>Précisez la condition</span>
          <input data-testid={`${idPrefix}-condition-detail`} required minLength={2} maxLength={140}
            value={form.condition_detail} onChange={(e) => setForm({ ...form, condition_detail: e.target.value })}
            className={PROMO_INPUT} />
        </label>
      )}
      <label>
        <span className={PROMO_LABEL}>Date de début</span>
        <input data-testid={`${idPrefix}-valid-from`} required type="date" value={form.valid_from}
          onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className={PROMO_INPUT} />
      </label>
      <label>
        <span className={PROMO_LABEL}>Date de fin</span>
        <input data-testid={`${idPrefix}-valid-to`} required type="date" value={form.valid_to}
          onChange={(e) => setForm({ ...form, valid_to: e.target.value })} className={PROMO_INPUT} />
      </label>
      <label className="sm:col-span-2">
        <span className={PROMO_LABEL}>Nb max d'utilisations (optionnel)</span>
        <input data-testid={`${idPrefix}-max-uses`} type="number" min="1" placeholder="Illimité si vide"
          value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={PROMO_INPUT} />
      </label>
      <fieldset className="sm:col-span-2">
        <span className={PROMO_LABEL}>Usage unique par (optionnel)</span>
        <div className="flex gap-5 flex-wrap mt-1">
          {[["email", "Courriel"], ["phone", "Téléphone"], ["address", "Adresse postale"]].map(([v, lbl]) => (
            <label key={v} className="flex items-center gap-2 text-sm text-ivory/70 cursor-pointer">
              <input type="checkbox" data-testid={`${idPrefix}-single-${v}`}
                checked={(form.single_use || []).includes(v)}
                onChange={(e) => setForm({ ...form, single_use: e.target.checked ? [...(form.single_use || []), v] : (form.single_use || []).filter((x) => x !== v) })}
                className="accent-gold" />
              {lbl}
            </label>
          ))}
        </div>
        <p className="text-xs text-ivory/65 mt-1">Le code ne pourra pas être réutilisé avec la même identité (selon les cases cochées). La connexion est obligatoire pour utiliser un coupon.</p>
      </fieldset>
    </div>
  );
}


const DeleteConfirmButton = ({ testid, label, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid={testid} className="text-ivory/70 hover:text-red-400 transition-colors duration-300">
        <Trash2 className="w-4 h-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-surface border-gold/20 text-ivory">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-2xl">Confirmer la suppression</AlertDialogTitle>
        <AlertDialogDescription className="text-ivory/60">{label} Cette action est irréversible.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="border-ivory/20 text-ivory/70 hover:bg-ivory/5">Annuler</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onDelete} className="bg-red-500 text-white hover:bg-red-400">
          Supprimer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Retrait d'accès (admin/vendeur) SANS supprimer le dossier client — action
// RÉVERSIBLE (demande du propriétaire 2026-10-06) : icône distincte de la poubelle
// (ambre, pas rouge) et dialogue qui dit clairement que la fiche est conservée
const RevokeConfirmButton = ({ testid, label, onRevoke, icon: Icon = UserX }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid={testid} title="Retirer l'accès — le dossier client est conservé"
        className="text-ivory/70 hover:text-amber-300 transition-colors duration-300">
        <Icon className="w-4 h-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-surface border-gold/20 text-ivory">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-2xl">Retirer l'accès ?</AlertDialogTitle>
        <AlertDialogDescription className="text-ivory/60">{label} Le dossier client est conservé — l'accès pourra être redonné plus tard.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="border-ivory/20 text-ivory/70 hover:bg-ivory/5">Annuler</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onRevoke} className="bg-amber-500 text-ink font-semibold hover:bg-amber-400">
          Retirer l'accès
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const EMPTY_PRODUCT = { name: "", category: "Trophées", tagline: "", description: "", image: "", images: [], image_ruban: "", image_insert: "", group: "", shop_category: "Trophées", shop_subcategory: "", base_price: 49.99, cost_price: "", active: true, sku: "", catalogue_no: "", stock: "", inscription_fees: { gravure: 7.0, impression: 9.0, arriere: 0 }, sizes: [{ id: "s1", label: "", extra: 0 }], finishes: [], price_tiers: [], tiers: [] };

const custName = (c) => c?.full_name || [c?.first_name, c?.last_name].filter(Boolean).join(" ") || "—";
// Liens de suivi publics par transporteur (« Autre » : pas d'URL connue — texte brut)
const CARRIER_TRACK_URL = {
  postes_canada: "https://www.canadapost-postescanada.ca/track-reperage/fr#/search?searchFor={n}",
  ups: "https://www.ups.com/track?tracknum={n}&loc=fr_CA",
  fedex: "https://www.fedex.com/fedextrack/?trknbr={n}",
  purolator: "https://www.purolator.com/fr/shipping/tracker?pin={n}",
  dhl: "https://www.dhl.com/ca-fr/home/suivi.html?tracking-id={n}",
  gls: "https://ship.gls-canada.com/tracking/{n}",
  ics: "https://www.icscourier.ca/usercontrols/TrackingViewDetail.aspx?TrackNumber={n}&Lan=FR&sign=False",
  stallion: "https://track.stallionexpress.ca/{n}",
};
const trackingUrlFor = (carrier, n) => (CARRIER_TRACK_URL[carrier] && n ? CARRIER_TRACK_URL[carrier].replace("{n}", n) : "");
const custAddress = (c) => [c?.address1 || c?.address, c?.address2].filter(Boolean).join(" · ");

// Historique des ventes du client (demande du propriétaire, 2026-10-06) : TOUTES
// ses soumissions ET commandes boutique regroupées dans la fiche, chacune avec
// son fil d'étapes datées (déplié au clic) — suivi complet par client.
const CustomerHistory = ({ customerId }) => {
  const [hist, setHist] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setHist(null); setErr(false);
    api.get(`/admin/customers/${customerId}/history`)
      .then(({ data }) => setHist(data))
      .catch(() => setErr(true));
  }, [customerId]);
  const fmtD = (iso) => fmtDateTimeMtl(iso);  // heure de Montréal (règle permanente)
  return (
    <div className="mt-6 border-t border-ivory/10 pt-4" data-testid="customer-history">
      <p className="text-[11px] uppercase tracking-widest text-gold mb-3 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> Historique des ventes
      </p>
      {err && <p className="text-xs text-red-300" data-testid="customer-history-error">Historique indisponible pour le moment.</p>}
      {!hist && !err && (
        <p className="text-xs text-ivory/50 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</p>
      )}
      {hist && hist.ventes.length === 0 && (
        <p className="text-xs text-ivory/50" data-testid="customer-history-empty">Aucune vente pour ce client pour l'instant.</p>
      )}
      {hist && hist.ventes.length > 0 && (
        <div className="space-y-2.5 max-h-80 overflow-y-auto pr-1" data-lenis-prevent>
          {hist.ventes.map((v) => (
            <details key={`${v.kind}-${v.id}`} className="rounded-lg border border-ivory/10 bg-ink/40"
              data-testid={`customer-history-${v.kind}-${v.id}`}>
              <summary className="cursor-pointer px-3.5 py-2.5 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 hover:text-gold transition-colors list-none">
                <span className={`text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${v.kind === "commande" ? "border-sky-400/40 text-sky-300" : "border-gold/40 text-gold"}`}>
                  {v.kind === "commande" ? "Boutique" : "Soumission"}
                </span>
                <span className="text-ivory/70">{fmtD(v.created_at)}</span>
                {v.invoice_no && <span className="text-ivory/50">nº {v.invoice_no}</span>}
                <span className="text-ivory/70">{v.articles} article{v.articles > 1 ? "s" : ""}</span>
                <span className={v.paye ? "text-emerald-300 font-semibold" : "text-amber-300"}>
                  {Number(v.total || 0).toFixed(2)} ${v.paye ? " — payée" : ""}
                </span>
                {v.receipt_url && (
                  <a href={v.receipt_url} target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`customer-history-receipt-${v.id}`}
                    className="text-gold hover:underline">Reçu Stripe</a>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-ivory/40 ml-auto" />
              </summary>
              <ul className="px-4 pb-3 pt-2 space-y-1 border-t border-ivory/10">
                {(v.events || []).map((e, i) => (
                  <li key={i} className="text-[11px] text-ivory/65 flex gap-2">
                    <span className="text-ivory/40 shrink-0 w-32">{fmtD(e.at)}</span>
                    <span>{e.label}</span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

// Calendrier des saisons (Paramètres) : sport présélectionné sur la page Forfaits
// pour chaque mois — les défauts reflètent SEASON_SPORT_BY_MONTH du backend
const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const SEASON_SPORTS = ["Hockey", "Soccer", "Football", "Basketball", "Volleyball", "Natation", "Baseball", "Golf", "Danse", "Autre"];
const DEFAULT_SEASON_CALENDAR = {
  "1": "Hockey", "2": "Hockey", "3": "Hockey", "4": "Soccer", "5": "Soccer", "6": "Soccer",
  "7": "Soccer", "8": "Soccer", "9": "Football", "10": "Hockey", "11": "Hockey", "12": "Hockey",
};

// Sous-titre de regroupement par sous-catégorie (pages Aperçu et Paramètres)
const SubHead = ({ children, testid }) => (
  <p data-testid={testid} className="text-[10px] uppercase tracking-[0.25em] text-gold/70 mt-10 border-b border-ivory/10 pb-2">{children}</p>
);

// Liste des mots-clés SEO en pastilles, chacune effaçable d'un clic sur le ×
const KeywordChips = ({ value, onRemove, testId }) => {
  const kws = (value || "").split(",").map((k) => k.trim()).filter(Boolean);
  if (!kws.length) {
    return <p className="text-xs text-ivory/45 italic mt-2">Aucun mot-clé — la liste par défaut du site est utilisée.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testId}>
      {kws.map((k, i) => (
        <span key={`${k}-${i}`} data-testid={`${testId}-${i}`}
          className="inline-flex items-center gap-1.5 text-xs bg-ink border border-ivory/15 rounded-full px-2.5 py-1">
          {k}
          <button type="button" title={`Retirer « ${k} »`} data-testid={`${testId}-del-${i}`}
            onClick={() => onRemove(k)}
            className="text-ivory/50 hover:text-red-400 transition-colors duration-300">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
  );
};

const EMPTY_POST = { title: "", title_en: "", excerpt: "", excerpt_en: "", content: "", content_en: "", image: "", published: true };

export default function Admin() {
  const { user } = useAuth();
  // Onglet conservé dans l'URL (?onglet=soumissions) — actualiser la page y REVIENT
  // (demande du propriétaire) ; le lien devient partageable et le bouton retour fonctionne.
  // Onglet PAR DÉFAUT : « Ventes & Soumissions » (demande du propriétaire — l'outil de
  // tous les jours s'ouvre en premier ; « Aperçu » reste accessible par son bouton)
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("onglet") || "soumissions";
  const setTab = (t) => setSearchParams((prev) => {
    const n = new URLSearchParams(prev);
    if (t === "soumissions") n.delete("onglet"); else n.set("onglet", t);
    return n;
  }, { replace: true });
  const [adPrefill, setAdPrefill] = useState(null);  // fiche produit à transformer en pub IA
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [prodQ, setProdQ] = useState("");
  const [prodPage, setProdPage] = useState(1);
  const [prodCatalogue, setProdCatalogue] = useState("");  // filtre par nº de catalogue
  const [prodSport, setProdSport] = useState("");  // filtre par sport (mots-clés multi-catalogues)
  const [prodSel, setProdSel] = useState({});      // produits cochés (regroupement — demande du propriétaire)
  const [groupName, setGroupName] = useState("");  // nom du regroupement à appliquer
  const [prodBulkBusy, setProdBulkBusy] = useState("");  // "active" | "group" pendant l'action en lot
  const [prodSansCoutant, setProdSansCoutant] = useState(false);  // filtre « Sans coûtant » (Gestion boutique)
  const [photoPreview, setPhotoPreview] = useState(null);  // photo plein écran (retour sans rien perdre)
  const [prodTotal, setProdTotal] = useState(0);
  const [catCounts, setCatCounts] = useState({});
  const [customers, setCustomers] = useState([]);
  const [carts, setCarts] = useState([]);            // paniers actifs des clients (onglet Paniers)
  const [showCalls, setShowCalls] = useState(false);   // popup « Demandes d'appel » (pilule de la rangée Ventes)
  const [callPending, setCallPending] = useState(0);   // badge de la pilule : appels en attente
  // Compteur de la pilule « Demandes d'appel » (demande du propriétaire) — la
  // liste complète s'ouvre en popup ; rechargé à l'arrivée et à la fermeture
  const loadCalls = () => api.get("/admin/call-requests").then(({ data }) => setCallPending(data.pending ?? 0)).catch(() => {});
  useEffect(() => { loadCalls(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [cartPriceEdit, setCartPriceEdit] = useState({});  // {`${userId}:${index}`: valeur saisie}
  const [cartQtyBusy, setCartQtyBusy] = useState("");  // article du panier en cours de modif/suppression
  // Fiche client ouverte depuis l'onglet Paniers (demande du propriétaire) :
  // coordonnées, adresse, historique d'achat, soumissions, IP observées
  const [clientFile, setClientFile] = useState(null);
  const [clientFileBusy, setClientFileBusy] = useState("");
  const [zoomPj, setZoomPj] = useState(null);  // pièce jointe de la fiche client agrandie en popup (demande du propriétaire, 2026-10-28)
  const [pjAttente, setPjAttente] = useState([]);  // chemins en file « prochain courriel » du client ouvert
  const [pjAttenteListe, setPjAttenteListe] = useState([]);  // file complète [{path, nom, at}] — visible dans la fiche (demande du propriétaire, 2026-10-28)
  const openClientFile = async (c) => {
    setClientFileBusy(c.user_id);
    try {
      const { data } = await api.get(`/admin/client-file/${c.user_id}`);
      setClientFile(data);
      // File « prochain courriel » du client (quelles P.J. sont déjà marquées)
      api.get("/admin/pj-attente", { params: { email: data.email } })
        .then((r) => { const f = r.data.files || []; setPjAttente(f.map((x) => x.path)); setPjAttenteListe(f); })
        .catch(() => { setPjAttente([]); setPjAttenteListe([]); });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Fiche client introuvable");
    } finally {
      setClientFileBusy("");
    }
  };
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [clientsView, setClientsView] = useState("tous");  // "tous" | "distribution" | "desabonnes" | "admins" | "vendeurs"
  const [vendeurs, setVendeurs] = useState(null);          // comptes vendeurs (vue « Vendeurs », demande du propriétaire)
  const [permsVendeur, setPermsVendeur] = useState(null);  // vendeur dont on règle les cases (sections/actions/clients)
  const [admins, setAdmins] = useState(null);              // comptes administrateurs (vue « admins »)
  const [viewingAdmin, setViewingAdmin] = useState(null);  // fiche admin affichée
  const [pwdAdmin, setPwdAdmin] = useState(null);          // admin dont on change le mot de passe
  const [pwdValue, setPwdValue] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);  // aperçu du courriel (sans envoi)

  // « Voir sans envoyer » — kind: confirmation | expediee | livree | parrainage
  const openEmailPreview = async (kind = "confirmation") => {
    try {
      const { data } = await api.get("/admin/test-order-email-preview", { params: { kind } });
      setEmailPreview({ ...data, kind });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const [nlSubs, setNlSubs] = useState([]);  // liste de l'info-lettre complète (incl. abonnés sans compte)
  const [nlOpen, setNlOpen] = useState(false);
  const [nlSubject, setNlSubject] = useState("");
  const [nlMessage, setNlMessage] = useState("");
  const [nlImgBusy, setNlImgBusy] = useState(false);  // illustration Gemini en génération
  const [nlSending, setNlSending] = useState(false);
  const [backupSending, setBackupSending] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupRestoring, setBackupRestoring] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);   // état sauvegarde (build_status/build_done/build_total/size_kb)
  const backupPollRef = useRef(null);
  const [mediaArchive, setMediaArchive] = useState(null);   // archive photos/vidéos en parties (status/done/total/parts)
  const mediaPollRef = useRef(null);
  const restoreInputRef = useRef(null);
  const [aboutDraft, setAboutDraft] = useState(null);
  const [aboutBusy, setAboutBusy] = useState(false);
  const [shopSettings, setShopSettings] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [aiTopic, setAiTopic] = useState("");
  const [aiGuide, setAiGuide] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuickTopic, setAiQuickTopic] = useState("");
  const [aiQuickLoading, setAiQuickLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prodFacets, setProdFacets] = useState({ categories: [], groups: [] });  // listes réelles (catégories/groupes) pour les formulaires
  const [detail, setDetail] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrierOther, setCarrierOther] = useState("");  // nom libre si transporteur « Autre »
  const [shipCost, setShipCost] = useState("");          // prix COÛTANT de l'envoi (admin seulement)
  // Correction des prix d'une commande MÊME PAYÉE (demande du propriétaire)
  const [priceEdit, setPriceEdit] = useState(false);
  const [priceDrafts, setPriceDrafts] = useState({});   // {index: prix unitaire}
  const [priceBusy, setPriceBusy] = useState(false);
  const saveOrderPrices = async () => {
    if (!detail) return;
    setPriceBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${detail.order_number}/prices`, { prices: priceDrafts });
      toast.success(`Prix corrigés — nouveau total : ${data.total.toFixed(2)} $`);
      const patch = { items: detail.items.map((it, k) => (priceDrafts[k] !== undefined
        ? { ...it, unit_price: parseFloat(String(priceDrafts[k]).replace(",", ".")), line_total: Math.round(parseFloat(String(priceDrafts[k]).replace(",", ".")) * (it.qty || 1) * 100) / 100, price_override: true }
        : it)), subtotal: data.subtotal, taxes: data.taxes, total: data.total };
      setDetail((d) => ({ ...d, ...patch }));
      setOrders((prev) => prev.map((o) => (o.order_number === detail.order_number ? { ...o, ...patch } : o)));
      setPriceEdit(false); setPriceDrafts({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPriceBusy(false);
    }
  };
  const [carrier, setCarrier] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [opinlyPosts, setOpinlyPosts] = useState(null);
  const [opinlyLoading, setOpinlyLoading] = useState(false);
  const [opinlySyncing, setOpinlySyncing] = useState(false);
  const [opinlyImporting, setOpinlyImporting] = useState("");
  const [soraPost, setSoraPost] = useState(null);
  const [soraForm, setSoraForm] = useState({ prompt: "", size: "1280x720", duration: 4 });
  const [soraLoading, setSoraLoading] = useState(false);
  const [soroPosts, setSoroPosts] = useState(null);
  const [soroLoading, setSoroLoading] = useState(false);
  const [soroSyncing, setSoroSyncing] = useState(false);
  const [soroImporting, setSoroImporting] = useState("");
  const [translating, setTranslating] = useState(false);
  const [tierTemplate, setTierTemplate] = useState([{ min_qty: 10, pct: 10 }, { min_qty: 25, pct: 18 }, { min_qty: 50, pct: 25 }]);
  const [tierScope, setTierScope] = useState("catalogues");
  const [applyingTiers, setApplyingTiers] = useState(false);
  const [genImages, setGenImages] = useState({ running: false, done: 0, total: 0, errors: 0 });
  const [feeGravure, setFeeGravure] = useState("15");
  const [feeImpression, setFeeImpression] = useState("10");
  const [applyingFees, setApplyingFees] = useState(false);
  const [promos, setPromos] = useState([]);
  const [gscStatus, setGscStatus] = useState(null);  // connexion Google Search Console (rapport mensuel)
  const [visitesBots, setVisitesBots] = useState(null);  // visites robots/IA du mois (Rapports)
  const [fileCourriels, setFileCourriels] = useState(null);  // file des courriels en chiffres (Rapports)
  const [journalEnvois, setJournalEnvois] = useState(null);  // journal des courriels partis, jour par jour (Rapports)
  const [showJour, setShowJour] = useState(null);  // jour du journal déplié
  const [showDevant, setShowDevant] = useState(false);  // détail « passées devant » déplié (clic sur le compteur)
  const [sondeEtat, setSondeEtat] = useState(null);  // sonde « site en panne » (bandeau admin)
  const [seoPinging, setSeoPinging] = useState(false);  // ping moteurs de recherche en cours
  const [gcalStatus, setGcalStatus] = useState(null); // connexion Google Agenda (rappels d'appel datés)
  const [cbStart, setCbStart] = useState("07:00");    // heures de rappel proposées (réglage Paramètres)
  const [cbEnd, setCbEnd] = useState("21:00");
  // Délais des relances automatiques, en heures (réglage Paramètres, 2026-10-06)
  const [relProof, setRelProof] = useState(48);   // rappel d'épreuve au client
  const [inactStaff, setInactStaff] = useState(48); // déconnexion du personnel après inactivité (réglable, admin seul)
  const [seuilMarteau, setSeuilMarteau] = useState(120); // seuil du blocage auto anti-marteau, req/min (réglable, admin seul)
  const [relQuote, setRelQuote] = useState(24);   // rappel atelier « soumission sans réponse »
  const [relPay, setRelPay] = useState(72);       // rappel de paiement au client
  const [relAddr, setRelAddr] = useState(48);     // relance « adresse non confirmée » après acceptation (2026-10-19)
  // Relances « passé dû » répétées (demande du propriétaire, 2026-10-27) :
  // 1re relance N jours après l'échéance de l'entente, puis aux N jours
  const [relPdDelai, setRelPdDelai] = useState(3);
  const [relPdInter, setRelPdInter] = useState(2);
  // Seuil « gros tournoi » pour l'avis Google 7 j après livraison (demande du
  // propriétaire, 2026-10-27) : pièces OU montant — réglable ici
  const [avisTournPieces, setAvisTournPieces] = useState(30);
  const [avisTournMontant, setAvisTournMontant] = useState(500);
  const [banniereBusy, setBanniereBusy] = useState(false);
  const [addrNote, setAddrNote] = useState("");   // mot personnel en tête des courriels d'adresses (2026-10-19)
  const [courrielApercu, setCourrielApercu] = useState(null); // aperçu d'un courriel de relance {titre, subject, html} (2026-10-20)
  const [testCode, setTestCode] = useState("");   // code d'accès au rapport des ventes TEST
  const [coPct, setCoPct] = useState("5");        // % de la contre-offre automatique (retrait pour raison de prix)
  const [objectifMens, setObjectifMens] = useState("");  // objectif de ventes du mois ($ TTC) — ligne sur la courbe (2026-10-22)
  const [comptableEmail, setComptableEmail] = useState(""); // courriel du comptable — reçoit le PDF groupé des factures (2026-10-24)
  const [seuilOr, setSeuilOr] = useState("");            // seuil ($) de la pastille « Client or » (vide = 10 000 $) (2026-10-24)
  const [comptaJour, setComptaJour] = useState("");      // jour du mois de l'envoi auto au comptable (vide = le 1er) (2026-10-27)
  const [comptableNom, setComptableNom] = useState("");  // création de l'accès comptable (lecture seule) (2026-10-27)
  const [comptableAccesEmail, setComptableAccesEmail] = useState("");
  const [payTermDef, setPayTermDef] = useState("reception");  // terme de paiement GLOBAL par défaut (2026-10-22) — fiche > dossier client > ce réglage
  const [ruleDraft, setRuleDraft] = useState({ level: "category", key: "", pct: "", min_qty: "" });  // nouveau rabais unique par type/catalogue (2026-10-22)
  const [bilanBusy, setBilanBusy] = useState(false);  // bouton « Bilan du mois » (Rapports, 2026-10-22)
  const [bilanTrimBusy, setBilanTrimBusy] = useState(false);  // bouton « Bilan du trimestre » (Rapports, 2026-10-22)
  const [bilanAnneeBusy, setBilanAnneeBusy] = useState(false);  // bouton « Bilan de l'année » (Rapports, 2026-10-22)
  const [markupMult, setMarkupMult] = useState("3"); // multiplicateur du prix de détail (« 3 » = ×3, « 200 % » = +200 %) — arrondi à ,95 $
  const [orderSilent, setOrderSilent] = useState({}); // case « Sans courriel » par commande (demande du propriétaire)
  const [signOrder, setSignOrder] = useState(null);   // popup « Livrée » — signature (main propre / ramassage)
  const [signOrderBusy, setSignOrderBusy] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);  // groupe d'onglets déplié à la main (sinon suit l'onglet actif)
  const [vendeurPerms, setVendeurPerms] = useState(null);  // cases du vendeur connecté (sections+mode par page)
  const EMPTY_PROMO = { code: "", kind: "pct", value: "", scope: "order", condition: "aucune", condition_detail: "", valid_from: "", valid_to: "", max_uses: "", active: true, single_use: [] };
  const [promoForm, setPromoForm] = useState(EMPTY_PROMO);
  const [promoCreateOpen, setPromoCreateOpen] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [savingPromoEdit, setSavingPromoEdit] = useState(false);
  const imageInput = useRef(null);
  const extraImageInput = useRef(null);  // photos supplémentaires du même code produit
  const rubanInput = useRef(null);       // photo du ruban (médailles)
  const insertInput = useRef(null);      // photo de l'insertion (s'il y a lieu)
  const postImageInput = useRef(null);

  const loadProducts = (q = prodQ, page = prodPage, cat = prodCatalogue, sp = prodSport, sc = prodSansCoutant) => {
    api.get("/admin/products", { params: { q, page, limit: 50, catalogue_no: cat, sport: sp, sans_coutant: sc ? 1 : 0 } })
      .then((r) => { setProducts(r.data.items); setProdTotal(r.data.total); }).catch(() => {});
  };

  // Tout activer/désactiver d'un catalogue (demande du propriétaire, 2026-10-16)
  const bulkActive = async (catNo, active) => {
    setProdBulkBusy(`active-${catNo}`);
    try {
      const { data } = await api.post("/admin/products/bulk-active", { catalogue_no: catNo, active });
      toast.success(`${data.modified} produit(s) ${active ? "ACTIVÉS" : "DÉSACTIVÉS"} dans la boutique (${catNo ? catalogueLabel(catNo) : "sans catalogue"}).`);
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProdBulkBusy("");
    }
  };

  // Regrouper les produits cochés sous un même nom (demande du propriétaire) —
  // nom vide = retirer du regroupement
  const bulkGroup = async (group) => {
    const ids = Object.keys(prodSel).filter((k) => prodSel[k]);
    if (!ids.length) return;
    setProdBulkBusy("group");
    try {
      const { data } = await api.post("/admin/products/bulk-group", { ids, group });
      toast.success(group
        ? `${data.modified} produit(s) regroupés sous « ${group} ».`
        : `${data.modified} produit(s) retirés de leur regroupement.`);
      setProdSel({}); setGroupName("");
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProdBulkBusy("");
    }
  };

  // Traduction instantanée FR↔EN d'un champ de fiche via l'accès Gemini du
  // propriétaire (demande du 2026-10-16) : on entre la description dans une
  // langue, l'autre se remplit toute seule (boutons « Traduire » du formulaire)
  const [translatingKey, setTranslatingKey] = useState("");  // champ en cours de traduction
  const [translatingProd, setTranslatingProd] = useState(""); // fiche en cours de traduction IA (bouton de la liste)
  const translateField = async (fromKey, toKey, target) => {
    const text = ((editing || {})[fromKey] || "").trim();
    if (!text) {
      toast.error("Écrivez d'abord le texte à traduire.");
      return;
    }
    setTranslatingKey(toKey);
    try {
      const { data } = await api.post("/admin/translate-text", { text, target });
      setEditing((e) => ({ ...e, [toKey]: data.translation }));
      toast.success(target === "en" ? "Traduit en anglais avec Gemini." : "Traduit en français avec Gemini.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTranslatingKey("");
    }
  };
  const TranslateBtn = ({ fromKey, toKey, target, children }) => (
    <button type="button" disabled={!!translatingKey}
      onClick={() => translateField(fromKey, toKey, target)}
      data-testid={`translate-${toKey}`} title="Traduction automatique avec Gemini"
      className="inline-flex items-center gap-1 text-[10px] text-gold/85 hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-2.5 py-0.5 transition-colors duration-300 disabled:opacity-50">
      {translatingKey === toKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {children}
    </button>
  );

  // Bouton IA de CHAQUE fiche de la boutique (demande du propriétaire, 2026-10-20) :
  // traduit d'un coup la langue MANQUANTE (nom, slogan, description, catégorie) et
  // enregistre — sans ouvrir le formulaire. Une langue déjà remplie n'est jamais
  // écrasée ; le bouton n'apparaît que si une traduction manque.
  const langueManquante = (p) =>
    (p.name && !p.name_en) || (!p.name && p.name_en)
    || (p.tagline && !p.tagline_en) || (!p.tagline && p.tagline_en)
    || (p.description && !p.description_en) || (!p.description && p.description_en)
    || (p.category && !p.category_en) || (!p.category && p.category_en);
  const traduireFiche = async (p) => {
    setTranslatingProd(p.id);
    try {
      const { data } = await api.post(`/admin/products/${p.id}/translate-missing`);
      const n = Object.keys(data.translated || {}).length;
      if (!n) { toast.success("Cette fiche est déjà bilingue — rien à traduire."); return; }
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...data.translated } : x)));
      toast.success(`Langue manquante traduite avec l'IA (${n} champ${n > 1 ? "s" : ""} — ${data.langue_cible === "en" ? "anglais" : "français"}) et enregistrée.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setTranslatingProd(""); }
  };

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    loadProducts();
    api.get("/admin/products/counts").then((r) => setCatCounts(r.data)).catch(() => {});
    api.get("/admin/products/facets").then((r) => setProdFacets(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/carts").then((r) => setCarts(r.data.carts || [])).catch(() => {});
    api.get("/admin/newsletter-subscribers").then((r) => setNlSubs(r.data)).catch(() => {});
    api.get("/settings/shop").then((r) => {
      setShopSettings({ ...r.data, express_rate: Math.round((r.data.express_rate ?? 0.15) * 10000) / 100 });
      if (Array.isArray(r.data.default_tiers)) {
        setTierTemplate(r.data.default_tiers.map((t) => ({ min_qty: t.min_qty, pct: Math.round(t.discount * 10000) / 100 })));
      }
      if (r.data.about_page?.fr) {
        setAboutDraft({ fr: r.data.about_page.fr, en: r.data.about_page.en || "" });
      }
    }).catch(() => {});
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!photoPreview) return;
    const onKey = (e) => { if (e.key === "Escape") setPhotoPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoPreview]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  // État initial de la sauvegarde (affiche une construction déjà en cours/terminée)
  useEffect(() => {
    if (user?.role !== "admin") return;
    pollBackupStatus().then((running) => {
      if (running) {
        setBackupSending(true);
        backupPollRef.current = setInterval(async () => {
          const still = await pollBackupStatus();
          if (!still) {
            clearInterval(backupPollRef.current);
            backupPollRef.current = null;
            setBackupSending(false);
          }
        }, 5000);
      }
    });
    pollMediaStatus().then((running) => {
      if (running && !mediaPollRef.current) {
        mediaPollRef.current = setInterval(async () => {
          const still = await pollMediaStatus();
          if (!still) {
            clearInterval(mediaPollRef.current);
            mediaPollRef.current = null;
          }
        }, 8000);
      }
    });
    return () => {
      if (backupPollRef.current) clearInterval(backupPollRef.current);
      if (mediaPollRef.current) clearInterval(mediaPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    const timer = setTimeout(() => loadProducts(), prodQ ? 350 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodQ, prodPage, prodCatalogue, prodSport, prodSansCoutant]);

  useEffect(() => {
    if (user?.role === "admin" && tab === "produits") {
      loadPromos();  // codes rabais déplacés dans « Gestion boutique » (2026-10-16)
    }
    if (user?.role === "admin" && tab === "parametres") {
      api.get("/admin/gsc/status").then((r) => setGscStatus(r.data)).catch(() => setGscStatus(null));
      api.get("/admin/gcal/status").then((r) => setGcalStatus(r.data)).catch(() => setGcalStatus(null));
    }
    if (["admin", "vendeur"].includes(user?.role) && tab === "rapports") {
      // Visites des robots/IA du mois en cours (demande du propriétaire,
      // 2026-10-28 : « afficher dans Rapports sans attendre le bilan »)
      api.get("/admin/visites-bots").then((r) => setVisitesBots(r.data)).catch(() => setVisitesBots(null));
      // File des courriels en chiffres (demande du propriétaire, 2026-10-28 :
      // « montrer combien de courriels de masse sont en file et combien de
      // ventes sont passées devant »)
      api.get("/admin/file-courriels").then((r) => setFileCourriels(r.data)).catch(() => setFileCourriels(null));
      // Journal des envois — fil des derniers courriels partis, jour par jour
      // (demande du propriétaire, 2026-10-29)
      api.get("/admin/journal-envois").then((r) => setJournalEnvois(r.data)).catch(() => setJournalEnvois(null));
    }
    if (["admin", "vendeur"].includes(user?.role)) {
      // Sonde « site en panne » : bandeau rouge dans l'admin dès qu'une page
      // du site en ligne ne répond plus (demande du propriétaire, 2026-10-28)
      api.get("/admin/sonde-etat").then((r) => setSondeEtat(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  // Heures de rappel proposées (demande du propriétaire) : bornes synchronisées
  // du réglage boutique (chargé avec les stats)
  useEffect(() => {
    const ch = shopSettings?.callback_hours;
    if (ch?.start && ch?.end) { setCbStart(ch.start); setCbEnd(ch.end); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettings?.callback_hours?.start, shopSettings?.callback_hours?.end]);

  // Délais des relances (demande du propriétaire, 2026-10-06) : synchronisés du
  // réglage boutique ; le code du rapport TEST est pré-rempli pour l'admin
  useEffect(() => {
    if (shopSettings?.proof_reminder_hours) setRelProof(shopSettings.proof_reminder_hours);
    if (shopSettings?.quote_reminder_hours) setRelQuote(shopSettings.quote_reminder_hours);
    if (shopSettings?.payment_reminder_hours) setRelPay(shopSettings.payment_reminder_hours);
    if (shopSettings?.passe_du_relance_delai) setRelPdDelai(shopSettings.passe_du_relance_delai);
    if (shopSettings?.passe_du_relance_intervalle) setRelPdInter(shopSettings.passe_du_relance_intervalle);
    if (shopSettings?.avis_tournoi_min_pieces) setAvisTournPieces(shopSettings.avis_tournoi_min_pieces);
    if (shopSettings?.avis_tournoi_min_montant) setAvisTournMontant(shopSettings.avis_tournoi_min_montant);
    if (shopSettings?.address_reminder_hours) setRelAddr(shopSettings.address_reminder_hours);
    if (shopSettings?.address_email_note !== undefined) setAddrNote(shopSettings.address_email_note || "");
    if (shopSettings?.staff_inactivity_hours) setInactStaff(shopSettings.staff_inactivity_hours);
    if (shopSettings?.auto_block_rpm) setSeuilMarteau(shopSettings.auto_block_rpm);
    if (typeof shopSettings?.test_report_code === "string") setTestCode(shopSettings.test_report_code);
    if (shopSettings?.counter_offer_pct) setCoPct(String(shopSettings.counter_offer_pct));
    if (shopSettings?.objectif_mensuel) setObjectifMens(String(shopSettings.objectif_mensuel));
    if (typeof shopSettings?.comptable_email === "string") setComptableEmail(shopSettings.comptable_email);
    if (shopSettings?.client_or_seuil) setSeuilOr(String(shopSettings.client_or_seuil));
    if (shopSettings?.compta_jour) setComptaJour(String(shopSettings.compta_jour));
    if (typeof shopSettings?.payment_terms_default === "string" && shopSettings.payment_terms_default) setPayTermDef(shopSettings.payment_terms_default);
    if (typeof shopSettings?.markup_multiplier === "string" && shopSettings.markup_multiplier) setMarkupMult(shopSettings.markup_multiplier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettings?.proof_reminder_hours, shopSettings?.quote_reminder_hours,
      shopSettings?.payment_reminder_hours, shopSettings?.address_reminder_hours,
      shopSettings?.address_email_note,
      shopSettings?.test_report_code,
      shopSettings?.counter_offer_pct, shopSettings?.markup_multiplier, shopSettings?.staff_inactivity_hours,
      shopSettings?.auto_block_rpm, shopSettings?.payment_terms_default, shopSettings?.objectif_mensuel,
      shopSettings?.comptable_email, shopSettings?.client_or_seuil, shopSettings?.compta_jour]);

  // Retour des connexions Google (?gsc= / ?gcal= / ?gbp=ok / erreur)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gsc = params.get("gsc");
    const gcal = params.get("gcal");
    const gbp = params.get("gbp");
    if (!gsc && !gcal && !gbp) return;
    if (gsc === "ok") toast.success("Google Search Console connecté — vos statistiques Google seront dans le rapport mensuel");
    else if (gsc === "portee-manquante") toast.error("La permission Search Console n'a pas été accordée — reconnectez en COCHANT TOUTES les cases sur l'écran de Google (sinon ni les statistiques ni le sitemap ne fonctionneront)", { duration: 10000 });
    else if (gsc === "erreur-etat") toast.error("Le lien de connexion a expiré — cliquez à nouveau « Connecter Google Search Console »");
    else if (gsc) toast.error("La connexion à Google Search Console a échoué — réessayez");
    if (gcal === "ok") toast.success("Google Agenda connecté (création d'événements ET lecture des plages occupées) — tout est complet");
    else if (gcal === "ok-sans-lecture") toast.warning("Google Agenda connecté, mais SANS la permission de lecture : les plages déjà occupées de votre agenda ne bloqueront pas les réservations. Cliquez « Reconnecter » et COCHEZ TOUTES les cases demandées par Google.", { duration: 12000 });
    else if (gcal === "portee-manquante") toast.error("La permission Google Agenda n'a pas été accordée — reconnectez en COCHANT la case demandée sur l'écran de Google", { duration: 10000 });
    else if (gcal === "erreur-etat") toast.error("Le lien de connexion a expiré — cliquez à nouveau « Connecter Google Agenda »");
    else if (gcal) toast.error("La connexion à Google Agenda a échoué — réessayez");
    if (gbp === "ok") toast.success("Avis Google connectés — voyez et répondez à tous les avis dans l'onglet Avis");
    else if (gbp === "portee-manquante") toast.error("La permission « avis Google » n'a pas été accordée — reconnectez en COCHANT la case demandée sur l'écran de Google", { duration: 10000 });
    else if (gbp === "erreur-etat") toast.error("Le lien de connexion a expiré — cliquez à nouveau « Connecter les avis Google »");
    else if (gbp) toast.error("La connexion des avis Google a échoué — réessayez");
    window.history.replaceState({}, "", "/admin");
    setTab(gbp ? "avis" : "parametres");
    api.get("/admin/gsc/status").then((r) => setGscStatus(r.data)).catch(() => {});
    api.get("/admin/gcal/status").then((r) => setGcalStatus(r.data)).catch(() => {});
  }, []);

  // sondage de la génération d'images IA tant qu'elle est en cours
  useEffect(() => {
    if (!genImages.running) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/products/generate-images-status");
        setGenImages(data);
        if (!data.running) {
          if (data.total === 0) {
            toast.success("Aucune image à générer — tous les produits et articles sont illustrés");
          } else {
            toast.success(`Images IA terminées : ${data.done}/${data.total}${data.errors ? ` (${plur(data.errors, "échec")})` : ""}`);
            load();
          }
        }
      } catch { /* silencieux */ }
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genImages.running]);

  // VENDEUR (demande du propriétaire) : il entre dans l'admin — on charge ses
  // cases (sections + mode lecteur/éditeur par page) et on le garde dessus
  useEffect(() => {
    if (user?.role !== "vendeur") return;
    api.get("/vendeur/perms").then(({ data }) => setVendeurPerms(data))
      .catch(() => setVendeurPerms({ sections: {}, actions: [] }));
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user?.role !== "vendeur" || !vendeurPerms) return;
    // « parametres » toujours permis : la carte 2FA y vit (règle 2FA des vendeurs)
    const allowed = [...Object.keys(vendeurPerms.sections || {}), "parametres"];
    if (allowed.length && !allowed.includes(tab)) setTab(allowed[0]);
  }, [user?.role, vendeurPerms, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user === false) return <Navigate to="/connexion" state={{ next: "/admin" }} replace />;
  if (!user) return null;
  // VENDEUR (demande du propriétaire) : il ENTRE dans l'admin — sections cochées
  // seulement, chacune en mode lecteur ou éditeur (barrière côté serveur aussi)
  if (user.role !== "admin" && user.role !== "vendeur")
    return <div className="pt-40 text-center text-ivory/70" data-testid="admin-denied">Accès réservé au personnel.</div>;
  const isVendeur = user.role === "vendeur";
  const sectionsMap = vendeurPerms?.sections || {};
  const tabMode = (id) => sectionsMap[id] || null;

  const saveProduct = async (e) => {
    e.preventDefault();
    const sizes = (editing.sizes || [])
      .map((s, i) => {
        // Prix de vente + coûtant PAR GRANDEUR (demande du propriétaire,
        // 2026-10-16) — remplace le supplément : « extra » est DÉRIVÉ du prix
        // saisi pour rester compatible avec les anciens calculs (panier, PDF…)
        const base = parseFloat(editing.base_price) || 0;
        const pv = s.price === "" || s.price === null || s.price === undefined
          ? base + (parseFloat(s.extra) || 0)
          : parseFloat(s.price) || 0;
        return { ...s, id: s.id || `s${i + 1}`, label: (s.label || "").trim(),
                 price: Math.round(pv * 100) / 100,
                 cost: (s.cost === "" || s.cost === null || s.cost === undefined)
                   ? null : Math.round(parseFloat(s.cost) * 100) / 100,
                 extra: Math.round((pv - base) * 100) / 100 };
      })
      .filter((s) => s.label);
    if (!sizes.length) {
      toast.error("Ajoutez au moins une grandeur");
      return;
    }
    const finishes = (editing.finishes || [])
      .map((f, i) => ({ id: f.id || `f${i + 1}`, label: (f.label || "").trim(), label_en: (f.label_en || "").trim(), extra: parseFloat(f.extra) || 0 }))
      .filter((f) => f.label);
    const priceTiers = (editing.price_tiers || [])
      .map((tr) => ({ min_qty: parseInt(tr.min_qty) || 0, price: parseFloat(tr.price) || 0 }))
      .filter((tr) => tr.min_qty >= 1 && tr.price > 0)
      .sort((a, b) => a.min_qty - b.min_qty)
      .slice(0, 5);
    const partialTier = (editing.price_tiers || []).some((tr) => {
      const q = parseInt(tr.min_qty) || 0;
      const pr = parseFloat(tr.price) || 0;
      return (q >= 1) !== (pr > 0);
    });
    if (partialTier) {
      toast.error("Chaque palier doit avoir une quantité (≥ 1) et un prix (> 0)");
      return;
    }
    const discountTiers = (editing.tiers || [])
      .map((tr) => ({ min_qty: parseInt(tr.min_qty) || 0, discount: (parseFloat(tr.pct) || 0) / 100 }))
      .filter((tr) => tr.min_qty >= 1 && tr.discount > 0)
      .sort((a, b) => a.min_qty - b.min_qty)
      .slice(0, 5);
    const partialDiscount = (editing.tiers || []).some((tr) => {
      const q = parseInt(tr.min_qty) || 0;
      const pct = parseFloat(tr.pct) || 0;
      return (q >= 1) !== (pct > 0);
    });
    if (partialDiscount) {
      toast.error("Chaque rabais doit avoir une quantité (≥ 1) et un pourcentage (> 0)");
      return;
    }
    if (discountTiers.some((tr) => tr.discount >= 1)) {
      toast.error("Le rabais doit être inférieur à 100 %");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        base_price: parseFloat(editing.base_price),
        cost_price: editing.cost_price === "" || editing.cost_price === null || editing.cost_price === undefined ? null : parseFloat(editing.cost_price),
        stock: editing.stock === "" || editing.stock === null || editing.stock === undefined ? null : Math.max(0, parseInt(editing.stock) || 0),
        catalogue_page: editing.catalogue_page === "" || editing.catalogue_page === null || editing.catalogue_page === undefined ? null : Math.max(1, parseInt(editing.catalogue_page) || 1),
        featured: !!editing.featured,
        inscription_fees: {
          gravure: parseFloat(editing.inscription_fees?.gravure) || 0,
          impression: parseFloat(editing.inscription_fees?.impression) || 0,
          arriere: parseFloat(editing.inscription_fees?.arriere) || 0,
        },
        sizes,
        finishes,
        price_tiers: priceTiers,
        tiers: discountTiers,
        images: (editing.images || []).map((u) => (u || "").trim()).filter(Boolean).slice(0, 10),
        group: (editing.group || "").trim(),
        shop_category: editing.shop_category || "",
        shop_subcategory: (editing.shop_subcategory || "").trim(),
        // Rabais unique + visibilité des paliers (demande du propriétaire, 2026-10-22) :
        // % vide = aucun rabais produit (retire la règle à la modification)
        show_tiers_client: !!editing.show_tiers_client,
        discount_rule: (() => {
          const pct = parseFloat(String(editing.discount_rule?.pct ?? "").replace(",", "."));
          if (!pct || pct <= 0) return null;
          return { pct, min_qty: Math.max(0, parseInt(editing.discount_rule?.min_qty) || 0) };
        })(),
      };
      if (editing.id) await api.patch(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing.id ? "Produit mis à jour" : "Produit créé");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const aiQuickPublish = async () => {
    if (aiQuickTopic.trim().length < 3) {
      toast.error("Entrez un sujet pour l'article");
      return;
    }
    setAiQuickLoading(true);
    try {
      const { data } = await api.post("/admin/ai/blog", { topic: aiQuickTopic });
      const { data: post } = await api.post("/admin/posts", { ...data, image: "", published: true });
      setAiQuickTopic("");
      toast.success(`Article publié : /blogue/${post.slug}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiQuickLoading(false);
    }
  };

  const aiWriteBlog = async () => {
    if (aiTopic.trim().length < 3) {
      toast.error("Entrez un sujet pour l'article");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/blog", { topic: aiTopic, kind: aiGuide ? "guide" : "article" });
      setEditingPost((p) => ({ ...p, ...data }));
      toast.success(aiGuide
        ? "Guide de référence rédigé par Claude — relisez avant de publier"
        : "Article rédigé par Claude — relisez avant de publier");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const aiWriteProduct = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Entrez d'abord le nom du produit");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/product", { name: editing.name, category: editing.category });
      setEditing((p) => ({ ...p, ...data }));
      toast.success("Textes générés par Claude — relisez avant d'enregistrer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingPost.id) {
        await api.patch(`/admin/posts/${editingPost.id}`, editingPost);
        toast.success("Article mis à jour");
      } else {
        await api.post("/admin/posts", editingPost);
        toast.success("Article publié");
      }
      setEditingPost(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Article supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteOrder = async (orderNumber) => {
    try {
      await api.delete(`/admin/orders/${orderNumber}`);
      toast.success(`Commande ${orderNumber} supprimée`);
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteProduct = async (id, name) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success(`Produit « ${name} » supprimé`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteCustomer = async (id, email) => {
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success(`Fiche client ${email} supprimée`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Donne la fonction admin à un client inscrit — réservé aux courriels @trophee.ca
  const makeAdmin = async (id, email) => {
    if (!window.confirm(`Donner la fonction ADMINISTRATEUR à ${email} ? Il aura accès à tout le tableau de bord (commandes, produits, clients, paramètres).`)) return;
    try {
      await api.post(`/admin/customers/${id}/make-admin`);
      toast.success(`${email} est maintenant administrateur — il quitte la liste des clients`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Cellule « 2FA » des tableaux du personnel (demande du propriétaire, 2026-10-23 —
  // tableau des grâces) : état de la double vérification + date butoir PAR COMPTE
  // (délai de grâce de 30 jours après la nomination), avec compte à rebours
  const cellule2FA = (p) => {
    if (p.totp_enabled) {
      return <span className="text-xs font-semibold text-emerald-300" data-testid={`twofa-active-${p.id}`}>Active ✓</span>;
    }
    if (!p.twofa_required_after) return <span className="text-xs text-ivory/45">—</span>;
    const j = Math.ceil((new Date(p.twofa_required_after + "T00:00:00") - Date.now()) / 86400000);
    const d = new Date(p.twofa_required_after + "T00:00:00")
      .toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" });
    return j < 0
      ? <span className="text-xs font-semibold text-red-300" data-testid={`twofa-due-${p.id}`}>Exigée à la connexion</span>
      : <span className="text-xs text-amber-300" data-testid={`twofa-due-${p.id}`}>obligatoire le {d} — dans {j} j</span>;
  };


  // Comptes administrateurs (vue « Administrateurs » de l'onglet Clients)
  const loadAdmins = async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data.admins || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setAdmins([]);
    }
  };

  // Comptes VENDEURS (demande du propriétaire) : des clients nommés vendeurs qui
  // ne voient QUE les dossiers cochés pour eux (soumissions « visibles par le
  // vendeur » + dossiers de leurs clients assignés) et ne font QUE les actions cochées
  const loadVendeurs = async () => {
    try {
      const { data } = await api.get("/admin/vendeurs");
      setVendeurs(data.vendeurs || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setVendeurs([]);
    }
  };

  const makeVendeur = async (id, email) => {
    if (!window.confirm(`Nommer ${email} VENDEUR ? Il se connectera à son portail (/vendeur) et ne verra QUE les dossiers que vous aurez cochés pour lui.`)) return;
    try {
      await api.post(`/admin/customers/${id}/make-vendeur`);
      toast.success(`${email} est maintenant vendeur — réglez ses cases dans la vue « Vendeurs »`);
      load();
      if (vendeurs !== null) loadVendeurs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeVendeur = async (id, email) => {
    try {
      await api.post(`/admin/customers/${id}/make-vendeur`);
      toast.success(`${email} n'est plus vendeur — il redevient un client ordinaire`);
      loadVendeurs();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveVendeurPerms = async () => {
    if (!permsVendeur) return;
    try {
      await api.put(`/admin/vendeurs/${permsVendeur.id}`, {
        sections: permsVendeur.sections, actions: permsVendeur.actions, clients: permsVendeur.clients });
      toast.success(`Cases de ${permsVendeur.email} enregistrées.`);
      setPermsVendeur(null);
      loadVendeurs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveAdminPassword = async () => {
    if (!pwdAdmin) return;
    if (pwdValue.length < 6) return toast.error("6 caractères minimum");
    try {
      await api.post(`/admin/admins/${pwdAdmin.id}/password`, { password: pwdValue });
      toast.success(`Mot de passe de ${pwdAdmin.email} changé — ses sessions ouvertes sont fermées, il se reconnectera avec le nouveau mot de passe.`);
      setPwdAdmin(null);
      setPwdValue("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteAdmin = async (id, email) => {
    try {
      await api.delete(`/admin/admins/${id}`);
      toast.success(`Compte administrateur ${email} supprimé`);
      loadAdmins();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Retire l'accès ADMIN sans supprimer le dossier client (demande du propriétaire,
  // 2026-10-06) — RÉVERSIBLE : le compte redevient un client ordinaire et peut être
  // re-nommé plus tard. Réservé au super admin (même règle que la nomination).
  const removeAdminAccess = async (id, email) => {
    try {
      await api.post(`/admin/admins/${id}/remove-access`);
      toast.success(`Accès administrateur retiré à ${email} — dossier client conservé, accès redonnable plus tard`);
      loadAdmins();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Retire un mot-clé SEO (pastille ×) — sauvegarde immédiate, en ligne aussitôt
  const removeKeyword = async (langKey, kw) => {
    const cur = (shopSettings?.[langKey] || "").split(",").map((k) => k.trim()).filter(Boolean);
    const idx = cur.findIndex((k) => k.toLowerCase() === kw.toLowerCase());
    if (idx < 0) return;
    cur.splice(idx, 1);
    const next = cur.join(", ");
    setShopSettings((s) => ({ ...s, [langKey]: next }));
    try {
      const { data } = await api.patch("/admin/settings", { [langKey]: next });
      setShopSettings((s) => ({ ...s, ...data, express_rate: data.express_rate * 100 }));
      toast.success(`Mot-clé « ${kw} » retiré — en ligne immédiatement`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePostPublished = async (p, v) => {
    try {
      await api.patch(`/admin/posts/${p.id}`, { ...p, published: v });
      setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: v } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Épingler / désépingler un article en haut de la page Blogue
  const togglePostPinned = async (p) => {
    try {
      const { data } = await api.post(`/admin/posts/${p.id}/pin`);
      setPosts((prev) => [...prev]
        .map((x) => (x.id === p.id ? { ...x, pinned: data.pinned } : x))
        .sort((a, b) => (b.pinned - a.pinned) || (String(b.created_at).localeCompare(String(a.created_at)))));
      toast.success(data.pinned
        ? `« ${p.title} » épinglé en haut de la page Blogue.`
        : `« ${p.title} » n'est plus épinglé.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadOpinlyPosts = async () => {
    setOpinlyLoading(true);
    try {
      const { data } = await api.get("/admin/blog/opinly/posts");
      setOpinlyPosts(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setOpinlyPosts(null);
    } finally {
      setOpinlyLoading(false);
    }
  };

  const syncOpinly = async () => {
    setOpinlySyncing(true);
    try {
      const { data } = await api.post("/admin/blog/opinly/sync");
      toast.success(data.synced > 0 ? `${plur(data.synced, "article")} ${data.synced > 1 ? "importés" : "importé"} depuis Opinly` : "Tout est à jour — aucun nouvel article Opinly");
      load();
      if (opinlyPosts !== null) loadOpinlyPosts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setOpinlySyncing(false);
    }
  };

  const importOpinly = async (slug) => {
    setOpinlyImporting(slug);
    try {
      const { data } = await api.post(`/admin/blog/opinly/import/${slug}`);
      toast.success(`Article importé : /blogue/${data.slug}`);
      load();
      loadOpinlyPosts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setOpinlyImporting("");
    }
  };

  const generateCover = async (p) => {
    try {
      await api.post(`/admin/blog/${p.id}/cover-image`);
      toast.success("Image de couverture en génération — visible dans ±30 secondes");
      setTimeout(load, 45000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openSoraDialog = (p) => {
    setSoraPost(p);
    setSoraForm({
      prompt: `Vidéo cinématique professionnelle pour illustrer un article de blogue d'un atelier québécois de trophées personnalisés. Sujet : ${p.title}. ${p.excerpt || ""}`.slice(0, 900),
      size: "1280x720",
      duration: 4,
    });
  };

  const generateLifestyle = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/lifestyle-image`);
      toast.success("Photo de mise en situation en génération — ajoutée à la galerie dans ±40 secondes");
      setTimeout(load, 50000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const generateSora = async () => {
    if (soraForm.prompt.trim().length < 10) {
      toast.error("Décrivez la vidéo (min. 10 caractères)");
      return;
    }
    setSoraLoading(true);
    try {
      await api.post(`/admin/blog/${soraPost.id}/sora-video`, { ...soraForm, duration: parseInt(soraForm.duration) });
      toast.success("Génération Sora lancée — la vidéo apparaîtra sur l'article dans 2 à 5 minutes");
      setSoraPost(null);
      setPosts((prev) => prev.map((x) => (x.id === soraPost.id ? { ...x, video_status: "processing" } : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSoraLoading(false);
    }
  };

  const loadSoroPosts = async () => {
    setSoroLoading(true);
    try {
      const { data } = await api.get("/admin/blog/soro/posts");
      setSoroPosts(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSoroPosts(null);
    } finally {
      setSoroLoading(false);
    }
  };

  const syncSoro = async () => {
    setSoroSyncing(true);
    try {
      const { data } = await api.post("/admin/blog/soro/sync");
      toast.success(data.synced > 0 ? `${plur(data.synced, "article")} ${data.synced > 1 ? "importés" : "importé"} depuis Soro` : "Tout est à jour — aucun nouvel article Soro");
      load();
      if (soroPosts !== null) loadSoroPosts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSoroSyncing(false);
    }
  };

  const importSoro = async (id) => {
    setSoroImporting(id);
    try {
      const { data } = await api.post(`/admin/blog/soro/import/${id}`);
      toast.success(`Article importé : /blogue/${data.slug}`);
      load();
      loadSoroPosts();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSoroImporting("");
    }
  };

  const translateAll = async () => {
    setTranslating(true);
    try {
      await api.post("/admin/blog/translate-all");
      toast.success("Traduction lancée — les articles deviennent bilingues un par un");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/blog/translate-status");
          if (!data.running) {
            clearInterval(poll);
            setTranslating(false);
            toast.success(`Traduction terminée : ${plur(data.done, "article")} ${data.done > 1 ? "traités" : "traité"}${data.errors ? `, ${plur(data.errors, "erreur")}` : ""}`);
            load();
          }
        } catch { /* réessaie au prochain cycle */ }
      }, 8000);
    } catch (err) {
      setTranslating(false);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Restaurer ${file.name} ? Les produits, pages de catalogues, codes rabais et articles seront fusionnés (rien n'est supprimé ; clients et commandes jamais touchés).`)) return;
    setBackupRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/backup/restore", fd, { timeout: 300000 });
      const parts = Object.entries(data.imported || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Restauration terminée : ${parts}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBackupRestoring(false);
    }
  };

  const toggleBackupEmail = async (v) => {
    try {
      const { data } = await api.patch("/admin/settings", { backup_email_enabled: v });
      setShopSettings({ ...data, express_rate: data.express_rate * 100 });
      toast.success(v ? "Courriel de sauvegarde activé (lundi 4 h)" : "Courriel de sauvegarde désactivé — la sauvegarde reste créée et téléchargeable ici");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleAutoBlog = async (v) => {    try {
      const payload = {
        shop_open: shopSettings.shop_open,
        shipping_standard: parseFloat(shopSettings.shipping_standard) || 0,
        shipping_free_over: parseFloat(shopSettings.shipping_free_over) || 0,
        express_rate: (parseFloat(shopSettings.express_rate) || 0) / 100,
        auto_blog_enabled: v,
      };
      const { data } = await api.patch("/admin/settings", payload);
      setShopSettings({ ...data, express_rate: data.express_rate * 100 });
      toast.success(v ? "Publication automatique activée : 1 article par jour (Claude) + synchro Opinly" : "Publication automatique désactivée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const tierRowsToApi = (rows) => rows
    .map((tr) => ({ min_qty: parseInt(tr.min_qty) || 0, discount: (parseFloat(tr.pct) || 0) / 100 }))
    .filter((tr) => tr.min_qty >= 1 && tr.discount > 0 && tr.discount < 1)
    .sort((a, b) => a.min_qty - b.min_qty)
    .slice(0, 5);

  const saveTierTemplate = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", { default_tiers: tierRowsToApi(tierTemplate) });
      toast.success("Gabarit de rabais enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const applyTierTemplate = async () => {
    setApplyingTiers(true);
    try {
      const { data } = await api.post("/admin/products/apply-tiers", {
        scope: tierScope,
        tiers: tierRowsToApi(tierTemplate),
      });
      toast.success(`Rabais appliqués à ${plur(data.modified, "produit")}${data.skipped_manual ? ` — ${plur(data.skipped_manual, "ignoré")} car modifiés manuellement` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplyingTiers(false);
    }
  };

  const applyFees = async () => {
    setApplyingFees(true);
    try {
      const payload = {};
      if (feeGravure !== "") payload.gravure = parseFloat(feeGravure);
      if (feeImpression !== "") payload.impression = parseFloat(feeImpression);
      const { data } = await api.post("/admin/products/apply-fees", payload);
      toast.success(`Prix des inscriptions appliqués à ${plur(data.modified, "produit")}${data.skipped_manual ? ` — ${plur(data.skipped_manual, "ignoré")} car modifiés manuellement` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplyingFees(false);
    }
  };

  const loadPromos = async () => {
    try {
      const { data } = await api.get("/admin/promos");
      setPromos(data);
    } catch { /* silencieux */ }
  };

  const generateImages = async () => {
    try {
      const { data } = await api.post("/admin/products/generate-missing-images");
      if (!data.ok) {
        toast.info(data.message || "Génération déjà en cours");
      }
      setGenImages((s) => ({ ...s, running: true }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const savePromo = async (e) => {
    e.preventDefault();
    setSavingPromo(true);
    try {
      await api.post("/admin/promos", {
        code: promoForm.code.trim(),
        kind: promoForm.kind,
        value: parseFloat(promoForm.value),
        scope: promoForm.scope,
        condition: promoForm.condition,
        condition_detail: promoForm.condition === "autre" ? promoForm.condition_detail.trim() : "",
        valid_from: promoForm.valid_from,
        valid_to: promoForm.valid_to,
        max_uses: promoForm.max_uses === "" ? null : parseInt(promoForm.max_uses),
        single_use: promoForm.single_use || [],
      });
      toast.success(`Code rabais ${promoForm.code.trim().toUpperCase()} créé`);
      setPromoForm(EMPTY_PROMO);
      setPromoCreateOpen(false);
      loadPromos();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPromo(false);
    }
  };

  const promoPayload = (f) => ({
    code: f.code.trim(),
    kind: f.kind,
    value: parseFloat(f.value),
    scope: f.scope,
    condition: f.condition,
    condition_detail: f.condition === "autre" ? (f.condition_detail || "").trim() : "",
    valid_from: f.valid_from,
    valid_to: f.valid_to,
    max_uses: f.max_uses === "" || f.max_uses == null ? null : parseInt(f.max_uses),
    active: f.active !== false,
    single_use: f.single_use || [],
  });

  const openPromoEdit = (p) => setEditingPromo({
    ...p, max_uses: p.max_uses ?? "",
    condition: p.condition || "aucune", condition_detail: p.condition_detail || "",
    single_use: p.single_use || [],
  });

  const updatePromo = async (e) => {
    e.preventDefault();
    setSavingPromoEdit(true);
    try {
      await api.patch(`/admin/promos/${editingPromo.id}`, promoPayload(editingPromo));
      toast.success(`Code rabais ${editingPromo.code.trim().toUpperCase()} modifié`);
      setEditingPromo(null);
      loadPromos();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPromoEdit(false);
    }
  };

  const deletePromo = async (p) => {
    if (!window.confirm(`Supprimer le code ${p.code} ?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Code rabais supprimé");
      loadPromos();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const promoStatus = (p) => {
    const today = dayKeyMtl();  // « aujourd'hui » à Montréal (règle permanente)
    if (!p.active) return { label: "inactif", cls: "text-ivory/65" };
    if (today < p.valid_from) return { label: "à venir", cls: "text-blue-300" };
    if (today > p.valid_to) return { label: "expiré", cls: "text-red-400" };
    if (p.max_uses != null && p.uses >= p.max_uses) return { label: "épuisé", cls: "text-red-400" };
    return { label: "actif", cls: "text-green-400" };
  };

  const toggleCatalogue = async (no) => {
    const disabled = shopSettings.disabled_catalogues || [];
    const next = disabled.includes(no) ? disabled.filter((c) => c !== no) : [...disabled, no];
    try {
      await api.patch("/admin/settings", { disabled_catalogues: next });
      setShopSettings({ ...shopSettings, disabled_catalogues: next });
      toast.success(next.includes(no) ? "Catalogue masqué côté client" : "Catalogue visible côté client");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Boutons « Tout activer » / « Tout masquer » (demande du propriétaire, 2026-10-20) :
  // un clic applique à toute la liste de la boutique — « Tout masquer » demande
  // confirmation car la boutique se vide côté client (réversible d'un clic)
  const tousNumerosCatalogues = Object.keys({ ...Object.fromEntries(Object.keys(CATALOGUE_NAMES).map((k) => [k, 0])), ...catCounts });
  const toggleTousCatalogues = async (toutActiver) => {
    const next = toutActiver ? [] : tousNumerosCatalogues;
    if (!toutActiver && !window.confirm(`Masquer les ${tousNumerosCatalogues.length} catalogues côté client ? La boutique n'offrira plus rien — réversible avec « Tout activer ».`)) return;
    try {
      await api.patch("/admin/settings", { disabled_catalogues: next });
      setShopSettings({ ...shopSettings, disabled_catalogues: next });
      toast.success(toutActiver ? "Tous les catalogues sont visibles côté client" : "Tous les catalogues sont masqués côté client");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Boutons par FAMILLE de catalogues (demande du propriétaire, 2026-10-20) :
  // active ou masque toute la famille (Caldwell, Marco Awards…) d'un seul clic —
  // un catalogue visible dans la famille → on masque tout ; tout masqué → on réactive
  const toggleFamilleCatalogues = async (famille) => {
    const deFamille = tousNumerosCatalogues.filter((no) => catalogueFamily(no) === famille);
    if (!deFamille.length) return;
    const disabled = shopSettings.disabled_catalogues || [];
    const unVisible = deFamille.some((no) => !disabled.includes(no));
    const next = unVisible
      ? [...new Set([...disabled, ...deFamille])]
      : disabled.filter((no) => !deFamille.includes(no));
    const nom = FAMILY_NAMES[famille] || famille;
    try {
      await api.patch("/admin/settings", { disabled_catalogues: next });
      setShopSettings({ ...shopSettings, disabled_catalogues: next });
      toast.success(unVisible ? `Famille ${nom} masquée côté client` : `Famille ${nom} visible côté client`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // « Tout activer » / « Tout masquer » pour les LIENS de la page « Les Catalogues »
  // (même demande du propriétaire que la boutique, 2026-10-20)
  const toggleTousLiensCatalogues = async (toutActiver) => {
    const tous = translations.fr.catalogues.groups.flatMap((g) => g.links.map((l) => l.url));
    const next = toutActiver ? [] : tous;
    if (!toutActiver && !window.confirm(`Masquer les ${tous.length} liens de la page « Les Catalogues » ? Réversible avec « Tout activer ».`)) return;
    try {
      await api.patch("/admin/settings", { catalogue_disabled_links: next });
      setShopSettings({ ...shopSettings, catalogue_disabled_links: next });
      toast.success(toutActiver ? "Tous les liens sont affichés sur la page « Les Catalogues »" : "Tous les liens sont masqués de la page « Les Catalogues »");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Interrupteurs des LIENS de la page publique « Les Catalogues » (par URL —
  // identique en français et en anglais). Masquer un lien ne supprime rien.
  const toggleCatalogueLink = async (url) => {
    const disabled = shopSettings.catalogue_disabled_links || [];
    const next = disabled.includes(url) ? disabled.filter((u) => u !== url) : [...disabled, url];
    try {
      await api.patch("/admin/settings", { catalogue_disabled_links: next });
      setShopSettings({ ...shopSettings, catalogue_disabled_links: next });
      toast.success(next.includes(url) ? "Lien masqué de la page Les Catalogues" : "Lien affiché sur la page Les Catalogues");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadPostImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (5 Mo max)");
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form);
      setEditingPost((prev) => ({ ...prev, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingImage(false);
      if (postImageInput.current) postImageInput.current.value = "";
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        shop_open: shopSettings.shop_open,
        shipping_standard: parseFloat(shopSettings.shipping_standard) || 0,
        shipping_free_over: parseFloat(shopSettings.shipping_free_over) || 0,
        hand_delivery_fee: parseFloat(shopSettings.hand_delivery_fee) || 0,
        hand_delivery_radius_km: parseFloat(shopSettings.hand_delivery_radius_km) || 25,
        express_rate: (parseFloat(shopSettings.express_rate) || 0) / 100,
        picks_keywords: shopSettings.picks_keywords ?? "",
        season_sport: shopSettings.season_sport ?? "",
        season_calendar: shopSettings.season_calendar ?? {},
        auto_blog_enabled: !!shopSettings.auto_blog_enabled,
      };
      const { data } = await api.patch("/admin/settings", payload);
      setShopSettings({ ...data, express_rate: data.express_rate * 100 });
      toast.success("Paramètres enregistrés");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editingCustomer.name, email: editingCustomer.email,
        phone: editingCustomer.phone || "", address1: editingCustomer.address1 || "",
        city: editingCustomer.city || "", province: editingCustomer.province || "QC",
        postal_code: editingCustomer.postal_code || "", newsletter: !!editingCustomer.newsletter,
      };
      if (editingCustomer.new_password) payload.new_password = editingCustomer.new_password;
      await api.patch(`/admin/customers/${editingCustomer.id}`, payload);
      toast.success("Client mis à jour");
      setEditingCustomer(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportNewsletterCsv = () => {
    const subs = customers.filter((c) => c.newsletter);
    const rows = [["Nom", "Courriel", "Téléphone", "Inscrit le"],
      ...subs.map((c) => [c.name, c.email, c.phone || "",
        c.created_at ? new Date(c.created_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL }) : ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "liste-distribution.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Liste de distribution exportée (${plur(subs.length, "abonné")})`);
  };

  const sendNewsletter = async (e) => {
    e.preventDefault();
    const subs = customers.filter((c) => c.newsletter).length;
    if (!window.confirm(`Envoyer « ${nlSubject} » aux ${plur(subs, "abonné")} de l'info-lettre ?\n\nL'envoi passera par Marketing → Campagnes : progression en direct, pause et arrêt complets y sont disponibles.`)) return;
    setNlSending(true);
    try {
      // Unifié avec les campagnes (demande du propriétaire 2026-10-09) : le mass
      // mailing part de no-reply@trophee.ca, avec suivi en direct + pause/arrêt.
      const { data: camp } = await api.post("/admin/campaigns", { subject: nlSubject, message: nlMessage });
      await api.post(`/admin/campaigns/${camp.id}/send`);
      toast.success("Envoi lancé — suivez la progression, mettez en pause ou arrêtez dans Marketing → Campagnes.");
      setNlOpen(false);
      setNlSubject("");
      setNlMessage("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setNlSending(false);
    }
  };

  const sendBackupNow = async () => {
    setBackupSending(true);
    try {
      const { data } = await api.post("/admin/backup/now");
      toast.success(data.already_running
        ? "Une sauvegarde est déjà en cours — progression ci-dessous."
        : "Sauvegarde complète lancée (données + toutes les photos/vidéos + code) — plusieurs minutes, progression ci-dessous. Vous pouvez quitter la page et revenir.");
      if (backupPollRef.current) clearInterval(backupPollRef.current);
      backupPollRef.current = setInterval(async () => {
        const running = await pollBackupStatus();
        if (!running) {
          clearInterval(backupPollRef.current);
          backupPollRef.current = null;
          setBackupSending(false);
          toast.success("Sauvegarde complète prête — téléchargeable ci-dessous.");
        }
      }, 5000);
      await pollBackupStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBackupSending(false);
    }
  };

  const pollBackupStatus = async () => {
    try {
      const { data } = await api.get("/admin/backup/status");
      setBackupStatus(data);
      return data.build_status === "running";
    } catch { return false; }
  };

  // Archive médias COMPLÈTE en parties (~80 Mo chacune, envoyées au nuage au fur et
  // à mesure — le disque du serveur de production est limité, un seul gros ZIP est impossible)
  const pollMediaStatus = async () => {
    try {
      const { data } = await api.get("/admin/backup/media/status");
      setMediaArchive(data);
      return data.status === "running";
    } catch { return false; }
  };

  const startMediaArchive = async () => {
    try {
      const { data } = await api.post("/admin/backup/media/start");
      toast.success(data.already_running
        ? "L'archive est déjà en cours — progression ci-dessous."
        : "Archive complète lancée — les photos et vidéos sont rassemblées en parties (peut prendre 30-60 min). Vous pouvez quitter la page.");
      if (mediaPollRef.current) clearInterval(mediaPollRef.current);
      mediaPollRef.current = setInterval(async () => {
        const running = await pollMediaStatus();
        if (!running) {
          clearInterval(mediaPollRef.current);
          mediaPollRef.current = null;
          toast.success("Archive photos/vidéos complète — les parties sont téléchargeables ci-dessous.");
        }
      }, 8000);
      await pollMediaStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadBackup = async () => {
    setBackupDownloading(true);
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : "backup-mrtrophee.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée");
    } catch (err) {
      toast.error("Aucune sauvegarde disponible — lancez-en une d'abord.");
    } finally {
      setBackupDownloading(false);
    }
  };

  const generateAbout = async () => {
    setAboutBusy(true);
    try {
      const { data } = await api.post("/admin/about/generate", {}, { timeout: 120000 });
      setAboutDraft(data);
      toast.success("Brouillon généré par Claude — révisez-le puis publiez.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAboutBusy(false);
    }
  };

  const publishAbout = async () => {
    if (!aboutDraft || aboutDraft.fr.trim().length < 50 || aboutDraft.en.trim().length < 50) {
      toast.error("Les deux textes (FR et EN) sont requis.");
      return;
    }
    setAboutBusy(true);
    try {
      await api.post("/admin/about/publish", aboutDraft);
      setShopSettings((s) => ({ ...(s || {}), about_page: { fr: aboutDraft.fr, en: aboutDraft.en } }));
      toast.success("Texte À propos publié sur le site.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAboutBusy(false);
    }
  };

  const resetAbout = async () => {
    setAboutBusy(true);
    try {
      await api.delete("/admin/about");
      setAboutDraft(null);
      setShopSettings((s) => ({ ...(s || {}), about_page: null }));
      toast.success("Retour au texte À propos par défaut.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAboutBusy(false);
    }
  };

  const addPriceTier = () => setEditing((prev) => ({
    ...prev,
    price_tiers: [...(prev.price_tiers || []), { min_qty: "", price: "" }],
  }));

  const updatePriceTier = (i, patch) => setEditing((prev) => ({
    ...prev,
    price_tiers: prev.price_tiers.map((tr, k) => (k === i ? { ...tr, ...patch } : tr)),
  }));

  const removePriceTier = (i) => setEditing((prev) => ({
    ...prev,
    price_tiers: prev.price_tiers.filter((_, k) => k !== i),
  }));

  const addDiscountTier = () => setEditing((prev) => ({
    ...prev,
    tiers: [...(prev.tiers || []), { min_qty: "", pct: "" }],
  }));

  const updateDiscountTier = (i, patch) => setEditing((prev) => ({
    ...prev,
    tiers: prev.tiers.map((tr, k) => (k === i ? { ...tr, ...patch } : tr)),
  }));

  const removeDiscountTier = (i) => setEditing((prev) => ({
    ...prev,
    tiers: prev.tiers.filter((_, k) => k !== i),
  }));

  const openEdit = (p) => {
    setEditing({
      ...p,
      stock: p.stock ?? "",
      price_tiers: p.price_tiers || [],
      tiers: (p.tiers || []).map((t) => ({ min_qty: t.min_qty, pct: Math.round(t.discount * 10000) / 100 })),
      // Rabais unique + visibilité des paliers (demande du propriétaire, 2026-10-22)
      show_tiers_client: !!p.show_tiers_client,
      discount_rule: p.discount_rule?.pct
        ? { pct: p.discount_rule.pct, min_qty: p.discount_rule.min_qty || "" }
        : { pct: "", min_qty: "" },
    });
  };

  const addSize = () => setEditing((prev) => ({
    ...prev,
    sizes: [...(prev.sizes || []), { id: `s${Math.random().toString(36).slice(2, 8)}`, label: "", price: "", cost: "" }],
  }));

  const updateSize = (i, patch) => setEditing((prev) => ({
    ...prev,
    sizes: prev.sizes.map((s, k) => (k === i ? { ...s, ...patch } : s)),
  }));

  const removeSize = (i) => setEditing((prev) => ({
    ...prev,
    sizes: prev.sizes.filter((_, k) => k !== i),
  }));

  const addFinish = () => setEditing((prev) => ({
    ...prev,
    finishes: [...(prev.finishes || []), { id: `f${Math.random().toString(36).slice(2, 8)}`, label: "", label_en: "", extra: 0 }],
  }));

  const updateFinish = (i, patch) => setEditing((prev) => ({
    ...prev,
    finishes: prev.finishes.map((f, k) => (k === i ? { ...f, ...patch } : f)),
  }));

  const removeFinish = (i) => setEditing((prev) => ({
    ...prev,
    finishes: prev.finishes.filter((_, k) => k !== i),
  }));

  // Téléversement générique vers un champ photo précis du produit (ruban, insertion…)
  const uploadSlotImage = (field, inputRef) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (5 Mo max)");
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form);
      setEditing((prev) => ({ ...prev, [field]: data.url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingImage(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (5 Mo max)");
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form);
      setEditing((prev) => ({ ...prev, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingImage(false);
      if (imageInput.current) imageInput.current.value = "";
    }
  };

  // Photos supplémentaires du même produit (galerie de la fiche) — plusieurs photos par code
  const uploadExtraImage = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    if ((editing?.images?.length || 0) + files.length > 10) {
      toast.error("Maximum 10 photos supplémentaires par produit");
      return;
    }
    setUploadingImage(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} : trop volumineux (5 Mo max)`); continue; }
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/uploads/image", form);
        setEditing((prev) => ({ ...prev, images: [...(prev.images || []), data.url] }));
      }
      toast.success("Photo(s) ajoutée(s) à la galerie");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingImage(false);
      if (extraImageInput.current) extraImageInput.current.value = "";
    }
  };

  const updateStatus = async (orderNumber, status) => {
    // « Livrée » en main propre / ramassage : popup de SIGNATURE d'abord (demande
    // du propriétaire, 2026-10-08) — le client signe à l'écran, facture finale ensuite
    const ord0 = orders.find((o) => o.order_number === orderNumber);
    if (status === "livree" && ord0?.status !== "livree"
        && ["main_propre", "ramassage"].includes(ord0?.delivery_method)) {
      setSignOrder(ord0);
      return;
    }
    // Case « Sans courriel » de la commande (demande du propriétaire) : le statut
    // change, mais AUCUN courriel ne part au client (ni avis de statut, ni avis Google)
    const silent = !!orderSilent[orderNumber];
    try {
      await api.patch(`/admin/orders/${orderNumber}/status`, { status, ...(silent ? { skip_email: true } : {}) });
      setOrders((prev) => prev.map((o) => (o.order_number === orderNumber ? { ...o, status } : o)));
      if (detail?.order_number === orderNumber) setDetail((d) => ({ ...d, status }));
      toast.success(silent ? "Statut mis à jour — aucun courriel envoyé au client" : "Statut mis à jour");
      const ord = orders.find((o) => o.order_number === orderNumber);
      if (status === "expediee" && !ord?.tracking_number) {
        toast.info("Pensez à ajouter le numéro de suivi dans le détail de la commande — le client le recevra par courriel", { duration: 6000 });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Popup « Signature de livraison » des commandes boutique (main propre /
  // ramassage) : signé → facture finale au client ; sauté → raison tracée
  const confirmerSignatureCommande = async (file, nom, photo, sansCourriel) => {
    setSignOrderBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", nom);
      if (photo) form.append("photo", photo);
      if (sansCourriel) form.append("no_email", "true");
      await api.post(`/admin/orders/${signOrder.order_number}/livraison-signature`, form);
      const patch = { status: "livree", delivery_signature: { name: nom, at: new Date().toISOString(), skipped: false } };
      setOrders((prev) => prev.map((o) => (o.order_number === signOrder.order_number ? { ...o, ...patch } : o)));
      if (detail?.order_number === signOrder.order_number) setDetail((d) => ({ ...d, ...patch }));
      toast.success(sansCourriel
        ? `Livrée — signée par ${nom}. AUCUN courriel envoyé au client (case cochée).`
        : `Livrée — signée par ${nom}${photo ? ", photo jointe" : ""}. Facture finale envoyée au client (copie à info@trophee.ca).`);
      setSignOrder(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSignOrderBusy(false);
    }
  };

  const sansSignatureCommande = async (raisonTxt, sansCourriel) => {
    setSignOrderBusy(true);
    try {
      const form = new FormData();
      form.append("skip", "true");
      form.append("reason", raisonTxt);
      if (sansCourriel) form.append("no_email", "true");
      await api.post(`/admin/orders/${signOrder.order_number}/livraison-signature`, form);
      const patch = { status: "livree", delivery_signature: { skipped: true, reason: raisonTxt, at: new Date().toISOString() } };
      setOrders((prev) => prev.map((o) => (o.order_number === signOrder.order_number ? { ...o, ...patch } : o)));
      if (detail?.order_number === signOrder.order_number) setDetail((d) => ({ ...d, ...patch }));
      toast.success(sansCourriel
        ? "Marquée « Livrée » SANS signature ni courriel — raison tracée dans l'historique."
        : "Marquée « Livrée » SANS signature — raison tracée dans l'historique.");
      setSignOrder(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSignOrderBusy(false);
    }
  };

  const saveTracking = async () => {
    if (!detail) return;
    if (carrier === "autre" && !carrierOther.trim()) {
      toast.error("Inscrivez le nom du transporteur (« Autre »).");
      return;
    }
    try {
      const costVal = shipCost.trim() === "" ? null : Math.max(0, Number(shipCost.replace(",", ".")) || 0);
      await api.patch(`/admin/orders/${detail.order_number}/status`, {
        status: detail.status, tracking_number: tracking, tracking_carrier: carrier,
        tracking_carrier_other: carrier === "autre" ? carrierOther.trim() : "",
        ...(costVal != null ? { shipping_cost: costVal } : {}),
        // respecte la case « Sans courriel » de la commande (demande du propriétaire)
        ...(orderSilent[detail.order_number] ? { skip_email: true } : {}),
      });
      setOrders((prev) => prev.map((o) => (o.order_number === detail.order_number
        ? { ...o, tracking_number: tracking, tracking_carrier: carrier,
            tracking_carrier_other: carrier === "autre" ? carrierOther.trim() : "",
            ...(costVal != null ? { shipping_cost: costVal } : {}) } : o)));
      setDetail((d) => ({ ...d, tracking_number: tracking, tracking_carrier: carrier,
                         tracking_carrier_other: carrier === "autre" ? carrierOther.trim() : "",
                         ...(costVal != null ? { shipping_cost: costVal } : {}) }));
      toast.success("Numéro de suivi enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const refundOrder = async () => {
    if (!detail) return;
    const remaining = Math.round((detail.total - (detail.refunded_total || 0)) * 100) / 100;
    const amount = refundAmount === "" ? remaining : Math.round(parseFloat(refundAmount) * 100) / 100;
    if (!amount || amount <= 0) {
      toast.error("Entrez un montant supérieur à 0");
      return;
    }
    try {
      const { data } = await api.post(`/admin/orders/${detail.order_number}/refund`, { amount });
      const patch = {
        refunded_total: data.refunded_total,
        refunds: [...(detail.refunds || []), { amount: data.refunded }],
        ...(data.full ? { status: "remboursee" } : {}),
      };
      setOrders((prev) => prev.map((o) => (o.order_number === detail.order_number ? { ...o, ...patch } : o)));
      setDetail((d) => ({ ...d, ...patch }));
      setRefundAmount("");
      toast.success(data.full
        ? "Commande remboursée entièrement via Stripe"
        : `Remboursement partiel de ${fmt(data.refunded)} envoyé via Stripe`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Onglets regroupés par univers (demande du propriétaire — maquette A, 5 couleurs) :
  // une rangée de groupes colorés ; le groupe de l'onglet actif se déplie tout seul,
  // un clic sur un autre groupe déplie ses sous-onglets sans changer de page.
  const GROUPS = [
    { id: "ventes", label: "Ventes", badge: stats?.quotes_unanswered ?? 0,
      ring: "border-emerald-400/40 text-emerald-300 hover:border-emerald-300",
      open: "bg-emerald-400/15 border-emerald-400 text-emerald-300",
      solid: "bg-emerald-400 text-ink border-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.35)]",
      tabs: [
        { id: "soumissions", label: (
          <>
            Ventes &amp; Soumissions ({stats?.quotes_unanswered ?? 0} non répondue{(stats?.quotes_unanswered ?? 0) > 1 ? "s" : ""})
            {/* Macaron rouge (demande du propriétaire, 2026-10-22) : tant qu'il
                reste des fiches à compléter, l'onglet le montre sans l'ouvrir */}
            {(stats?.fiches_incompletes ?? 0) > 0 && (
              <span data-testid="badge-fiches-incompletes"
                title={`${stats.fiches_incompletes} fiche(s) à compléter (coordonnées manquantes) — aussi signalées au rapport du soir`}
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold align-middle">
                {stats.fiches_incompletes}
              </span>
            )}
          </>
        ) },
        { id: "commandes", label: `Commandes (${orders.length})` },
        { id: "paniers", label: `Paniers (${carts.length})` },
        { id: "rapports", label: "Rapports" },
      ] },
    { id: "catalogue", label: "Boutique & catalogues",
      ring: "border-gold/40 text-gold hover:border-gold",
      open: "bg-gold/15 border-gold text-gold",
      solid: "bg-gold text-ink border-gold shadow-[0_0_18px_rgba(212,175,55,0.35)]",
      tabs: [
        { id: "produits", label: `Gestion boutique (${prodTotal})` },
        { id: "zones", label: "Catalogues" },
        { id: "forfaits", label: "Forfaits" },
        { id: "occasions", label: "Occasions" },
      ] },
    { id: "clients", label: "Clients",
      ring: "border-sky-400/40 text-sky-300 hover:border-sky-300",
      open: "bg-sky-400/15 border-sky-400 text-sky-300",
      solid: "bg-sky-400 text-ink border-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.35)]",
      tabs: [
        { id: "clients", label: `Clients (${customers.length})` },
        { id: "prospects", label: "Prospects" },
        { id: "avis", label: "Avis" },
        { id: "rebonds", label: "Rebonds" },
        { id: "assistant", label: "Clavardage en direct" },
      ] },
    { id: "marketing", label: "Marketing",
      ring: "border-violet-400/40 text-violet-300 hover:border-violet-300",
      open: "bg-violet-400/15 border-violet-400 text-violet-300",
      solid: "bg-violet-400 text-ink border-violet-400 shadow-[0_0_18px_rgba(167,139,250,0.35)]",
      tabs: [
        { id: "blogue", label: `Blogue (${posts.length})` },
        { id: "campagnes", label: "Campagnes" },
        { id: "publicites", label: "Publicités IA" },
        { id: "motscles", label: "Mots-clés" },
        { id: "backlinks", label: "Backlinks" },
        { id: "pages", label: "Pages (contenu)" },
        { id: "realisations", label: "Réalisations" },
      ] },
    { id: "systeme", label: "Système",
      ring: "border-ivory/25 text-ivory/70 hover:border-ivory/60",
      open: "bg-ivory/10 border-ivory/50 text-ivory",
      solid: "bg-ivory text-ink border-ivory shadow-[0_0_18px_rgba(245,239,221,0.25)]",
      tabs: [
        { id: "parametres", label: "Paramètres" },
      ] },
  ];
  const GROUP_OF = Object.fromEntries(GROUPS.flatMap((g) => g.tabs.map((t) => [t.id, g.id])));
  // VENDEUR (demande du propriétaire) : seuls les groupes contenant une section
  // cochée sont visibles — chaque onglet est en mode lecteur ou éditeur
  const visibleGroups = isVendeur
    ? (() => {
        const g = GROUPS.map((grp) => ({ ...grp, tabs: grp.tabs.filter((t) => t.id in sectionsMap) }))
          .filter((grp) => grp.tabs.length);
        // La double vérification (2FA) s'impose AUSSI aux vendeurs (demande du
        // propriétaire) : Paramètres est TOUJours visible pour eux (carte 2FA)
        if (!("parametres" in sectionsMap)) {
          const sys = GROUPS.find((x) => x.id === "systeme");
          if (sys) g.push({ ...sys, tabs: sys.tabs.filter((t) => t.id === "parametres") });
        }
        return g;
      })()
    : GROUPS;
  const shownGroup = openGroup ?? GROUP_OF[tab] ?? null;  // suit l'onglet actif par défaut
  const shownGrp = visibleGroups.find((g) => g.id === shownGroup);

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-44 overflow-x-clip">
      <SEO title="Administration | Mr Trophée" description="Tableau de bord d'administration." path="/admin" noindex />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">Administration</p>
        <h1 className="font-serif text-5xl md:text-6xl mb-10">Tableau de bord</h1>
      </FadeUp>

      {/* AVIS DE 30 JOURS 2FA (demande du propriétaire) : tant que le membre du
          personnel (admin OU vendeur) n'a pas activé la double vérification,
          le compte à rebours s'affiche en haut */}
      {(user?.role === "admin" || user?.role === "vendeur") && !user?.totp_enabled && user?.twofa_required_after
        && !window.location.hostname.includes("preview.emergentagent.com") && (  /* aperçu : 2FA jamais obligatoire (demande du propriétaire) */
        <div data-testid="admin-2fa-notice"
          className="mb-8 border border-amber-400/40 bg-amber-400/10 rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0" />
          <p className="text-sm text-amber-200 flex-1 min-w-[260px]">
            <strong>Sécurité :</strong> la double vérification (2FA) deviendra <strong>obligatoire
            le {new Date(user.twofa_required_after + "T00:00:00").toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" })}</strong>
            {" "}— dans {Math.max(0, Math.ceil((new Date(user.twofa_required_after + "T00:00:00") - Date.now()) / 86400000))} jours.
            Activez-la dès maintenant pour éviter tout blocage à la connexion.
          </p>
          <button onClick={() => setTab("parametres")} data-testid="admin-2fa-notice-cta"
            className="text-xs font-semibold bg-amber-400 text-ink rounded-full px-4 py-2 hover:bg-amber-300 transition-colors duration-300">
            Activer maintenant
          </button>
        </div>
      )}

      {/* Rangée principale : Aperçu + 5 groupes colorés (Ventes, Catalogue, Clients,
          Marketing, Système) — chaque groupe déplie ses sous-onglets */}
      <div className={`flex gap-3 flex-wrap items-center ${shownGrp ? "mb-4" : "mb-12"}`}>
        {(!isVendeur || "apercu" in sectionsMap) && (
        <button data-testid="admin-tab-apercu" onClick={() => { setTab("apercu"); setOpenGroup(null); }}
          className={`px-6 py-2.5 rounded-full border transition-all duration-300 ${
            tab === "apercu" ? "bg-gold text-ink border-gold font-bold text-base shadow-[0_0_18px_rgba(212,175,55,0.35)]" : "text-sm border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"}`}>
          Aperçu
        </button>
        )}
        {visibleGroups.map((g) => (
          <button key={g.id} data-testid={`admin-group-${g.id}`}
            onClick={() => setOpenGroup(openGroup === g.id ? null : g.id)}
            className={`px-6 py-2.5 rounded-full border transition-all duration-300 flex items-center gap-2 text-sm font-semibold ${
              shownGroup === g.id ? g.open : g.ring}`}>
            {g.label}
            {g.badge > 0 && (
              <span data-testid={`admin-group-badge-${g.id}`}
                className="bg-emerald-400 text-ink text-[10px] font-extrabold rounded-full px-1.5 py-0.5 leading-none">
                {g.badge}
              </span>
            )}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${shownGroup === g.id ? "rotate-180" : ""}`} />
          </button>
        ))}
      </div>

      {/* Sous-onglets du groupe déplié — l'onglet actif prend la couleur du groupe */}
      {shownGrp && (
        <div data-testid="admin-subtabs"
          className="flex gap-2.5 mb-12 flex-wrap border border-ivory/10 rounded-2xl p-4 bg-ivory/[0.02]">
          {shownGrp.tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); setOpenGroup(null); }}
              className={`px-5 py-2 rounded-full border transition-all duration-300 ${
                tab === t.id ? `font-bold text-base ${shownGrp.solid}` : `text-sm ${shownGrp.ring}`}`}>
              {t.label}
            </button>
          ))}
          {/* « Demandes d'appel » (demande du propriétaire) : une PILULE dans la
              rangée Ventes — la liste s'ouvre en popup, plus de carte étalée */}
          {shownGrp.id === "ventes" && (
            <button data-testid="admin-tab-call-requests" onClick={() => setShowCalls(true)}
              className={`px-5 py-2 rounded-full border transition-all duration-300 text-sm inline-flex items-center gap-1.5 ${shownGrp.ring}`}>
              <Phone className="w-3.5 h-3.5" /> Demandes d'appel
              {callPending > 0 && (
                <span data-testid="admin-tab-call-requests-badge"
                  className="bg-emerald-400 text-ink text-[10px] font-extrabold rounded-full px-1.5 py-0.5 leading-none">
                  {callPending}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Popup « Demandes d'appel » (demande du propriétaire) : la liste des
          visiteurs à rappeler vit ICI — ouverte par la pilule de la rangée Ventes */}
      {showCalls && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="call-requests-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => { setShowCalls(false); loadCalls(); }} aria-label="Fermer" />
          <div data-lenis-prevent className="relative bg-ink border border-gold/25 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain p-3 sm:p-5 shadow-2xl shadow-black/60">
            <button onClick={() => { setShowCalls(false); loadCalls(); }} data-testid="call-requests-close"
              className="absolute top-4 right-4 z-10 bg-ink border border-ivory/15 rounded-full p-1.5 text-ivory/60 hover:text-gold hover:border-gold/50 transition-colors" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
            <div className="pt-8"><CallRequestsCard /></div>
          </div>
        </div>
      )}

      {/* BANDEAU PANNE (notif immédiate, demande du propriétaire, 2026-10-28) :
          la sonde aux 15 min a détecté qu'une page du site en ligne ne répond
          plus — visible par l'atelier dans TOUT l'admin, peu importe l'onglet ;
          disparait tout seul au rétablissement */}
      {sondeEtat?.en_panne && (
        <div className="mb-5 rounded-xl border-2 border-red-500 bg-red-500/15 px-5 py-4 flex items-start gap-3"
          data-testid="bandeau-panne" role="alert">
          <ShieldAlert className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-300">Site en ligne en difficulté — mrtrophee.ca</p>
            <p className="text-sm text-red-200/80 mt-1">
              La sonde (aux 15 minutes) signale : {sondeEtat.details || "une page ne répond plus"}.
              Détecté {sondeEtat.depuis ? `le ${fmtDateMtl(sondeEtat.depuis)}` : "récemment"}.
              Un courriel et un message Messenger ont été envoyés — le bandeau disparaitra au rétablissement.
            </p>
          </div>
        </div>
      )}

      {/* BANDEAU FILE BLOQUÉE (demande du propriétaire, 2026-10-29) : un courriel
          transactionnel attend en file depuis plus d'une heure — visible partout
          dans l'admin ; un courriel + Messenger ont aussi été envoyés ; disparait
          au déblocage */}
      {sondeEtat?.file_bloquee?.active && (
        <div className="mb-5 rounded-xl border-2 border-amber-500 bg-amber-500/15 px-5 py-4 flex items-start gap-3"
          data-testid="bandeau-file-bloquee" role="alert">
          <Mail className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-300">File de courriels bloquée</p>
            <p className="text-sm text-amber-200/80 mt-1">
              {sondeEtat.file_bloquee.compte} courriel(s) attend(ent) en file depuis plus d'une heure
              {sondeEtat.file_bloquee.depuis ? ` (depuis le ${fmtDateMtl(sondeEtat.file_bloquee.depuis)})` : ""}.
              La file réessaie chaque minute — un courriel et un message Messenger vous ont été envoyés.
            </p>
          </div>
        </div>
      )}

      {tab === "apercu" && stats && (<>
        <WebhookStatutChip />
        <SubHead testid="apercu-group-ventes">Ventes et clients</SubHead>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-stats">
          {[
            // TOUTES les ventes comptent (demande du propriétaire, 2026-10-06) :
            // boutique ET soumissions — le détail par canal est sous chaque carte
            { label: "Revenus confirmés", value: fmt(stats.revenue), testid: "stat-revenue",
              sub: `boutique ${fmt(stats.revenue_shop || 0)} · soumissions ${fmt(stats.revenue_quotes || 0)}` },
            { label: "Ventes payées", value: stats.paid_orders, testid: "stat-paid",
              sub: `boutique ${stats.paid_shop || 0} · soumissions ${stats.paid_quotes || 0}` },
            { label: "Ventes totales", value: stats.total_orders, testid: "stat-orders",
              sub: `boutique ${stats.total_shop || 0} · soumissions ${stats.total_quotes || 0}` },
            { label: "Clients inscrits", value: stats.customers, testid: "stat-customers" },
          ].map((s) => (
            <div key={s.label} className="bg-surface border border-gold/15 rounded-xl p-7">
              <p className="text-xs uppercase tracking-widest text-ivory/70 mb-2">{s.label}</p>
              <p data-testid={s.testid} className="font-serif text-4xl text-champagne">{s.value}</p>
              {s.sub && <p className="text-[10px] text-ivory/45 mt-1.5" data-testid={`${s.testid}-sub`}>{s.sub}</p>}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-ivory/40 -mt-3" data-testid="stats-note">
          Ventes comptabilisées depuis le 1er août 2026.
        </p>
      </>)}

      {tab === "apercu" && stats && shopSettings && user?.role === "admin" && (
        <ThemeSaisonCard themes={shopSettings?.themes_saison || []}
          forceId={shopSettings?.theme_force_id || ""}
          onChange={(themes) => setShopSettings((s) => ({ ...s, themes_saison: themes }))}
          onForceChange={(id) => setShopSettings((s) => ({ ...s, theme_force_id: id }))} />
      )}

      {tab === "apercu" && stats && <SubHead testid="apercu-group-visiteurs">Visiteurs et pages</SubHead>}
      {tab === "apercu" && stats && <LiveVisitors />}
      {/* Mini-carte des positions GPS consenties (demande du propriétaire 2026-10-10) */}
      {tab === "apercu" && stats && <CarteVisiteurs />}

      {tab === "apercu" && stats && <EngagedVisitors />}

      {tab === "apercu" && stats && <AnalyticsCard />}

      {tab === "apercu" && stats && <SubHead testid="apercu-group-relances">Relances et demandes</SubHead>}
      {tab === "apercu" && stats && <RelancesStats />}

      {tab === "apercu" && stats && <SubHead testid="apercu-group-prospects">Prospects</SubHead>}
      {tab === "apercu" && stats && <ProspectStats />}

      {tab === "apercu" && stats && <SubHead testid="apercu-group-produits">Produits</SubHead>}
      {tab === "apercu" && stats && <HotProducts />}

      {tab === "forfaits" && (<>
        <ForfaitStats />
        <ForfaitSoumissions />
        <div className="bg-surface border border-ivory/10 rounded-xl p-6 mb-8" data-testid="forfait-texts-editor">
          <h3 className="text-[11px] uppercase tracking-widest text-gold mb-1">Textes de la page « Forfaits Tournoi »</h3>
          <p className="text-xs text-ivory/55 mb-5">
            Tous les textes de la page publique /forfaits : titres, slogans, descriptions des médailles et trophées,
            formulaire, portail d'inscription, SEO et bandeau d'annonce — modifiables en français et en anglais,
            en ligne immédiatement après « Enregistrer ». « Réinitialiser » ramène le texte d'origine.
          </p>
          <ContentEditor fixedSection="forfaits" />
        </div>
      </>)}

      {tab === "soumissions" && <QuoteRequestsTab vendeur={isVendeur && tabMode("soumissions") !== "edition"}
        onCycle={() => {
          // Demande du propriétaire (2026-10-21) : l'actualisation automatique
          // des 30 s met à jour TOUTE la page, pas seulement la liste — pilules
          // du haut et compteurs des onglets (non répondues, commandes, paniers)
          api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
          api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
          api.get("/admin/carts").then((r) => setCarts(r.data.carts || [])).catch(() => {});
        }} />}
      {tab === "rapports" && (
        <div className="space-y-6">
          {/* RAPPORT DES RETARDS (demande du propriétaire, 2026-10-27) :
              dossiers passés dûs selon l'entente + total en souffrance */}
          <RapportRetards />
          {/* VISITES DES ROBOTS ET DES IA — MOIS EN COURS (demande du
              propriétaire, 2026-10-28 : « afficher dans Rapports, sans
              attendre le bilan ») — mêmes chiffres que la section du bilan
              mensuel ; minimums observés (requêtes qui rejoignent notre
              serveur : sitemap, images, API) */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="visites-bots-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold">Visites des robots et des IA — mois en cours</p>
                <p className="text-xs text-ivory/60 mt-1 max-w-xl">
                  Combien de fois les robots des moteurs et des IA (ChatGPT, Claude, Perplexity, Google…)
                  ont visité le site ce mois-ci. Comptés sur les requêtes qui rejoignent notre serveur
                  (sitemap, images, API) — chiffres minimums.
                </p>
              </div>
              <button type="button" data-testid="visites-bots-refresh"
                onClick={() => api.get("/admin/visites-bots").then((r) => setVisitesBots(r.data)).catch(() => {})}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-ivory/20 text-ivory/70 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
              </button>
            </div>
            {!visitesBots ? (
              <p className="text-xs text-ivory/40">Chargement…</p>
            ) : visitesBots.total === 0 ? (
              <p className="text-xs text-ivory/50" data-testid="visites-bots-vide">
                Aucune visite de robot enregistrée ce mois-ci pour l'instant.
              </p>
            ) : (
              <>
                <p className="text-sm text-ivory/80 mb-3" data-testid="visites-bots-total">
                  <strong className="text-gold text-lg">{visitesBots.total}</strong> visite(s) de robots en {visitesBots.mois}
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <table className="w-full text-sm" data-testid="visites-bots-table">
                    <tbody>
                      {visitesBots.par_bot.map((b) => (
                        <tr key={b.bot} className="border-b border-ivory/10">
                          <td className="py-1.5 text-ivory/80">{b.bot}</td>
                          <td className="py-1.5 text-right font-semibold text-ivory">{b.n}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {visitesBots.recents?.length > 0 && (
                    <div data-testid="visites-bots-recents">
                      <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Derniers passages</p>
                      <ul className="space-y-1 text-xs text-ivory/60">
                        {visitesBots.recents.slice(0, 8).map((v, i) => (
                          <li key={i} className="truncate">
                            <span className="text-ivory/80">{v.bot}</span> — {v.path} · {fmtDateMtl(v.at)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {/* FILE DES COURRIELS EN CHIFFRES (demande du propriétaire,
              2026-10-28 : « combien de courriels de masse sont en file et
              combien de ventes sont passées devant ») — les envois importants
              (soumissions, ventes, matrices, comptes) passent TOUJOURS devant
              le mass mailing ; le compteur les additionne */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="file-courriels-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold">File des courriels — en chiffres</p>
                <p className="text-xs text-ivory/60 mt-1 max-w-xl">
                  Pendant un envoi de masse, les courriels importants (soumissions, ventes, matrices, comptes)
                  passent toujours devant — l'envoi de masse patiente et repart ensuite.
                </p>
              </div>
              <button type="button" data-testid="file-courriels-refresh"
                onClick={() => api.get("/admin/file-courriels").then((r) => setFileCourriels(r.data)).catch(() => {})}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-ivory/20 text-ivory/70 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
              </button>
            </div>
            {!fileCourriels ? (
              <p className="text-xs text-ivory/40">Chargement…</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="file-courriels-stats">
                <div className="bg-ink border border-ivory/10 rounded-lg p-4" data-testid="file-courriels-masse">
                  <p className="text-2xl font-bold text-ivory">{fileCourriels.en_file_masse}</p>
                  <p className="text-[10px] uppercase tracking-widest text-ivory/50 mt-1">En file — envois de masse</p>
                </div>
                <div className="bg-ink border border-ivory/10 rounded-lg p-4" data-testid="file-courriels-prio">
                  <p className="text-2xl font-bold text-ivory">{fileCourriels.en_file_prioritaires}</p>
                  <p className="text-[10px] uppercase tracking-widest text-ivory/50 mt-1">En file — importants (passent devant)</p>
                </div>
                <button type="button" onClick={() => setShowDevant((s) => !s)}
                  title="Cliquer pour voir la liste (date et destinataire)"
                  className="bg-ink border border-gold/25 rounded-lg p-4 text-left hover:border-gold transition-colors cursor-pointer"
                  data-testid="file-courriels-devant-mois">
                  <p className="text-2xl font-bold text-gold">{fileCourriels.devant_mois}</p>
                  <p className="text-[10px] uppercase tracking-widest text-ivory/50 mt-1">Passées devant ce mois-ci {showDevant ? "▴" : "▾"}</p>
                </button>
                <button type="button" onClick={() => setShowDevant((s) => !s)}
                  title="Cliquer pour voir la liste (date et destinataire)"
                  className="bg-ink border border-ivory/10 rounded-lg p-4 text-left hover:border-gold transition-colors cursor-pointer"
                  data-testid="file-courriels-devant-total">
                  <p className="text-2xl font-bold text-ivory/80">{fileCourriels.devant_total}</p>
                  <p className="text-[10px] uppercase tracking-widest text-ivory/50 mt-1">Passées devant au total {showDevant ? "▴" : "▾"}</p>
                </button>
              </div>
            )}
            {/* DÉTAIL au clic (demande du propriétaire, 2026-10-28) : chaque
                courriel passé devant la masse, avec date et destinataire */}
            {showDevant && fileCourriels && (
              <div className="mt-4 border-t border-ivory/10 pt-3" data-testid="file-courriels-devant-liste">
                {(fileCourriels.devant_liste || []).length === 0 ? (
                  <p className="text-xs text-ivory/50">Aucune vente n'a encore doublé la masse — la liste se remplira au prochain envoi de masse.</p>
                ) : (
                  <ul className="space-y-1 max-h-56 overflow-y-auto">
                    {fileCourriels.devant_liste.map((e, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 text-xs text-ivory/75 border-b border-ivory/5 py-1.5"
                        data-testid={`file-courriels-devant-item-${i}`}>
                        <span className="truncate"><strong className="text-champagne">{e.to}</strong> — {e.subject}</span>
                        <span className="text-ivory/45 whitespace-nowrap">{fmtDateMtl(e.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* JOURNAL DES ENVOIS (demande du propriétaire, 2026-10-29 : « fil des
              derniers courriels partis, à chaque jour ») — groupé par jour, chaque
              jour dépliable : destinataire, sujet, canal, compte, CC et heure */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="journal-envois-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold">Journal des envois — jour par jour</p>
                <p className="text-xs text-ivory/60 mt-1 max-w-xl">
                  Chaque courriel parti du site (destinataire, sujet, heure) — pour voir d'un coup d'œil que tout part bien.
                </p>
              </div>
              <button type="button" data-testid="journal-envois-refresh"
                onClick={() => api.get("/admin/journal-envois").then((r) => setJournalEnvois(r.data)).catch(() => {})}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-ivory/20 text-ivory/70 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
              </button>
            </div>
            {!journalEnvois ? (
              <p className="text-xs text-ivory/40">Chargement…</p>
            ) : (journalEnvois.jours || []).length === 0 ? (
              <p className="text-xs text-ivory/50" data-testid="journal-envois-vide">
                Aucun courriel consigné pour l'instant — le journal se remplit à mesure que le site envoie (soumissions, ventes, épreuves…).
              </p>
            ) : (
              <div className="space-y-2" data-testid="journal-envois-jours">
                {journalEnvois.jours.map((j) => (
                  <div key={j.jour} className="border border-ivory/10 rounded-lg overflow-hidden">
                    <button type="button" onClick={() => setShowJour((s) => (s === j.jour ? null : j.jour))}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-ink hover:bg-surface/60 transition-colors"
                      data-testid={`journal-jour-${j.jour}`}>
                      <span className="text-sm font-semibold text-ivory">{fmtDateMtl(j.jour)}</span>
                      <span className="text-xs text-ivory/55">
                        <span className="text-gold font-bold">{j.nb}</span> courriel{j.nb > 1 ? "s" : ""} {showJour === j.jour ? "▴" : "▾"}
                      </span>
                    </button>
                    {showJour === j.jour && (
                      <ul className="divide-y divide-ivory/5 max-h-72 overflow-y-auto" data-testid={`journal-jour-liste-${j.jour}`}>
                        {j.envois.map((e, i) => (
                          <li key={i} className="px-4 py-2 text-xs" data-testid={`journal-envoi-${j.jour}-${i}`}>
                            <div className="flex items-center justify-between gap-3">
                              <span className="truncate text-champagne font-semibold">{e.to}</span>
                              <span className="text-ivory/45 whitespace-nowrap">{(e.at || "").slice(11, 16)}</span>
                            </div>
                            <p className="text-ivory/70 truncate mt-0.5">{e.subject}</p>
                            <p className="text-[10px] text-ivory/40 mt-0.5">
                              {e.canal}{e.compte ? ` · ${e.compte}` : ""}
                              {Array.isArray(e.cc) && e.cc.length ? ` · CC : ${e.cc.join(", ")}` : ""}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
              le même courriel que celui du 1er — ventes, livraisons, ventes
              perdues, comparaison au mois précédent et aux 5 mêmes mois passés */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="bilan-mois-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-semibold">Bilans mensuel, trimestriel et annuel</p>
                <p className="text-xs text-ivory/70 mt-1 max-w-xl">
                  Le résumé complet de la période (ventes, livraisons, ventes perdues, comparaisons)
                  envoyé à info@trophee.ca — les mêmes courriels que ceux qui partent automatiquement
                  le 1er à 8 h (mois, et trimestre les 1er janv./avr./juill./oct.) et le 1er janvier (année).
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" data-testid="bilan-mois-send" disabled={bilanBusy}
                  onClick={async () => {
                    setBilanBusy(true);
                    try {
                      const { data } = await api.post("/admin/rapports/bilan-mois");
                      toast.success(`Bilan de ${data.mois} envoyé — ${data.ventes} vente(s) (${data.total.toFixed(2)} $), ${data.livres} colis livrés, ${data.perdues} vente(s) perdue(s).`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    finally { setBilanBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors disabled:opacity-60">
                  {bilanBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Recevoir le bilan du mois
                </button>
                <button type="button" data-testid="bilan-trimestre-send" disabled={bilanTrimBusy}
                  onClick={async () => {
                    setBilanTrimBusy(true);
                    try {
                      const { data } = await api.post("/admin/rapports/bilan-trimestre");
                      toast.success(`Bilan du ${data.trimestre} envoyé — ${data.ventes} vente(s) (${data.total.toFixed(2)} $), ${data.livres} colis livrés, ${data.perdues} vente(s) perdue(s).`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    finally { setBilanTrimBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                  {bilanTrimBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Recevoir le bilan du trimestre
                </button>
                <button type="button" data-testid="bilan-annee-send" disabled={bilanAnneeBusy}
                  onClick={async () => {
                    setBilanAnneeBusy(true);
                    try {
                      const { data } = await api.post("/admin/rapports/bilan-annee");
                      toast.success(`Bilan de l'année ${data.annee} envoyé — ${data.ventes} vente(s) (${data.total.toFixed(2)} $), ${data.livres} colis livrés, ${data.perdues} vente(s) perdue(s).`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    finally { setBilanAnneeBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/80 px-5 py-2.5 rounded-full text-sm hover:border-gold/60 hover:text-gold transition-colors disabled:opacity-60">
                  {bilanAnneeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Recevoir le bilan de l'année
                </button>
              </div>
            </div>
          </div>
          <DelivrabiliteCard />
          <TaxReportTab isAdmin={user?.role === "admin"} />
        </div>
      )}

      {tab === "commandes" && (
        <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="admin-orders-table">
          {/* Barre d'outils (manifeste, courriels de test) — réservée à l'admin ;
              le vendeur en mode éditeur agit dans les fiches, pas ici */}
          {!isVendeur && (
          <div className="flex flex-wrap justify-end gap-2 sm:gap-3 p-4 border-b border-ivory/10">
            {/* Manifeste du jour (demande du propriétaire) : toutes les étiquettes
                Stallion du jour (commandes + soumissions) en un seul PDF */}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/stallion/manifest`} target="_blank" rel="noreferrer"
              data-testid="orders-manifest"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-gold/40 text-gold px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
              <Truck className="w-4 h-4" /> Manifeste du jour
            </a>
            <button
              data-testid="preview-order-email-btn"
              onClick={() => openEmailPreview("confirmation")}
              title="Voir le courriel de confirmation à l'écran — rien n'est envoyé"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-ivory/20 text-ivory/70 px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm rounded-full hover:border-gold/60 hover:text-gold transition-colors duration-300"
            >
              <Eye className="w-4 h-4" /> Voir sans envoyer
            </button>
            <button
              data-testid="test-order-email-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/test-order-email");
                  toast.success(`Courriel de test envoyé à ${data.to} — regardez le « Merci, champion. » en écriture signature.`);
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }}
              title="Recevoir un exemple du courriel de confirmation de commande (sans vraie commande)"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-ivory/20 text-ivory/70 px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm rounded-full hover:border-gold/60 hover:text-gold transition-colors duration-300"
            >
              <Mail className="w-4 h-4" /> Courriel de test
            </button>
            <a
              href={`${API_BASE}/api/admin/orders/export`}
              data-testid="export-orders-btn"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-gold/40 text-gold px-4 py-2 text-xs sm:px-5 sm:py-2.5 sm:text-sm rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Download className="w-4 h-4" /> Exporter Excel
            </a>
          </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="border-ivory/10 hover:bg-transparent">
                <TableHead className="text-ivory/60">Commande</TableHead>
                <TableHead className="text-ivory/60">Client</TableHead>
                <TableHead className="text-ivory/60">Contenu</TableHead>
                <TableHead className="text-ivory/60">Livraison</TableHead>
                <TableHead className="text-ivory/60 text-right">Total</TableHead>
                <TableHead className="text-ivory/60">Statut</TableHead>
                <TableHead className="text-ivory/60">Fiche</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.order_number} className="border-ivory/10" data-testid={`admin-order-${o.order_number}`}>
                  <TableCell className="font-serif text-lg text-champagne">
                    {o.order_number}
                    {o.payment_method && (
                      <span className={`block text-[10px] font-sans not-italic tracking-widest uppercase mt-1 ${o.payment_method === "paypal" ? "text-sky-400" : "text-violet-300"}`}>
                        {o.payment_method === "paypal" ? "PayPal" : "Carte"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{custName(o.customer)}</p>
                    <p className="text-xs text-ivory/65">{o.customer.company || o.user_email}</p>
                    {/* Adresse du client visible DÈS QUE LA VENTE EST PAYÉE (demande du propriétaire) */}
                    {["payee", "en_production", "expediee", "livree"].includes(o.status) && (
                      <p className="text-xs text-gold/90 mt-1" data-testid={`order-address-${o.order_number}`}>
                        {[custAddress(o.customer), o.customer.city, o.customer.province, o.customer.postal_code].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-ivory/60 max-w-[260px]">
                    {o.items.map((i, k) => (
                      <span key={k} className="block">
                        <ExpandableText text={`${i.qty}× ${i.name} (${i.size_label})`}
                          testId={`order-item-${o.order_number}-${k}`} className="block" />
                        {/* Gravure ligne par ligne (demande du propriétaire 2026-10-10) */}
                        <GravureLignes texte={i.inscription_text} testid={`order-gravure-${o.order_number}-${k}`} />
                      </span>
                    ))}
                  </TableCell>
                  <TableCell className="text-xs text-ivory/60">{o.customer.delivery_date}</TableCell>
                  <TableCell className="text-right text-champagne">{fmt(o.total)}</TableCell>
                  <TableCell>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.order_number, v)}>
                      <SelectTrigger data-testid={`order-status-${o.order_number}`} className="w-[190px] bg-ink border-ivory/15 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-gold/20 text-ivory">
                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v} data-testid={`status-option-${v}`}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Case « Sans courriel » (demande du propriétaire) : le changement de
                        statut n'envoie AUCUN courriel au client quand elle est cochée */}
                    <label className="mt-1.5 flex items-center gap-1.5 text-[10px] text-ivory/50 cursor-pointer select-none w-fit hover:text-gold transition-colors"
                      data-testid={`order-silent-${o.order_number}`}>
                      <input type="checkbox" checked={!!orderSilent[o.order_number]}
                        onChange={(e) => setOrderSilent((s) => ({ ...s, [o.order_number]: e.target.checked }))}
                        className="w-3 h-3 accent-[#d4af37] cursor-pointer" />
                      Sans courriel
                    </label>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`order-detail-${o.order_number}`}
                        onClick={() => { setDetail(o); setTracking(o.tracking_number || ""); setCarrier(o.tracking_carrier || ""); setCarrierOther(o.tracking_carrier_other || ""); setShipCost(o.shipping_cost != null ? String(o.shipping_cost) : ""); }}
                        className="text-ivory/70 hover:text-gold transition-colors duration-300"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <DeleteConfirmButton
                        testid={`delete-order-${o.order_number}`}
                        label={`La commande ${o.order_number} sera définitivement supprimée (transactions de paiement incluses).`}
                        onDelete={() => deleteOrder(o.order_number)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-ivory/65 py-10" data-testid="admin-no-orders">Aucune commande pour l'instant.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "produits" && (
        <div>
          {/* Traduction anglaise massive des fiches par Claude (tâche de fond) */}
          <TraductionCard />
          <button
            data-testid="admin-add-product"
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            className="mb-8 inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300"
          >
            <Plus className="w-4 h-4" /> Nouveau produit
          </button>
          <button
            data-testid="generate-images-btn"
            onClick={generateImages}
            disabled={genImages.running}
            className="mb-8 ml-3 inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full hover:bg-gold/10 transition-colors duration-300 disabled:opacity-50"
          >
            {genImages.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {genImages.running ? `Génération IA… ${genImages.done}/${genImages.total || "?"}` : "Images IA manquantes"}
          </button>
          <BulkProductTools onDone={load} />
          {/* filtres par catalogue — regroupés par famille (menu déroulant) + par sport */}
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="products-catalogue-filters">
            <button data-testid="products-filter-all"
              onClick={() => { setProdCatalogue(""); setProdPage(1); }}
              className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${!prodCatalogue ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"}`}>
              Tous
            </button>
            {Object.entries(
              Object.keys({ ...Object.fromEntries(Object.keys(CATALOGUE_NAMES).map((k) => [k, 0])), ...catCounts })
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                .reduce((acc, no) => {
                  const f = catalogueFamily(no);
                  (acc[f] = acc[f] || []).push(no);
                  return acc;
                }, {})
            ).map(([fam, nos]) => nos.length > 1 ? (
              /* famille à plusieurs catalogues (ex. Caldwell 4.0-4.27) : menu déroulant */
              <select key={fam}
                value={nos.includes(prodCatalogue) ? prodCatalogue : ""}
                onChange={(e) => { setProdCatalogue(e.target.value); setProdPage(1); }}
                data-testid={`products-family-${fam}`}
                className={`px-4 py-2 rounded-full text-xs border bg-ink cursor-pointer transition-colors duration-300 ${nos.includes(prodCatalogue) ? "border-gold text-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"}`}>
                <option value="">
                  {FAMILY_NAMES[fam] || `Catalogues ${fam}.x`} ({nos.reduce((s, n) => s + (catCounts[n] || 0), 0)})
                </option>
                {nos.map((no) => (
                  <option key={no} value={no} data-testid={`products-filter-${no}`}>
                    {catalogueShortLabel(no)} ({catCounts[no] || 0})
                  </option>
                ))}
              </select>
            ) : (
              <button key={fam} data-testid={`products-filter-${nos[0]}`}
                onClick={() => { setProdCatalogue(nos[0]); setProdPage(1); }}
                className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${prodCatalogue === nos[0] ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"}`}>
                {catalogueLabel(nos[0])} ({catCounts[nos[0]] || 0})
              </button>
            ))}
            <select value={prodSport}
              onChange={(e) => { setProdSport(e.target.value); setProdPage(1); }}
              data-testid="products-sport-select"
              className={`px-4 py-2 rounded-full text-xs border bg-ink cursor-pointer transition-colors duration-300 ${prodSport ? "border-gold text-gold font-semibold" : "border-gold/40 text-gold/85 hover:border-gold"}`}>
              <option value="">Sport : tous</option>
              {SPORTS.map((s) => (
                <option key={s.id} value={s.id} data-testid={`products-sport-${s.id}`}>{s.fr}</option>
              ))}
            </select>
            {/* Filtre « Sans coûtant » (demande du propriétaire, 2026-10-16) :
                produits sans prix coûtant, pour intervention rapide — admin */}
            {user?.role === "admin" && (
              <button type="button"
                onClick={() => { setProdSansCoutant((v) => !v); setProdPage(1); }}
                data-testid="products-sans-coutant"
                className={`px-4 py-2 rounded-full text-xs border cursor-pointer transition-colors duration-300 ${
                  prodSansCoutant
                    ? "bg-amber-400 text-ink border-amber-400 font-semibold"
                    : "border-amber-400/40 text-amber-300 hover:border-amber-300"}`}>
                {prodSansCoutant ? `Sans coûtant (${prodTotal}) — actif` : "Sans coûtant"}
              </button>
            )}
          </div>
          <div className="mb-4">
            <input data-testid="admin-products-search" value={prodQ}
              onChange={(e) => { setProdQ(e.target.value); setProdPage(1); }}
              placeholder="Rechercher (nom, référence, catégorie)…"              className="w-full max-w-md bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
          </div>
          {/* Regroupement de trophées cochés (demande du propriétaire,
              2026-10-16) : cocher des produits dans le tableau, puis leur
              donner un nom de regroupement commun — admin seulement */}
          {user?.role === "admin" && Object.values(prodSel).some(Boolean) && (
            <div className="mb-4 flex flex-wrap items-center gap-3 bg-gold/5 border border-gold/30 rounded-xl px-4 py-3"
              data-testid="products-group-bar">
              <span className="text-xs text-gold font-semibold">
                {Object.values(prodSel).filter(Boolean).length} produit(s) coché(s)
              </span>
              <input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                data-testid="products-group-name" maxLength={80}
                placeholder="Nom du regroupement (ex. : Séries hockey 2026)"
                className="flex-1 min-w-[220px] bg-ink border border-ivory/15 rounded-lg px-4 py-2 text-sm focus:border-gold focus:outline-none" />
              <button type="button" disabled={prodBulkBusy === "group" || !groupName.trim()}
                onClick={() => bulkGroup(groupName.trim())}
                data-testid="products-group-apply"
                className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors disabled:opacity-50">
                {prodBulkBusy === "group" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Regrouper ensemble
              </button>
              <button type="button" disabled={prodBulkBusy === "group"}
                onClick={() => bulkGroup("")}
                data-testid="products-ungroup"
                className="text-xs border border-ivory/25 text-ivory/70 px-4 py-2 rounded-full hover:border-red-400/60 hover:text-red-300 transition-colors disabled:opacity-50">
                Retirer du regroupement
              </button>
              <button type="button" onClick={() => setProdSel({})}
                data-testid="products-group-clear"
                className="text-xs text-ivory/50 hover:text-gold underline underline-offset-2 transition-colors">
                Tout décocher
              </button>
            </div>
          )}
          <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="admin-products-table">
            <Table>
              <TableHeader>
                <TableRow className="border-ivory/10 hover:bg-transparent">
                  {user?.role === "admin" && <TableHead className="text-ivory/60 w-8" title="Cocher pour regrouper" />}
                  <TableHead className="text-ivory/60">Produit</TableHead>
                  <TableHead className="text-ivory/60">Catégorie</TableHead>
                  <TableHead className="text-ivory/60">Prix de base</TableHead>
                  <TableHead className="text-ivory/60">Paliers</TableHead>
                  <TableHead className="text-ivory/60">Actif</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* regroupés par numéro de catalogue (nº1, 2, 3…), sans catalogue à la fin */}
                {Object.entries(
                  products.reduce((acc, p) => {
                    const k = p.catalogue_no || "";
                    (acc[k] = acc[k] || []).push(p);
                    return acc;
                  }, {})
                ).sort(([a], [b]) => (!a ? 1 : !b ? -1 : a.localeCompare(b, undefined, { numeric: true })))
                  .map(([catNo, items]) => (
                  <Fragment key={catNo || "sans-catalogue"}>
                    <TableRow className="border-ivory/10 bg-gold/5 hover:bg-gold/5" data-testid={`catalogue-group-${catNo || "aucun"}`}>
                      <TableCell colSpan={7} className="py-2.5">
                        <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                          {catNo ? catalogueLabel(catNo) : "Sans catalogue"}
                        </span>
                        <span className="text-ivory/65 text-xs ml-3">{items.length} produit{items.length > 1 ? "s" : ""} affiché{items.length > 1 ? "s" : ""}</span>
                        {/* Tout activer/désactiver du catalogue dans la boutique
                            (demande du propriétaire, 2026-10-16) — admin seulement */}
                        {user?.role === "admin" && (
                          <span className="ml-4 inline-flex items-center gap-2">
                            <button type="button" disabled={prodBulkBusy === `active-${catNo}`}
                              onClick={() => bulkActive(catNo, true)}
                              data-testid={`catalogue-enable-${catNo || "aucun"}`}
                              className="text-[10px] border border-emerald-500/50 text-emerald-300 px-2.5 py-1 rounded-full hover:bg-emerald-500 hover:text-ink transition-colors disabled:opacity-50">
                              Tout activer
                            </button>
                            <button type="button" disabled={prodBulkBusy === `active-${catNo}`}
                              onClick={() => bulkActive(catNo, false)}
                              data-testid={`catalogue-disable-${catNo || "aucun"}`}
                              className="text-[10px] border border-red-400/50 text-red-300 px-2.5 py-1 rounded-full hover:bg-red-400 hover:text-ink transition-colors disabled:opacity-50">
                              Tout désactiver
                            </button>
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                    {items.map((p) => (
                  <TableRow key={p.id} className="border-ivory/10" data-testid={`admin-product-${p.id}`}>
                    {user?.role === "admin" && (
                      <TableCell className="w-8">
                        <input type="checkbox" data-testid={`product-select-${p.id}`}
                          checked={!!prodSel[p.id]}
                          onChange={(e) => setProdSel((s) => ({ ...s, [p.id]: e.target.checked }))}
                          title="Cocher pour regrouper avec d'autres trophées"
                          className="w-4 h-4 accent-[#d4af37] cursor-pointer" />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <button type="button" data-testid={`product-photo-${p.id}`} title="Ouvrir la photo en pleine page"
                          onClick={() => p.image && setPhotoPreview({ src: p.image, name: p.name })}
                          className="shrink-0 cursor-zoom-in rounded focus:outline-none focus:ring-2 focus:ring-gold/60">
                          <img src={p.image} alt="" className="w-10 h-10 rounded object-contain bg-[#f4f2ec] hover:ring-2 hover:ring-gold/60 transition" />
                        </button>
                        <div>
                          <span className="font-serif text-lg">{p.name}</span>
                          {p.sku && <span className="block text-xs text-ivory/65" data-testid={`product-sku-${p.id}`}>Réf. {p.catalogue_no ? `${p.catalogue_no} — ` : ""}{p.sku}</span>}
                          {p.stock != null && (
                            <span className={`block text-xs ${p.stock === 0 ? "text-red-400" : "text-ivory/65"}`} data-testid={`product-stock-${p.id}`}>
                              Stock : {p.stock}{p.stock === 0 ? " — épuisé" : ""}
                            </span>
                          )}
                          {p.manual_edit && (
                            <span className="block text-[10px] text-champagne/80" data-testid={`product-manual-${p.id}`}
                              title="Modifié manuellement — les changements en masse (paliers, prix des inscriptions) ne touchent jamais ce produit">
                              modifié man. — protégé des changements en masse
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-ivory/60">{p.category}</TableCell>
                    <TableCell className="text-champagne">
                      {fmt(p.base_price)}
                      {p.cost_price != null && user?.role === "admin" && (
                        <span className="block text-xs text-ivory/65" data-testid={`product-cost-${p.id}`}>Coûtant : {fmt(p.cost_price)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-ivory/70">
                      {p.price_tiers?.length
                        ? p.price_tiers.map((tr) => `${tr.min_qty}+: ${fmt(tr.price)}`).join(" · ")
                        : (p.tiers || []).map((t) => `${t.min_qty}+: −${Math.round(t.discount * 100)}%`).join(" · ")}
                    </TableCell>
                    <TableCell>
                      <Switch
                        data-testid={`product-active-${p.id}`}
                        checked={p.active}
                        onCheckedChange={async (v) => {
                          await api.patch(`/admin/products/${p.id}`, { ...p, active: v });
                          setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: v } : x)));
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {/* Bouton IA « langue manquante » (demande du propriétaire,
                            2026-10-20) : visible seulement si une traduction manque */}
                        {langueManquante(p) && (
                          <button
                            data-testid={`translate-product-${p.id}`}
                            onClick={() => traduireFiche(p)}
                            disabled={translatingProd === p.id}
                            title="Traduire la langue manquante avec l'IA (FR ↔ EN) et enregistrer"
                            className="text-gold/80 hover:text-gold transition-colors duration-300 disabled:opacity-50"
                          >
                            {translatingProd === p.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Languages className="w-4 h-4" />}
                          </button>
                        )}
                        <button
                          data-testid={`ad-product-${p.id}`}
                          onClick={() => {
                            setAdPrefill({ product_id: p.id, name: p.name, image: p.image || "",
                                           topic: `${p.name} — ${fmt(p.base_price)}` });
                            setTab("publicites");
                            window.scrollTo(0, 0);
                          }}
                          title="Créer une publicité IA à partir de cette fiche"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300"
                        >
                          <Megaphone className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`lifestyle-product-${p.id}`}
                          onClick={() => generateLifestyle(p)}
                          title="Générer une photo de mise en situation IA (gala) — ajoutée à la galerie de la fiche"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300"
                        >
                          <Sparkles className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`preview-product-${p.id}`}
                          onClick={() => window.open(`/produit/${p.id}`, "_blank", "noopener,noreferrer")}
                          title="Aperçu de la fiche telle que vue par le client"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`edit-product-${p.id}`}
                          onClick={() => openEdit(p)}
                          title="Modifier la fiche"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <DeleteConfirmButton
                          testid={`delete-product-${p.id}`}
                          label={`Le produit « ${p.name} » sera définitivement supprimé de la boutique.`}
                          onDelete={() => deleteProduct(p.id, p.name)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                    ))}
                  </Fragment>
                  ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-ivory/10">
              <p className="text-xs text-ivory/70" data-testid="admin-products-pageinfo">
                {prodTotal} produits · page {prodPage} / {Math.max(1, Math.ceil(prodTotal / 50))}
              </p>
              <div className="flex gap-2">
                <button data-testid="admin-products-prev" disabled={prodPage <= 1}
                  onClick={() => setProdPage((p) => Math.max(1, p - 1))}
                  className="px-4 py-1.5 text-xs border border-ivory/20 rounded-full hover:border-gold/50 disabled:opacity-30 transition-colors duration-300">
                  Précédent
                </button>
                <button data-testid="admin-products-next" disabled={prodPage >= Math.ceil(prodTotal / 50)}
                  onClick={() => setProdPage((p) => p + 1)}
                  className="px-4 py-1.5 text-xs border border-ivory/20 rounded-full hover:border-gold/50 disabled:opacity-30 transition-colors duration-300">
                  Suivant
                </button>
              </div>
            </div>
          </div>

          {/* Codes rabais — déplacés de Paramètres vers « Gestion boutique »
              (demande du propriétaire, 2026-10-16 : toutes les fonctions de la
              boutique dans cet onglet) */}
          {user?.role === "admin" && (
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8" data-testid="promo-admin-card">
            <p className="font-semibold mb-1">Codes rabais</p>
            <p className="text-xs text-ivory/70 mb-5">Ex. WELCOME10 pour 10 % de rabais. Le client entre le code au panier ou à la caisse ; le rabais est calculé par le serveur au moment de la commande. Le crayon modifie un code (nom, dates, conditions) ; la poubelle y met fin.</p>
            <button data-testid="promo-add-toggle" onClick={() => { setPromoForm(EMPTY_PROMO); setPromoCreateOpen(true); }}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 text-sm mb-6">
              <Plus className="w-4 h-4" /> Ajouter un code
            </button>
            {promos.length > 0 && (
              <div className="overflow-x-auto" data-testid="promo-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-ivory/10 hover:bg-transparent">
                      <TableHead className="text-ivory/60">Code</TableHead>
                      <TableHead className="text-ivory/60">Rabais</TableHead>
                      <TableHead className="text-ivory/60">Portée</TableHead>
                      <TableHead className="text-ivory/60">Condition</TableHead>
                      <TableHead className="text-ivory/60">Validité</TableHead>
                      <TableHead className="text-ivory/60">Utilisations</TableHead>
                      <TableHead className="text-ivory/60">Statut</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promos.map((p) => {
                      const st = promoStatus(p);
                      return (
                        <TableRow key={p.id} className="border-ivory/10" data-testid={`promo-row-${p.code}`}>
                          <TableCell className="font-semibold tracking-wider">{p.code}</TableCell>
                          <TableCell className="text-sm">{p.kind === "pct" ? `${p.value} %` : fmt(p.value)}</TableCell>
                          <TableCell className="text-sm text-ivory/60">
                            {p.scope === "inscription" ? "Inscriptions" : p.scope === "products" ? "Produits" : "Commande complète"}
                          </TableCell>
                          <TableCell className="text-xs text-ivory/70" data-testid={`promo-condition-${p.code}`}>
                            {(!p.condition || p.condition === "aucune") ? "—"
                              : p.condition === "inscription" ? "Sur inscription"
                              : p.condition === "distribution" ? "Liste de distribution"
                              : p.condition === "referencement" ? "Référencement"
                              : p.condition_detail || "Autre"}
                          </TableCell>
                          <TableCell className="text-xs text-ivory/70">{p.valid_from} → {p.valid_to}</TableCell>
                          <TableCell className="text-sm text-ivory/60">{p.uses}{p.max_uses != null ? ` / ${p.max_uses}` : ""}</TableCell>
                          <TableCell className={`text-xs ${st.cls}`} data-testid={`promo-status-${p.code}`}>{st.label}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button data-testid={`promo-edit-${p.code}`} onClick={() => openPromoEdit(p)} title="Modifier"
                                className="text-ivory/65 hover:text-gold transition-colors duration-300">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button data-testid={`promo-delete-${p.code}`} onClick={() => deletePromo(p)} title="Supprimer"
                                className="text-ivory/65 hover:text-red-400 transition-colors duration-300">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {tab === "clients" && (() => {
        const subs = customers.filter((c) => c.newsletter && !c.unsubscribed);
        const guestsActive = nlSubs.filter((s) => s.guest && !s.unsubscribed)
          .map((g) => ({ ...g, orders_count: "—", newsletter: true }));
        const desabonnes = [
          ...customers.filter((c) => c.unsubscribed),
          ...nlSubs.filter((s) => s.guest && s.unsubscribed).map((g) => ({ ...g, orders_count: "—" })),
        ];
        const shown = clientsView === "distribution" ? [...subs, ...guestsActive]
          : clientsView === "desabonnes" ? desabonnes
          : customers;
        return (
        <div>
          {/* Compteur des rabais de bienvenue du mois (offerts/utilisés) —
              demande du propriétaire, 2026-10-23 */}
          <CompteurBienvenue />
          {/* Recherche de compte (tous les comptes) + fusion de doublons —
              demande du propriétaire, 2026-10-16 */}
          {clientsView === "tous" && (
            <RechercheFusionClients isAdmin={user?.role === "admin"}
              onOuvrirFiche={(c) => openClientFile({ user_id: c.user_id })}
              onFusionFaite={() => api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {})} />
          )}
          <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="clients-view-toggle">
            <button data-testid="clients-view-all" onClick={() => setClientsView("tous")}
              className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${clientsView === "tous" ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60"}`}>
              Tous les clients ({customers.length})
            </button>
            <button data-testid="clients-view-admins" onClick={() => { setClientsView("admins"); if (admins === null) loadAdmins(); }}
              className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 inline-flex items-center gap-2 ${clientsView === "admins" ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60"}`}>
              <ShieldCheck className="w-3.5 h-3.5" /> Administrateurs
            </button>
            <button data-testid="clients-view-vendeurs" onClick={() => { setClientsView("vendeurs"); if (vendeurs === null) loadVendeurs(); }}
              className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 inline-flex items-center gap-2 ${clientsView === "vendeurs" ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60"}`}>
              <Briefcase className="w-3.5 h-3.5" /> Vendeurs{vendeurs ? ` (${vendeurs.length})` : ""}
            </button>
            <button data-testid="clients-view-distribution" onClick={() => setClientsView("distribution")}
              className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${clientsView === "distribution" ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60"}`}>
              Liste de distribution ({subs.length + guestsActive.length})
            </button>
            {desabonnes.length > 0 && (
              <button data-testid="clients-view-desabonnes" onClick={() => setClientsView("desabonnes")}
                className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${clientsView === "desabonnes" ? "bg-red-500/80 text-white border-red-500 font-semibold" : "border-red-500/40 text-red-400/90 hover:bg-red-500/10"}`}>
                Désabonnés ({desabonnes.length})
              </button>
            )}
            {subs.length > 0 && (
              <button data-testid="newsletter-export" onClick={exportNewsletterCsv}
                className="px-4 py-2 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300 inline-flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Exporter la liste (CSV)
              </button>
            )}
            {subs.length > 0 && (
              <button data-testid="newsletter-compose" onClick={() => setNlOpen(true)}
                className="px-4 py-2 rounded-full text-xs bg-gold text-ink font-semibold hover:bg-champagne transition-colors duration-300 inline-flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Rédiger un envoi
              </button>
            )}
          </div>
        {clientsView === "vendeurs" ? (
          <div className="bg-surface border border-gold/20 rounded-xl overflow-hidden" data-testid="admin-vendeurs-table">
            <p className="text-xs text-ivory/55 px-5 pt-4">
              Comptes vendeurs : ils ne voient QUE les dossiers que vous avez cochés pour eux
              (soumissions « visibles par le vendeur » + tous les dossiers de leurs clients assignés)
              et ne peuvent faire QUE les actions cochées. Pour nommer un vendeur : bouton mallette
              sur sa fiche dans « Tous les clients ». Le bouton ambre retire l'accès vendeur SANS
              supprimer la fiche client — l'accès peut être redonné plus tard.
            </p>
            <Table>
              <TableHeader>
                <TableRow className="border-ivory/10">
                  <TableHead className="text-ivory/60">Vendeur</TableHead>
                  <TableHead className="text-ivory/60">Sections visibles</TableHead>
                  <TableHead className="text-ivory/60">Actions permises</TableHead>
                  <TableHead className="text-ivory/60">Clients assignés</TableHead>
                  <TableHead className="text-ivory/60">Supervision</TableHead>
                  <TableHead className="text-ivory/60">2FA</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(vendeurs || []).map((v) => (
                  <TableRow key={v.id} className="border-ivory/10" data-testid={`vendeur-row-${v.id}`}>
                    <TableCell>
                      <span className="font-serif text-lg">{v.name || "—"}</span>
                      <span className="block text-xs text-ivory/60">{v.email}</span>
                      {v.promoted_vendeur_by && <span className="block text-[11px] text-ivory/45">nommé par {v.promoted_vendeur_by}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-ivory/70" data-testid={`vendeur-sections-${v.id}`}>
                      {Object.keys(v.sections || {}).length
                        ? Object.entries(v.sections).map(([s, m]) =>
                            `${VENDEUR_SECTION_LABELS[s] || s}${m === "edition" ? " ✎" : ""}`).join(", ")
                        : "Aucune"}
                    </TableCell>
                    <TableCell className="text-sm text-ivory/70" data-testid={`vendeur-actions-${v.id}`}>
                      {v.actions.includes("creer_facture") ? "Créer des factures" : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-ivory/70" data-testid={`vendeur-clients-${v.id}`}>
                      {v.clients.length ? `${v.clients.length} client${v.clients.length > 1 ? "s" : ""}` : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-ivory/70" data-testid={`vendeur-stats-${v.id}`}>
                      {v.stats ? (
                        <>
                          <span className="block">{v.stats.factures} facture{v.stats.factures > 1 ? "s" : ""} créée{v.stats.factures > 1 ? "s" : ""}
                            {v.stats.factures ? ` — ${v.stats.total.toFixed(2)} $` : ""}</span>
                          <span className="block text-[11px] text-ivory/45">
                            {v.stats.payees} payée{v.stats.payees > 1 ? "s" : ""} · {v.stats.dossiers_assignes} dossier{v.stats.dossiers_assignes > 1 ? "s" : ""} assigné{v.stats.dossiers_assignes > 1 ? "s" : ""}</span>
                        </>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{cellule2FA(v)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <button data-testid={`perms-vendeur-${v.id}`}
                          onClick={() => setPermsVendeur({ ...v, sections: Array.isArray(v.sections)
                            ? Object.fromEntries(v.sections.map((s) => [s, "lecture"]))
                            : (v.sections || {}) })}
                          title="Régler les cases (pages visibles, lecteur/éditeur, actions, clients assignés)"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300">
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* Retirer l'accès VENDEUR sans supprimer la fiche client
                            (demande du propriétaire 2026-10-06) — réversible */}
                        <RevokeConfirmButton
                          testid={`remove-vendeur-${v.id}`}
                          label={`${v.email} redeviendra un client ordinaire — il n'aura plus accès au portail vendeur.`}
                          onRevoke={() => removeVendeur(v.id, v.email)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {vendeurs !== null && vendeurs.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-ivory/65 py-10" data-testid="admin-no-vendeurs">
                    Aucun vendeur — nommez-en un depuis « Tous les clients » (bouton mallette).
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        ) : clientsView === "admins" ? (
          <div className="bg-surface border border-gold/20 rounded-xl overflow-hidden" data-testid="admin-admins-table">
            <p className="text-xs text-ivory/55 px-5 pt-4">
              Comptes ayant accès au tableau de bord. Vous ne pouvez pas supprimer votre propre compte ni le dernier administrateur.
              Le bouton bouclier ambre (super admin seulement) retire l'accès SANS supprimer la fiche client — l'accès peut être redonné plus tard.
            </p>
            <Table>
              <TableHeader>
                <TableRow className="border-ivory/10">
                  <TableHead className="text-ivory/60">Administrateur</TableHead>
                  <TableHead className="text-ivory/60">Courriel</TableHead>
                  <TableHead className="text-ivory/60">Depuis</TableHead>
                  <TableHead className="text-ivory/60">2FA</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(admins || []).map((a) => (
                  <TableRow key={a.id} className="border-ivory/10" data-testid={`admin-row-${a.id}`}>
                    <TableCell>
                      <span className="font-serif text-lg">{a.name || "—"}</span>
                      {a.is_you && <span className="block text-xs text-gold" data-testid={`admin-you-${a.id}`}>Vous</span>}
                      {a.google && <span className="block text-xs text-ivory/65">Compte Google</span>}
                    </TableCell>
                    <TableCell className="text-sm text-ivory/70">{a.email}</TableCell>
                    <TableCell className="text-sm text-ivory/70">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL, year: "numeric", month: "short", day: "numeric" }) : "—"}
                      {a.promoted_admin_by && <span className="block text-[11px] text-ivory/50">nommé par {a.promoted_admin_by}</span>}
                    </TableCell>
                    <TableCell>{cellule2FA(a)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <button data-testid={`view-admin-${a.id}`} onClick={() => setViewingAdmin(a)}
                          title="Voir le compte"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button data-testid={`pwd-admin-${a.id}`} onClick={() => { setPwdAdmin(a); setPwdValue(""); }}
                          title="Modifier le mot de passe"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300">
                          <KeyRound className="w-4 h-4" />
                        </button>
                        {/* Retirer l'accès ADMIN sans supprimer la fiche (demande du
                            propriétaire 2026-10-06) — réversible, super admin seulement */}
                        {(user?.email || "").toLowerCase() === "info@trophee.ca" && !a.is_you && (
                          <RevokeConfirmButton
                            testid={`remove-access-admin-${a.id}`}
                            icon={ShieldOff}
                            label={`${a.email} perdra l'accès au tableau de bord et redeviendra un client ordinaire.`}
                            onRevoke={() => removeAdminAccess(a.id, a.email)}
                          />
                        )}
                        {!a.is_you && (
                          <DeleteConfirmButton
                            testid={`delete-admin-${a.id}`}
                            label={`Le compte administrateur ${a.email} sera supprimé — il n'aura plus accès au tableau de bord.`}
                            onDelete={() => deleteAdmin(a.id, a.email)}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {admins !== null && admins.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-ivory/65 py-10">Aucun administrateur.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        ) : (
        <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="admin-customers-table">
          <Table>
            <TableHeader>
              <TableRow className="border-ivory/10">
                <TableHead className="text-ivory/60">Client</TableHead>
                <TableHead className="text-ivory/60">Courriel</TableHead>
                <TableHead className="text-ivory/60">Téléphone</TableHead>
                <TableHead className="text-ivory/60">Inscription</TableHead>
                <TableHead className="text-ivory/60">Commandes</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} className="border-ivory/10" data-testid={`admin-customer-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {c.picture ? (
                        <img src={c.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full border border-gold/30 object-cover" />
                      ) : (
                        <span className="w-9 h-9 rounded-full bg-gold/15 text-gold flex items-center justify-center font-serif text-sm">
                          {c.name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                      )}
                      <div>
                        <span className="font-serif text-lg">{c.name}</span>
                        {c.google && <span className="block text-xs text-ivory/65">Compte Google</span>}
                        {c.guest && <span className="block text-xs text-ivory/50">Abonné sans compte</span>}
                        {c.newsletter && !c.unsubscribed && <span className="block text-xs text-gold" data-testid={`customer-newsletter-${c.id}`}>Infolettre</span>}
                        {c.unsubscribed && <span className="block text-xs text-red-400" data-testid={`customer-unsub-${c.id}`}>Désabonné de l'infolettre</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-ivory/70">{c.email}</TableCell>
                  <TableCell className="text-sm text-ivory/70" data-testid={`customer-phone-${c.id}`}>{c.phone || "—"}</TableCell>
                  <TableCell className="text-sm text-ivory/70">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL, year: "numeric", month: "short", day: "numeric" }) : "—"}
                  </TableCell>
                  <TableCell className="text-sm text-champagne" data-testid={`customer-orders-${c.id}`}>{c.orders_count}</TableCell>
                  <TableCell>
                    {!c.guest && (
                    <div className="flex items-center gap-3">
                      {/* Nouvelle fiche PRÉ-REMPLIE depuis cette fiche client
                          (demande du propriétaire, 2026-10-22) : un clic crée la
                          fiche avec les coordonnées du client et l'ouvre dans
                          Ventes & Soumissions */}
                      <button
                        data-testid={`nouvelle-fiche-client-${c.id}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/admin/quote-requests/depuis-client/${c.id}`);
                            sessionStorage.setItem("ouvrir_fiche", data.id);
                            setTab("soumissions");
                            toast.success(`Fiche créée pour ${c.name || c.email} — coordonnées déjà remplies.`);
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        title="Créer une fiche pré-remplie avec les coordonnées de ce client (ouverte dans Ventes & Soumissions)"
                        className="text-ivory/70 hover:text-gold transition-colors duration-300"
                      >
                        <FilePlus2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`make-vendeur-${c.id}`}
                        onClick={() => makeVendeur(c.id, c.email)}
                        title="Nommer vendeur — il ne verra que les dossiers cochés pour lui (portail /vendeur)"
                        className="text-ivory/70 hover:text-gold transition-colors duration-300"
                      >
                        <Briefcase className="w-4 h-4" />
                      </button>
                      {/* Nommer ADMIN : réservé au SUPER ADMIN info@trophee.ca
                          (demande du propriétaire) — les autres admins ne voient
                          même pas le bouton ; le courriel @trophee.ca est exigé */}
                      {(user?.email || "").toLowerCase() === "info@trophee.ca"
                        && (c.email || "").toLowerCase().endsWith("@trophee.ca") && (
                        <button
                          data-testid={`make-admin-${c.id}`}
                          onClick={() => makeAdmin(c.id, c.email)}
                          title="Donner la fonction administrateur (super admin seulement — courriels @trophee.ca)"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300"
                        >
                          <ShieldCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`edit-customer-${c.id}`}
                        onClick={() => setEditingCustomer({ ...c })}
                        className="text-ivory/70 hover:text-gold transition-colors duration-300"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <DeleteConfirmButton
                        testid={`delete-customer-${c.id}`}
                        label={`La fiche de ${c.email} sera supprimée (ses commandes restent dans l'historique).`}
                        onDelete={() => deleteCustomer(c.id, c.email)}
                      />
                    </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-ivory/65 py-10" data-testid="admin-no-customers">
                  {clientsView === "distribution" ? "Aucun abonné à l'info-lettre pour l'instant."
                    : clientsView === "desabonnes" ? "Aucun désabonné — excellent !"
                    : "Aucun client inscrit pour l'instant."}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        )}
        </div>
        );
      })()}

      {/* Cases à cocher d'un vendeur (demande du propriétaire) : sections de
          l'admin visibles, actions permises, clients assignés */}
      <Dialog open={!!permsVendeur} onOpenChange={(v) => { if (!v) setPermsVendeur(null); }}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cases du vendeur</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              {permsVendeur?.name || permsVendeur?.email} — il ne verra et ne pourra faire QUE ce qui est coché ici.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pr-2 -mr-2">
          {permsVendeur && (
            <div className="space-y-5" data-testid="vendeur-perms-dialog">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gold mb-1">Pages de l'admin visibles — et son pouvoir sur chacune</p>
                <p className="text-[11px] text-ivory/50 mb-2">Cochez la page, puis choisissez : <strong>Lecteur seulement</strong> (il regarde) ou <strong>Éditeur</strong> (il peut modifier).</p>
                {VENDEUR_SECTION_GROUPS.map(([groupe, items]) => (
                  <div key={groupe} className="mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-ivory/40 mb-1">{groupe}</p>
                    {items.map((k) => {
                      const mode = permsVendeur.sections[k];
                      return (
                        <div key={k} className="flex items-center gap-2.5 py-1">
                          <label className="flex items-center gap-2 text-sm text-ivory/80 cursor-pointer flex-1 min-w-0">
                            <input type="checkbox" className="w-4 h-4 accent-[#D4AF37] shrink-0"
                              data-testid={`vendeur-perm-section-${k}`}
                              checked={!!mode}
                              onChange={(e) => setPermsVendeur((v) => {
                                const s = { ...v.sections };
                                if (e.target.checked) s[k] = "lecture"; else delete s[k];
                                return { ...v, sections: s };
                              })} />
                            <span className="truncate">{VENDEUR_SECTION_LABELS[k]}</span>
                          </label>
                          {mode && (
                            <span className="inline-flex rounded-full border border-ivory/15 overflow-hidden shrink-0"
                              data-testid={`vendeur-mode-${k}`}>
                              {[["lecture", "Lecteur"], ["edition", "Éditeur"]].map(([m, lab]) => (
                                <button key={m} type="button"
                                  onClick={() => setPermsVendeur((v) => ({ ...v, sections: { ...v.sections, [k]: m } }))}
                                  data-testid={`vendeur-mode-${k}-${m}`}
                                  className={`px-2.5 py-1 text-[10px] transition-colors duration-200 ${mode === m ? "bg-gold text-ink font-bold" : "text-ivory/60 hover:text-gold"}`}>
                                  {lab}
                                </button>
                              ))}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Actions permises</p>
                <label className="flex items-center gap-2.5 text-sm text-ivory/80 py-1 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#D4AF37]"
                    data-testid="vendeur-perm-action-facture"
                    checked={permsVendeur.actions.includes("creer_facture")}
                    onChange={(e) => setPermsVendeur((v) => ({ ...v, actions: e.target.checked ? [...v.actions, "creer_facture"] : v.actions.filter((a) => a !== "creer_facture") }))} />
                  Créer des factures (ventes et soumissions)
                </label>
                <label className="flex items-center gap-2.5 text-sm text-ivory/80 py-1 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#D4AF37]"
                    data-testid="vendeur-perm-action-pages"
                    checked={permsVendeur.actions.includes("modifier_pages")}
                    onChange={(e) => setPermsVendeur((v) => ({ ...v, actions: e.target.checked ? [...v.actions, "modifier_pages"] : v.actions.filter((a) => a !== "modifier_pages") }))} />
                  Modifier les pages (bouton « Modifier la page » sur le site)
                </label>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Clients assignés — tous leurs dossiers lui sont visibles</p>
                <div className="flex gap-2 mb-2">
                  <select id="vendeur-client-pick" data-testid="vendeur-client-pick"
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm flex-1 focus:border-gold focus:outline-none [color-scheme:dark]">
                    <option value="">Choisir un client…</option>
                    {customers.filter((c) => !permsVendeur.clients.includes((c.email || "").toLowerCase())).map((c) => (
                      <option key={c.id} value={(c.email || "").toLowerCase()}>{c.name} — {c.email}</option>
                    ))}
                  </select>
                  <button type="button" data-testid="vendeur-client-add"
                    onClick={() => {
                      const sel = document.getElementById("vendeur-client-pick");
                      const em = sel?.value;
                      if (em) setPermsVendeur((v) => ({ ...v, clients: [...v.clients, em] }));
                    }}
                    className="border border-gold/40 text-gold rounded-lg px-4 text-sm hover:bg-gold/10 transition-colors">
                    Ajouter
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {permsVendeur.clients.map((em) => (
                    <span key={em} className="text-xs border border-ivory/20 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5" data-testid={`vendeur-client-chip-${em}`}>
                      {em}
                      <button type="button" onClick={() => setPermsVendeur((v) => ({ ...v, clients: v.clients.filter((c) => c !== em) }))}
                        className="text-ivory/50 hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {permsVendeur.clients.length === 0 && (
                    <span className="text-xs text-ivory/45">Aucun — il ne verra que les soumissions cochées pour lui.</span>
                  )}
                </div>
              </div>
              <button onClick={saveVendeurPerms} data-testid="vendeur-perms-save"
                className="w-full bg-gold text-ink font-semibold rounded-full px-5 py-3 text-sm hover:bg-champagne transition-colors duration-300">
                Enregistrer les cases
              </button>
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fiche « Voir le compte » d'un administrateur */}
      <Dialog open={!!viewingAdmin} onOpenChange={(v) => { if (!v) setViewingAdmin(null); }}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Compte administrateur</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">Fiche de {viewingAdmin?.email}</DialogDescription>
          </DialogHeader>
          {viewingAdmin && (
            <div className="space-y-2.5 text-sm" data-testid="admin-view-dialog">
              <p><span className="text-ivory/55">Nom :</span> {viewingAdmin.name || "—"}</p>
              <p><span className="text-ivory/55">Courriel :</span> {viewingAdmin.email}</p>
              <p><span className="text-ivory/55">Téléphone :</span> {viewingAdmin.phone || "—"}</p>
              <p><span className="text-ivory/55">Type de connexion :</span> {viewingAdmin.google ? "Compte Google" : "Courriel + mot de passe"}</p>
              <p><span className="text-ivory/55">Compte créé le :</span> {viewingAdmin.created_at ? new Date(viewingAdmin.created_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL, year: "numeric", month: "long", day: "numeric" }) : "—"}</p>
              {viewingAdmin.promoted_admin_by && (
                <p><span className="text-ivory/55">Nommé admin par :</span> {viewingAdmin.promoted_admin_by} {viewingAdmin.promoted_admin_at ? `le ${new Date(viewingAdmin.promoted_admin_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL })}` : ""}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Changement de mot de passe d'un administrateur */}
      <Dialog open={!!pwdAdmin} onOpenChange={(v) => { if (!v) { setPwdAdmin(null); setPwdValue(""); } }}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Modifier le mot de passe</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              Nouveau mot de passe pour {pwdAdmin?.email}. Toutes ses sessions ouvertes seront fermées{pwdAdmin?.is_you ? " — vous serez vous-même déconnecté" : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input type="text" autoComplete="off" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)}
              data-testid="admin-pwd-input" placeholder="Nouveau mot de passe (6 caractères min.)"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
            <button onClick={saveAdminPassword} data-testid="admin-pwd-save"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
              <KeyRound className="w-4 h-4" /> Enregistrer le mot de passe
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Popup « Signature de livraison » des commandes boutique (main propre /
          ramassage, demande du propriétaire 2026-10-08) */}
      {signOrder && (
        <SignatureLivraison
          titre={`Signature — ${signOrder.order_number}`}
          sousTitre={signOrder.delivery_method === "ramassage"
            ? "Ramassage à l'atelier : faites signer le client, puis la facture finale part automatiquement (copie à info@trophee.ca)."
            : "Livraison en main propre : faites signer le client, puis la facture finale part automatiquement (copie à info@trophee.ca)."}
          busy={signOrderBusy}
          sansCourrielDefaut={!!orderSilent[signOrder.order_number]}
          onSigner={confirmerSignatureCommande}
          onSansSignature={sansSignatureCommande}
          onFermer={() => setSignOrder(null)} />
      )}

      {/* Aperçu à l'écran des courriels clients (exemples fictifs) */}
      <Dialog open={!!emailPreview} onOpenChange={(v) => { if (!v) setEmailPreview(null); }}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-3xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Aperçu des courriels clients</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm" data-testid="email-preview-subject">
              {emailPreview?.subject} — exemple fictif, rien n'a été envoyé
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="email-preview-kinds">
            {[["confirmation", "Confirmation"], ["expediee", "Expédiée"], ["livree", "Livrée"], ["parrainage", "Parrainage"]].map(([k, lab]) => (
              <button key={k} data-testid={`email-kind-${k}`} onClick={() => openEmailPreview(k)}
                className={`px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${emailPreview?.kind === k ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                {lab}
              </button>
            ))}
          </div>
          {emailPreview && (
            <iframe key={emailPreview.kind} title="Aperçu du courriel" data-testid="email-preview-frame"
              srcDoc={emailPreview.html}
              className="w-full flex-1 rounded-lg border border-ivory/15 bg-white" />
          )}
        </DialogContent>
      </Dialog>

      {tab === "blogue" && (
        <div>
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <button
              data-testid="admin-add-post"
              onClick={() => setEditingPost({ ...EMPTY_POST })}
              className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Plus className="w-4 h-4" /> Nouvel article
            </button>
            <button
              data-testid="translate-all-btn"
              disabled={translating} onClick={translateAll}
              className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50"
            >
              {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
              {translating ? "Traduction en cours…" : "Tout traduire (FR + EN)"}
            </button>
            <div className="flex-1 min-w-72 flex gap-2 border border-gold/25 rounded-lg p-2 pl-4 bg-gold/5 items-center">
              <Sparkles className="w-4 h-4 text-gold shrink-0" />
              <input
                data-testid="ai-quick-topic"
                value={aiQuickTopic} onChange={(e) => setAiQuickTopic(e.target.value)}
                placeholder="Sujet — Claude rédige et publie directement (FR + EN)"
                className="flex-1 min-w-0 bg-transparent text-sm text-ivory placeholder:text-ivory/60 focus:outline-none"
              />
              <button
                data-testid="ai-quick-publish" disabled={aiQuickLoading} onClick={aiQuickPublish}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-4 py-2 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50 whitespace-nowrap shrink-0"
              >
                {aiQuickLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Rédiger et publier
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            <div className="bg-surface border border-gold/25 rounded-xl p-5" data-testid="auto-blog-card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-gold" /> Publication automatique</p>
                  <p className="text-xs text-ivory/70 mt-1.5">Chaque jour : Claude rédige et publie 1 nouvel article (FR + EN) et les nouveaux articles Opinly sont importés automatiquement.</p>
                  {shopSettings?.last_auto_blog_date && (
                    <p className="text-[11px] text-ivory/65 mt-1.5">Dernière publication auto : {shopSettings.last_auto_blog_date}</p>
                  )}
                </div>
                <Switch data-testid="auto-blog-switch" checked={!!shopSettings?.auto_blog_enabled} onCheckedChange={toggleAutoBlog} />
              </div>
            </div>
            <div className="bg-surface border border-gold/25 rounded-xl p-5" data-testid="opinly-card">
              <p className="font-semibold inline-flex items-center gap-2"><Globe className="w-4 h-4 text-gold" /> Opinly (SEO)</p>
              <p className="text-xs text-ivory/70 mt-1.5 mb-3">Importez les articles générés par Opinly directement dans le blogue.</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="opinly-sync-btn" disabled={opinlySyncing} onClick={syncOpinly}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-4 py-2 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                  {opinlySyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Synchroniser
                </button>
                <button data-testid="opinly-list-btn" disabled={opinlyLoading} onClick={() => (opinlyPosts === null ? loadOpinlyPosts() : setOpinlyPosts(null))}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                  {opinlyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  {opinlyPosts === null ? "Voir les articles Opinly" : "Masquer"}
                </button>
              </div>
              {opinlyPosts !== null && (
                <div className="mt-4 space-y-2 max-h-56 overflow-y-auto" data-testid="opinly-posts-list">
                  {opinlyPosts.length === 0 && <p className="text-xs text-ivory/65">Aucun article disponible dans Opinly pour le moment.</p>}
                  {opinlyPosts.map((op) => (
                    <div key={op.slug} className="flex items-center justify-between gap-3 border border-ivory/10 rounded-lg px-3 py-2.5" data-testid={`opinly-post-${op.slug}`}>
                      <div className="min-w-0">
                        <ExpandableText text={op.title} testId={`opinly-title-${op.slug}`} className="block text-sm" />
                        <ExpandableText text={op.description} testId={`opinly-desc-${op.slug}`} className="block text-[11px] text-ivory/65" />
                      </div>
                      {op.imported ? (
                        <span className="text-[11px] text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 rounded-full whitespace-nowrap">Importé</span>
                      ) : (
                        <button data-testid={`opinly-import-${op.slug}`} disabled={opinlyImporting === op.slug} onClick={() => importOpinly(op.slug)}
                          className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 whitespace-nowrap">
                          {opinlyImporting === op.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Importer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-surface border border-gold/25 rounded-xl p-5" data-testid="soro-card">
              <p className="font-semibold inline-flex items-center gap-2"><Globe className="w-4 h-4 text-gold" /> Soro (trysoro)</p>
              <p className="text-xs text-ivory/70 mt-1.5 mb-3">Publiez les articles Soro directement dans le blogue (image incluse).</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="soro-sync-btn" disabled={soroSyncing} onClick={syncSoro}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-4 py-2 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                  {soroSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Synchroniser
                </button>
                <button data-testid="soro-list-btn" disabled={soroLoading} onClick={() => (soroPosts === null ? loadSoroPosts() : setSoroPosts(null))}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                  {soroLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  {soroPosts === null ? "Voir les articles Soro" : "Masquer"}
                </button>
              </div>
              {soroPosts !== null && (
                <div className="mt-4 space-y-2 max-h-56 overflow-y-auto" data-testid="soro-posts-list">
                  {soroPosts.length === 0 && <p className="text-xs text-ivory/65">Aucun article disponible dans Soro pour le moment.</p>}
                  {soroPosts.map((sp) => (
                    <div key={sp.id} className="flex items-center justify-between gap-3 border border-ivory/10 rounded-lg px-3 py-2.5" data-testid={`soro-post-${sp.id}`}>
                      <div className="min-w-0">
                        <ExpandableText text={sp.title} testId={`soro-title-${sp.id}`} className="block text-sm" />
                        <ExpandableText text={sp.excerpt} testId={`soro-excerpt-${sp.id}`} className="block text-[11px] text-ivory/65" />
                      </div>
                      {sp.imported ? (
                        <span className="text-[11px] text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 rounded-full whitespace-nowrap">Publié</span>
                      ) : (
                        <button data-testid={`soro-import-${sp.id}`} disabled={soroImporting === sp.id} onClick={() => importSoro(sp.id)}
                          className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 whitespace-nowrap">
                          {soroImporting === sp.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Publier
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="admin-posts-table">
            <Table>
              <TableHeader>
                <TableRow className="border-ivory/10">
                  <TableHead className="text-ivory/60">Article</TableHead>
                  <TableHead className="text-ivory/60">Date</TableHead>
                  <TableHead className="text-ivory/60">Publié</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((p) => (
                  <TableRow key={p.id} className="border-ivory/10" data-testid={`admin-post-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-contain bg-[#f4f2ec]" />}
                        <div>
                          <span className="font-serif text-lg">{p.title}</span>
                          <span className="block text-xs text-ivory/65">/blogue/{p.slug}</span>
                          <span className="flex flex-wrap gap-1.5 mt-1">
                            {p.pinned && <span data-testid={`pinned-badge-${p.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gold border border-gold/50 bg-gold/15 px-2 py-0.5 rounded-full"><Pin className="w-2.5 h-2.5" />Épinglé</span>}
                            {p.source === "opinly" && <span className="text-[10px] uppercase tracking-wider text-sky-300 border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 rounded-full">Opinly</span>}
                            {p.source === "soro" && <span className="text-[10px] uppercase tracking-wider text-violet-300 border border-violet-400/30 bg-violet-400/10 px-2 py-0.5 rounded-full">Soro</span>}
                            {p.source === "auto" && <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/30 bg-gold/10 px-2 py-0.5 rounded-full">Auto</span>}
                            {p.content && !p.content_en && <span data-testid={`lang-fr-only-${p.id}`} className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 rounded-full">FR seul</span>}
                            {!p.content && p.content_en && <span data-testid={`lang-en-only-${p.id}`} className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 rounded-full">EN seul</span>}
                            {p.video_status === "processing" && <span data-testid={`video-processing-${p.id}`} className="text-[10px] uppercase tracking-wider text-amber-300 border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 rounded-full">Vidéo en génération…</span>}
                            {p.video_status === "failed" && <span data-testid={`video-failed-${p.id}`} className="text-[10px] uppercase tracking-wider text-red-300 border border-red-400/30 bg-red-400/10 px-2 py-0.5 rounded-full">Vidéo échouée</span>}
                            {p.video && <span data-testid={`video-ready-${p.id}`} className="text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 rounded-full">Vidéo</span>}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-ivory/60">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL }) : "—"}
                    </TableCell>
                    <TableCell>
                      <Switch data-testid={`toggle-post-${p.id}`} checked={p.published}
                        onCheckedChange={(v) => togglePostPublished(p, v)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-3">
                        <button data-testid={`pin-post-${p.id}`} onClick={() => togglePostPinned(p)}
                          title={p.pinned ? "Désépingler — l'article retourne à sa place normale" : "Épingler — afficher cet article en haut de la page Blogue"}
                          className={`transition-colors duration-300 ${p.pinned ? "text-gold" : "text-ivory/70 hover:text-gold"}`}>
                          {p.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                        </button>
                        <button data-testid={`cover-post-${p.id}`} onClick={() => generateCover(p)} title={p.image ? "Régénérer l'image de couverture avec l'IA" : "Générer une image de couverture avec l'IA"}
                          className="text-ivory/70 hover:text-gold transition-colors duration-300">
                          <ImagePlus className="w-4 h-4" />
                        </button>
                        <button data-testid={`sora-post-${p.id}`} onClick={() => openSoraDialog(p)} title="Générer une vidéo Sora (OpenAI) pour cet article"
                          className="text-ivory/70 hover:text-gold transition-colors duration-300 disabled:opacity-40" disabled={p.video_status === "processing"}>
                          <Film className="w-4 h-4" />
                        </button>
                        <button data-testid={`edit-post-${p.id}`} onClick={() => setEditingPost({ ...p })}
                          className="text-ivory/70 hover:text-gold transition-colors duration-300">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-post-${p.id}`} onClick={() => deletePost(p.id)}
                          className="text-ivory/70 hover:text-red-400 transition-colors duration-300">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {posts.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-ivory/65 py-10">Aucun article — créez le premier !</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {tab === "pages" && <ContentEditor />}

      {tab === "publicites" && <AdsManager prefill={adPrefill} onPrefillConsumed={() => setAdPrefill(null)} />}

      {tab === "occasions" && <AdminOccasions />}

      {/* Paniers des clients — l'admin peut ajuster le prix unitaire d'un article ;
          le prix ajusté s'affiche au client (« Prix ajusté pour vous ») et est
          respecté à la caisse (demande client 2026-09-18) */}
      {tab === "paniers" && (
        <div className="max-w-4xl" data-testid="admin-carts-tab">
          <SubHead testid="carts-head">Paniers actifs des clients</SubHead>
          {carts.length === 0 && <p className="text-ivory/60 text-sm" data-testid="carts-empty">Aucun panier actif pour le moment.</p>}
          <div className="space-y-5">
            {carts.map((c) => (
              <div key={c.user_id} className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid={`cart-row-${c.user_id}`}>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-ivory">{c.first_name || "Client"} <span className="text-gold">{c.email}</span></p>
                    <p className="text-[11px] text-ivory/45">Mis à jour : {fmtDateTimeMtl(c.updated_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => openClientFile(c)} disabled={clientFileBusy === c.user_id}
                      data-testid={`cart-client-file-${c.user_id}`}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                      {clientFileBusy === c.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                      Fiche client
                    </button>
                    <p className="text-xs text-ivory/60">{plur(c.items.length, "article")}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {c.items.map((it, i) => {
                    const k = `${c.user_id}:${i}`;
                    const edited = cartPriceEdit[k];
                    return (
                      <div key={i} className="flex items-center gap-3 bg-ink border border-ivory/10 rounded-lg p-3 flex-wrap">
                        <img src={it.image} alt="" className="w-10 h-10 object-contain bg-white rounded shrink-0" />
                        <div className="flex-1 min-w-40">
                          <p className="text-sm text-ivory truncate">{it.name}</p>
                          <p className="text-[11px] text-ivory/50">{it.size_label} · Qté {it.qty}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-ivory/60 text-[11px]">Prix calculé : {fmt(unitPrice(it.pricing, it.size_id, it.qty, it.inscription_type, it.finish_id))}</p>
                          {typeof it.custom_price === "number" && (
                            <p className="text-gold font-bold" data-testid={`cart-custom-${c.user_id}-${i}`}>Ajusté : {fmt(it.custom_price)}</p>
                          )}
                        </div>
                        {/* Boutons de fonctions traditionnels (demande du propriétaire,
                            2026-11-03) : MODIFIER la quantité, SUPPRIMER (poubelle),
                            en plus d'AJUSTER le prix */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <button type="button" title="Réduire la quantité" data-testid={`cart-qty-minus-${c.user_id}-${i}`}
                              disabled={it.qty <= 1 || cartQtyBusy === k}
                              onClick={async () => {
                                setCartQtyBusy(k);
                                try {
                                  await api.patch(`/admin/carts/${c.user_id}/items/${i}/edit`, { qty: it.qty - 1 });
                                  const r = await api.get("/admin/carts"); setCarts(r.data.carts || []);
                                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                                setCartQtyBusy("");
                              }}
                              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-ivory/25 text-ivory/80 hover:border-gold hover:text-gold disabled:opacity-30 transition-colors">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-xs text-ivory/80 w-7 text-center" data-testid={`cart-qty-${c.user_id}-${i}`}>{it.qty}</span>
                            <button type="button" title="Augmenter la quantité" data-testid={`cart-qty-plus-${c.user_id}-${i}`}
                              disabled={cartQtyBusy === k}
                              onClick={async () => {
                                setCartQtyBusy(k);
                                try {
                                  await api.patch(`/admin/carts/${c.user_id}/items/${i}/edit`, { qty: it.qty + 1 });
                                  const r = await api.get("/admin/carts"); setCarts(r.data.carts || []);
                                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                                setCartQtyBusy("");
                              }}
                              className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-ivory/25 text-ivory/80 hover:border-gold hover:text-gold disabled:opacity-30 transition-colors">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <input type="number" min="0" step="0.01" placeholder="Prix $" data-testid={`cart-price-input-${c.user_id}-${i}`}
                            value={edited ?? ""}
                            onChange={(e) => setCartPriceEdit((s) => ({ ...s, [k]: e.target.value }))}
                            className="w-24 bg-surface border border-ivory/20 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                          <button type="button" data-testid={`cart-price-save-${c.user_id}-${i}`}
                            disabled={edited === undefined || edited === ""}
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/carts/${c.user_id}/items/${i}`, { custom_price: parseFloat(edited) });
                                const r = await api.get("/admin/carts");
                                setCarts(r.data.carts || []);
                                setCartPriceEdit((s) => ({ ...s, [k]: undefined }));
                                toast.success("Prix ajusté — le client est avisé par courriel");
                              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                            }}
                            className="bg-gold text-ink text-xs font-bold px-4 py-2 rounded-full disabled:opacity-40 hover:bg-champagne transition-colors duration-200">
                            Ajuster
                          </button>
                          {typeof it.custom_price === "number" && (
                            <button type="button" data-testid={`cart-price-clear-${c.user_id}-${i}`}
                              onClick={async () => {
                                try {
                                  await api.patch(`/admin/carts/${c.user_id}/items/${i}`, { custom_price: null });
                                  const r = await api.get("/admin/carts");
                                  setCarts(r.data.carts || []);
                                  toast.success("Prix rétabli au prix calculé");
                                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                              }}
                              className="border border-ivory/25 text-ivory/70 text-xs px-3 py-2 rounded-full hover:border-gold hover:text-gold transition-colors duration-200">
                              Rétablir
                            </button>
                          )}
                          {/* POUBELLE : supprimer l'article du panier (avec confirmation) */}
                          <button type="button" title="Supprimer l'article" data-testid={`cart-item-delete-${c.user_id}-${i}`}
                            disabled={cartQtyBusy === k}
                            onClick={async () => {
                              if (!window.confirm(`Supprimer « ${it.name} » du panier de ${c.email} ?`)) return;
                              setCartQtyBusy(k);
                              try {
                                await api.delete(`/admin/carts/${c.user_id}/items/${i}`);
                                const r = await api.get("/admin/carts"); setCarts(r.data.carts || []);
                                toast.success("Article supprimé du panier");
                              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                              setCartQtyBusy("");
                            }}
                            className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-red-400/40 text-red-400 hover:bg-red-500 hover:text-ink disabled:opacity-30 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Fiche client (demande du propriétaire) : coordonnées, adresse,
              historique d'achat, soumissions et adresses IP observées */}
        </div>
      )}

          {clientFile && (
            <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="client-file-modal">
              <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setClientFile(null)} aria-label="Fermer" />
              <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl shadow-black/60">
                <button onClick={() => setClientFile(null)} data-testid="client-file-close"
                  className="absolute top-3 right-3 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
                  <X className="w-5 h-5" />
                </button>
                <h3 className="font-serif text-2xl text-ivory mb-1" data-testid="client-file-name">
                  {clientFile.profile.name || "Client"} <span className="text-gold text-lg">{clientFile.email}</span>
                </h3>
                {/* Bouton MODIFICATION dans la fiche (demande du propriétaire,
                    2026-10-28 : « où est mon bouton modification ? ») — ouvre le
                    dialogue de modification du compte, pré-rempli */}
                <button type="button" data-testid="client-file-edit"
                  onClick={() => {
                    setEditingCustomer({
                      id: clientFile.user_id, name: clientFile.profile.name || "",
                      email: clientFile.email, phone: clientFile.contact.phone || "",
                      address1: clientFile.contact.address1 || "", city: clientFile.contact.city || "",
                      province: clientFile.contact.province || "QC",
                      postal_code: clientFile.contact.postal_code || "",
                      newsletter: !!clientFile.profile.newsletter,
                    });
                    setClientFile(null);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 mb-3">
                  <Pencil className="w-3.5 h-3.5" /> Modifier la fiche
                </button>
                <p className="text-xs text-ivory/55 mb-5">
                  Membre depuis le {clientFile.profile.member_since ? fmtDateMtl(clientFile.profile.member_since) : "—"}
                  {clientFile.profile.newsletter ? " · abonné à l'info-lettre" : ""}
                  {clientFile.profile.referral_code ? ` · code parrain ${clientFile.profile.referral_code}` : ""}
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <div className="bg-ink border border-ivory/10 rounded-lg p-4">
                    <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Coordonnées</p>
                    <p className="text-sm text-ivory" data-testid="client-file-phone">Tél : {clientFile.contact.phone || "—"}</p>
                    {clientFile.contact.company && <p className="text-sm text-ivory/80">{clientFile.contact.company}</p>}
                    <p className="text-sm text-ivory/80 mt-1" data-testid="client-file-address">{clientFile.contact.address || "Adresse non fournie"}</p>
                  </div>
                  <div className="bg-ink border border-ivory/10 rounded-lg p-4">
                    <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Résumé</p>
                    <p className="text-sm text-ivory" data-testid="client-file-stats">
                      {clientFile.stats.paid_orders} commande(s) payée(s) · <strong className="text-gold">{clientFile.stats.total_spent.toFixed(2)} $</strong> dépensés
                    </p>
                    <p className="text-sm text-ivory/80 mt-1">{plur(clientFile.stats.quotes, "soumission")} · {plur(clientFile.stats.cart_items, "article")} au panier</p>
                    <p className="text-[11px] text-ivory/50 mt-2" data-testid="client-file-ips">
                      IP observée(s) : {clientFile.ips.length ? clientFile.ips.join(" · ") : "aucune"}
                    </p>
                  </div>
                </div>
                {/* RECHERCHES PASSÉES du client (demande du propriétaire,
                    2026-10-27 : « lecture des cookies des clients ») — termes
                    cherchés dans la boutique, y compris AVANT sa connexion
                    (rattachés par le témoin navigateur), les plus récents d'abord */}
                <div className="bg-ink border border-ivory/10 rounded-lg p-4 mb-5" data-testid="client-file-recherches">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Recherches passées sur le site</p>
                  {(clientFile.recherches || []).length === 0 ? (
                    <p className="text-xs text-ivory/50">Aucune recherche enregistrée pour l'instant.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {clientFile.recherches.map((rec, i) => (
                        <span key={i} title={rec.ts ? `dernière fois : ${fmtDateMtl(rec.ts)}` : ""}
                          data-testid={`client-file-recherche-${i}`}
                          className="text-[11px] border border-sky-400/30 text-sky-300 rounded-full px-2.5 py-0.5">
                          {rec.terme}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {/* PIÈCES JOINTES reçues du client (demande du propriétaire,
                    2026-10-28 : « ajouter les P.J. à la fiche des clients dans
                    la section pièce jointe et une miniature ») — soumissions et
                    réponses du portail, miniature cliquable (image) ou icône
                    (PDF), la plus récente d'abord */}
                <div className="bg-ink border border-ivory/10 rounded-lg p-4 mb-5" data-testid="client-file-pieces-jointes">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">
                    Pièces jointes reçues + épreuves envoyées ({(clientFile.pieces_jointes || []).length})
                  </p>
                  {(clientFile.pieces_jointes || []).length === 0 ? (
                    <p className="text-xs text-ivory/50">Aucune pièce jointe reçue pour l'instant.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {clientFile.pieces_jointes.map((pj, i) => {
                        const href = pj.url.startsWith("http") ? pj.url : `${process.env.REACT_APP_BACKEND_URL}${pj.url}`;
                        const estImage = /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(pj.url) || /\.(heic|heif)(\?|$)/i.test(pj.url);
                        const etiquette = `${pj.name || "pièce jointe"}${pj.quote_name ? ` — ${pj.quote_name}` : ""}${pj.at ? ` (${fmtDateMtl(pj.at)})` : ""}`;
                        // LIEN GOOGLE DRIVE (épreuve, demande du propriétaire
                        // 2026-07-02) : vignette spéciale — ouvre le lien dans un
                        // nouvel onglet ; pas de bouton « Joindre » (ce n'est pas
                        // un fichier stocké, on ne peut pas l'attacher tel quel)
                        if (pj.drive) {
                          return (
                            <div key={i} className="w-16">
                              <a href={href} target="_blank" rel="noreferrer" title={`${etiquette} — ouvrir dans Google Drive`}
                                data-testid={`client-file-pj-${i}`}
                                className="block w-16 rounded-lg border border-emerald-500/40 overflow-hidden hover:border-emerald-300 transition-colors duration-300">
                                <span className="w-16 h-16 flex flex-col items-center justify-center gap-1 bg-emerald-500/10 text-emerald-300">
                                  <Link2 className="w-5 h-5" />
                                  <span className="text-[9px] uppercase">Drive</span>
                                </span>
                                <span className="block text-[9px] text-ivory/50 truncate px-1 py-0.5 bg-surface/80">{pj.name || "Épreuve Drive"}</span>
                              </a>
                            </div>
                          );
                        }
                        // « Joindre au prochain courriel » (demande du
                        // propriétaire, 2026-10-28) : la P.J. entre dans la file
                        // du client et sera PRÉ-JOINTE au prochain popup courriel
                        const chemin = pj.url.includes("/api/files/") ? pj.url.split("/api/files/")[1] : pj.url;
                        const enFile = pjAttente.includes(chemin);
                        const joindreBtn = (
                          <button type="button" disabled={enFile}
                            data-testid={`client-file-pj-joindre-${i}`}
                            onClick={async () => {
                              try {
                                await api.post(`/admin/client-file/${clientFile.user_id}/pj-attente`, { url: pj.url, name: pj.name });
                                setPjAttente((ps) => [...ps, chemin]);
                                toast.success(`« ${pj.name || "Pièce jointe"} » sera jointe au prochain courriel de ${clientFile.email}.`);
                              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                            }}
                            title={enFile ? "Déjà en file pour le prochain courriel" : "Joindre en 1 clic au prochain courriel du client"}
                            className={`mt-1 w-16 inline-flex items-center justify-center gap-1 text-[9px] rounded-full border px-1 py-0.5 transition-colors ${
                              enFile ? "border-emerald-400/50 text-emerald-300 cursor-default"
                                     : "border-gold/40 text-gold hover:bg-gold hover:text-ink"}`}>
                            <Mail className="w-2.5 h-2.5" /> {enFile ? "En file" : "Joindre"}
                          </button>
                        );
                        const vignette = estImage ? (
                          <img src={href} alt={pj.name || `Pièce jointe ${i + 1}`} loading="lazy"
                            className="w-16 h-16 object-contain bg-surface" />
                        ) : (
                          <span className="w-16 h-16 flex flex-col items-center justify-center gap-1 bg-surface text-ivory/60">
                            <Paperclip className="w-5 h-5" />
                            <span className="text-[9px] uppercase">{/\.pdf(\?|$)/i.test(pj.url) ? "PDF" : "fichier"}</span>
                          </span>
                        );
                        // Images : agrandies EN POPUP dans la page (demande du
                        // propriétaire, 2026-10-28) ; PDF/fichiers : nouvel onglet
                        return (
                          <div key={i} className="w-16">
                            {estImage ? (
                              <button type="button" title={`${etiquette} — cliquer pour agrandir`}
                                onClick={() => setZoomPj({ url: href, nom: etiquette })}
                                data-testid={`client-file-pj-${i}`}
                                className="block w-16 rounded-lg border border-ivory/15 overflow-hidden hover:border-gold transition-colors duration-300 cursor-zoom-in">
                                {vignette}
                                <span className="block text-[9px] text-ivory/50 truncate px-1 py-0.5 bg-surface/80">{pj.name || `P.J. ${i + 1}`}</span>
                              </button>
                            ) : (
                              <a href={href} target="_blank" rel="noreferrer" title={etiquette}
                                data-testid={`client-file-pj-${i}`}
                                className="block w-16 rounded-lg border border-ivory/15 overflow-hidden hover:border-gold transition-colors duration-300">
                                {vignette}
                                <span className="block text-[9px] text-ivory/50 truncate px-1 py-0.5 bg-surface/80">{pj.name || `P.J. ${i + 1}`}</span>
                              </a>
                            )}
                            {joindreBtn}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* FILE « PROCHAIN COURRIEL » VISIBLE (demande du propriétaire,
                    2026-10-28 : « afficher la liste des pièces en file avec un
                    bouton pour en retirer une avant l'envoi ») — chaque pièce
                    partira jointe au prochain courriel du client ; « Retirer »
                    la sort de la file sans la supprimer des P.J. reçues */}
                {pjAttenteListe.length > 0 && (
                  <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-lg p-4 mb-5" data-testid="client-file-pj-file">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
                      En file pour le prochain courriel ({pjAttenteListe.length})
                    </p>
                    <ul className="space-y-1.5">
                      {pjAttenteListe.map((f, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 text-xs bg-ink border border-emerald-400/20 rounded-lg px-2.5 py-1.5"
                          data-testid={`client-file-pj-file-${i}`}>
                          <span className="truncate text-ivory/80">
                            <Paperclip className="w-3 h-3 inline mr-1.5 text-emerald-300" />
                            {f.nom}
                            <span className="text-ivory/40"> · {fmtDateMtl(f.at)}</span>
                          </span>
                          <button type="button"
                            data-testid={`client-file-pj-file-retirer-${i}`}
                            onClick={async () => {
                              try {
                                await api.post("/admin/pj-attente/consommer", { email: clientFile.email, paths: [f.path] });
                                setPjAttente((ps) => ps.filter((p) => p !== f.path));
                                setPjAttenteListe((ls) => ls.filter((x) => x.path !== f.path));
                                toast.success(`« ${f.nom} » retirée de la file — elle reste dans les pièces reçues.`);
                              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                            }}
                            className="inline-flex items-center gap-1 text-[10px] rounded-full border border-red-400/40 text-red-300 px-2 py-0.5 hover:bg-red-500 hover:text-white transition-colors shrink-0">
                            <X className="w-3 h-3" /> Retirer
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-ivory/40 mt-2">Ces pièces seront pré-jointes au prochain courriel de ce client (soumission, question, adresse, étape, expédition ou facture).</p>
                  </div>
                )}
                {/* Courriels SUPPLÉMENTAIRES du dossier (demande du propriétaire,
                    2026-10-22) : plusieurs adresses séparées par des virgules dans
                    la MÊME case — tous les courriels au client (soumissions,
                    rappels, factures, épreuves…) leur sont aussi adressés en CC */}
                <div className="bg-ink border border-ivory/10 rounded-lg p-4 mb-5" data-testid="client-file-courriels-card">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Courriels supplémentaires du dossier</p>
                  <p className="text-[11px] text-ivory/50 mb-2">
                    Tous les courriels envoyés au client (soumissions, rappels, factures…) sont aussi adressés à ces adresses.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="text" value={(clientFile.profile.extra_emails || []).join(", ")}
                      onChange={(e) => setClientFile({ ...clientFile, profile: { ...clientFile.profile, extra_emails: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })}
                      placeholder="ex. : comptabilite@client.ca, gerant@client.ca"
                      data-testid="client-file-courriels-input"
                      className="flex-1 min-w-[240px] bg-surface border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs text-ivory focus:border-gold focus:outline-none" />
                    <button type="button" data-testid="client-file-courriels-save"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/admin/client-file/${clientFile.user_id}/courriels`,
                            { emails: (clientFile.profile.extra_emails || []).join(", ") });
                          setClientFile((cf) => ({ ...cf, profile: { ...cf.profile, extra_emails: data.extra_emails } }));
                          toast.success(data.extra_emails.length
                            ? `${data.extra_emails.length} courriel(s) supplémentaire(s) enregistré(s) — ils recevront une copie des courriels du client.`
                            : "Courriels supplémentaires retirés.");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="px-3 py-1.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors">
                      Enregistrer les courriels
                    </button>
                  </div>
                </div>
                {/* Étapes de commande par texto (demande du propriétaire, 2026-10-30) :
                    case + code à 6 chiffres → les 4 liens (soumission, approbation,
                    paiement, matrice) partent par texto au numéro VÉRIFIÉ */}
                <div className="bg-ink border border-emerald-500/25 rounded-lg p-4 mb-5" data-testid="client-file-sms-etapes">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Étapes de la commande par texto</p>
                  <SmsEtapesBox phone={clientFile.contact?.phone || ""}
                    userId={clientFile.user_id}
                    verified={(clientFile.profile?.sms_etapes || {}).verified} />
                </div>
                {/* Préférence TEXTO du client (demande du propriétaire, 2026-10-30 :
                    « demande d'avis par SMS aux clients qui préfèrent les textos aux
                    courriels ») — case au dossier + bouton d'envoi immédiat ; le
                    numéro utilisé est le téléphone de la fiche */}
                <div className="bg-ink border border-ivory/10 rounded-lg p-4 mb-5" data-testid="client-file-sms-card">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Avis Google par texto</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-ivory/75 cursor-pointer select-none">
                      <input type="checkbox" checked={!!clientFile.profile.pref_sms}
                        data-testid="client-file-pref-sms"
                        onChange={async (e) => {
                          const v = e.target.checked;
                          setClientFile((cf) => ({ ...cf, profile: { ...cf.profile, pref_sms: v } }));
                          try {
                            await api.post(`/admin/client-file/${clientFile.user_id}/pref-sms`, { pref_sms: v });
                            toast.success(v ? "Ce client recevra les demandes d'avis par texto." : "Ce client recevra les demandes d'avis par courriel.");
                          } catch (err) {
                            setClientFile((cf) => ({ ...cf, profile: { ...cf.profile, pref_sms: !v } }));
                            toast.error(formatApiError(err.response?.data?.detail));
                          }
                        }}
                        className="w-4 h-4 accent-emerald-500" />
                      Préfère les textos aux courriels (avis Google par SMS)
                    </label>
                    <button type="button" data-testid="client-file-avis-texto"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/admin/client-file/${clientFile.user_id}/avis-texto`);
                          toast.success(`Demande d'avis envoyée par texto au ${data.dest}.`);
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="px-3 py-1.5 rounded-full text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-ink transition-colors">
                      Demander l'avis par texto maintenant
                    </button>
                  </div>
                  <p className="text-[10px] text-ivory/40 mt-2">
                    Numéro utilisé : le téléphone de la fiche ({clientFile.contact?.phone || "aucun — ajoutez-le via « Modifier »"}).
                  </p>
                </div>
                {/* Terme de paiement CONVENU au dossier client (demande du
                    propriétaire, 2026-10-21 : « à mon choix ou celui convenu dans
                    le dossier client ») — s'applique automatiquement aux
                    soumissions du client (PDF + courriel) sauf choix différent
                    sur la fiche de la soumission */}
                <div className="bg-ink border border-ivory/10 rounded-lg p-4 mb-5">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Terme de paiement convenu au dossier</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={clientFile.profile.payment_terms || ""}
                      onChange={(e) => setClientFile({ ...clientFile, profile: { ...clientFile.profile, payment_terms: e.target.value } })}
                      data-testid="client-file-terme-paiement"
                      className="bg-surface border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs text-ivory focus:border-gold focus:outline-none">
                      <option value="">Aucun terme convenu — « relâchée au paiement reçu » par défaut</option>
                      <option value="reception">Relâchée une fois le paiement reçu</option>
                      <option value="15j">15 jours net</option>
                      <option value="30j">30 jours net</option>
                    </select>
                    <button type="button" data-testid="client-file-terme-save"
                      onClick={async () => {
                        try {
                          await api.post(`/admin/client-file/${clientFile.user_id}/terme-paiement`,
                            { terms: clientFile.profile.payment_terms || "" });
                          toast.success("Terme convenu enregistré au dossier — il s'appliquera aux soumissions (PDF et courriel).");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="px-3 py-1.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors">
                      Enregistrer le terme
                    </button>
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Historique d'achat</p>
                {clientFile.orders.length === 0 && <p className="text-sm text-ivory/50 mb-4">Aucune commande pour l'instant.</p>}
                <div className="space-y-2 mb-5" data-testid="client-file-orders">
                  {clientFile.orders.map((o) => (
                    <div key={o.order_number} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-ink border border-ivory/10 rounded-lg px-4 py-2.5 text-sm">
                      <span className="font-semibold text-ivory">{o.order_number}</span>
                      <span className="text-ivory/50 text-xs">{fmtDateMtl(o.created_at)}</span>
                      <span className="text-gold font-semibold">{Number(o.total || 0).toFixed(2)} $</span>
                      <span className="text-xs text-ivory/60">{plur(o.items, "article")} · {o.status}</span>
                      {o.invoice_no && <span className="text-[10px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">Facture {o.invoice_no}</span>}
                      {o.tracking && <span className="text-[10px] text-sky-300">Suivi {o.tracking}</span>}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Soumissions</p>
                {clientFile.quotes.length === 0 && <p className="text-sm text-ivory/50">Aucune soumission.</p>}
                <div className="space-y-2" data-testid="client-file-quotes">
                  {clientFile.quotes.map((q) => (
                    <div key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-ink border border-ivory/10 rounded-lg px-4 py-2.5 text-sm">
                      <span className="text-ivory">{q.name}</span>
                      <span className="text-ivory/50 text-xs">{fmtDateMtl(q.created_at)}</span>
                      <span className="text-xs text-ivory/60">{plur(q.items, "article")}</span>
                      {q.amount > 0 && <span className="text-gold">{Number(q.amount).toFixed(2)} $</span>}
                      {q.paye && <span className="text-[10px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5">Payée</span>}
                      {q.invoice_no && <span className="text-[10px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">Facture {q.invoice_no}</span>}
                    </div>
                  ))}
                </div>
                {/* RÈGLE PERMANENTE (propriétaire, 2026-10-20) : TOUTES les
                    communications (rendez-vous, rappels, annulations, présence)
                    restent dans l'historique du client */}
                {(clientFile.communications?.length > 0 || clientFile.rendezvous?.length > 0) && (
                  <div className="mt-5">
                    <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2">Communications et rendez-vous</p>
                    <div className="space-y-2 max-h-72 overflow-y-auto overscroll-contain pr-1" data-lenis-prevent data-testid="client-file-communications">
                      {(clientFile.communications || []).map((c) => (
                        <div key={c.id} className="bg-ink border border-ivory/10 rounded-lg px-4 py-2.5 text-sm" data-testid={`client-comm-${c.id}`}>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                              c.type === "rdv_annulation" ? "border-red-400/40 text-red-300"
                              : c.type === "rdv_confirmation" ? "border-emerald-400/40 text-emerald-300"
                              : "border-gold/40 text-gold"}`}>{c.titre}</span>
                            <span className="text-ivory/45 text-xs">{fmtDateMtl(c.at)}</span>
                          </div>
                          <p className="text-xs text-ivory/60 mt-1">{c.resume}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

      {/* AGRANDISSEMENT EN POPUP d'une pièce jointe de la fiche client
          (demande du propriétaire, 2026-10-28) : grande image dans une fenêtre
          interne, sans quitter la page — téléchargement possible */}
      {zoomPj && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="client-pj-zoom">
          <button className="absolute inset-0 bg-ink/90 backdrop-blur-sm" onClick={() => setZoomPj(null)} aria-label="Fermer" />
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setZoomPj(null)} data-testid="client-pj-zoom-close"
              className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-champagne transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <img src={zoomPj.url} alt={zoomPj.nom} className="w-full max-h-[78vh] object-contain rounded-xl border border-gold/30 bg-ink" />
            <div className="flex items-center justify-between gap-3 mt-3">
              <p className="text-xs text-ivory/60 truncate">{zoomPj.nom}</p>
              <a href={zoomPj.url} target="_blank" rel="noreferrer" data-testid="client-pj-zoom-download"
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors shrink-0">
                <Download className="w-3.5 h-3.5" /> Ouvrir en grand
              </a>
            </div>
          </div>
        </div>
      )}

      {tab === "motscles" && shopSettings && (
        <div className="max-w-4xl" data-testid="admin-keywords-tab">
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 space-y-4" data-testid="seo-keywords-card">
            <div>
              <p className="font-semibold">Mots-clés SEO (référencement)</p>
              <p className="text-xs text-ivory/70 mt-1">
                Chaque mot-clé est <strong className="text-ivory/90">actif sur toutes les pages du site</strong> :
                inscrit automatiquement dans la balise meta keywords et les données structurées JSON-LD —
                lus par Google, Bing et les robots des IA. Aucune limite de nombre. Séparez-les par des virgules,
                retirez-en un avec le × (en ligne immédiatement). Laissez vide pour la liste par défaut du site.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Mots-clés (français)</label>
                <textarea data-testid="seo-keywords-fr" rows={4}
                  value={shopSettings.seo_keywords_fr || ""}
                  onChange={(e) => setShopSettings((s) => ({ ...s, seo_keywords_fr: e.target.value }))}
                  placeholder="Ex. : trophées personnalisés, gravure laser, médailles Québec…"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <KeywordChips value={shopSettings.seo_keywords_fr || ""} testId="seo-keywords-fr-list"
                  onRemove={(kw) => removeKeyword("seo_keywords_fr", kw)} />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Mots-clés (anglais)</label>
                <textarea data-testid="seo-keywords-en" rows={4}
                  value={shopSettings.seo_keywords_en || ""}
                  onChange={(e) => setShopSettings((s) => ({ ...s, seo_keywords_en: e.target.value }))}
                  placeholder="Ex.: custom trophies, laser engraving, medals Quebec…"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <KeywordChips value={shopSettings.seo_keywords_en || ""} testId="seo-keywords-en-list"
                  onRemove={(kw) => removeKeyword("seo_keywords_en", kw)} />
              </div>
            </div>
            <button type="button" data-testid="seo-keywords-save" disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const { data } = await api.patch("/admin/settings", {
                    seo_keywords_fr: shopSettings.seo_keywords_fr || "",
                    seo_keywords_en: shopSettings.seo_keywords_en || "",
                  });
                  setShopSettings((s) => ({ ...s, ...data, express_rate: data.express_rate * 100 }));
                  toast.success("Mots-clés SEO enregistrés — en ligne immédiatement");
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                finally { setSaving(false); }
              }}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer les mots-clés
            </button>
          </div>

          {/* Historique des tournées du dimanche + lancement sur demande
              (demande du propriétaire, 2026-10-16) */}
          <MotsClesTourneeCard isAdmin={user?.role === "admin"}
            onChanged={() => api.get("/admin/settings").then(({ data }) =>
              setShopSettings((s) => ({ ...s, ...data, express_rate: (data.express_rate ?? 0) * 100 }))).catch(() => {})} />

          {/* Variations hebdomadaires du classement Google (demande du
              propriétaire, 2026-10-16) — données Search Console */}
          <PositionsGoogleCard />

          {/* Sous-section : liste complète réellement diffusée (base intégrée + ajouts admin) */}
          {(() => {
            const mergedFr = mergeKeywords(shopSettings.seo_keywords_fr, KEYWORDS_FR).split(", ").filter(Boolean);
            const mergedEn = mergeKeywords(shopSettings.seo_keywords_en, KEYWORDS_EN).split(", ").filter(Boolean);
            const customFr = new Set((shopSettings.seo_keywords_fr || "").split(",").map((k) => k.trim().toLowerCase()).filter(Boolean));
            const customEn = new Set((shopSettings.seo_keywords_en || "").split(",").map((k) => k.trim().toLowerCase()).filter(Boolean));
            const KwGroup = ({ title, list, custom, tid }) => (
              <div>
                <p className="text-xs text-ivory/70 mb-2">{title} — <strong className="text-gold">{list.length} mots-clés actifs</strong></p>
                <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-1" data-testid={tid}>
                  {list.map((k, i) => (
                    <span key={`${k}-${i}`} data-testid={`${tid}-${i}`}
                      className={`text-xs rounded-full px-2.5 py-1 border ${custom.has(k.toLowerCase())
                        ? "border-gold/60 text-gold bg-gold/10"
                        : "border-ivory/15 text-ivory/70 bg-ink"}`}>
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            );
            return (
              <div className="bg-surface border border-gold/20 rounded-xl p-7 space-y-5 mt-4" data-testid="seo-keywords-all">
                <div>
                  <p className="font-semibold">Liste complète diffusée aux moteurs et aux IA</p>
                  <p className="text-xs text-ivory/70 mt-1">
                    Tous les mots-clés réellement inscrits sur chaque page du site (balise meta + données structurées JSON-LD) :
                    la liste de base intégrée au site <em>plus</em> vos ajouts. <span className="text-gold">Doré = ajouté par vous</span> · ivoire = liste de base.
                  </p>
                </div>
                <KwGroup title="Français" list={mergedFr} custom={customFr} tid="seo-all-fr" />
                <KwGroup title="Anglais" list={mergedEn} custom={customEn} tid="seo-all-en" />
              </div>
            );
          })()}
          <SeoPositions />
          <IndexationCard />
        </div>
      )}

      {tab === "zones" && <><CatalogueUpload /><CatalogueZonesEditor /></>}
      {tab === "realisations" && <RealisationsTab />}

      {tab === "avis" && <AdminReviews />}
      {/* Statistiques du chatbot Claude (demande du propriétaire, 2026-10-06) */}
      {tab === "assistant" && <><ChatbotStats /><MessengerBotStats /><FaqEnrichi /></>}

      {tab === "campagnes" && <CampaignsTab />}

      {tab === "prospects" && <ProspectsScraper />}
      {tab === "rebonds" && <RebondsTab />}
      {tab === "backlinks" && <BacklinksTab />}

      {tab === "parametres" && isVendeur && (
        <div className="max-w-4xl" data-testid="admin-settings">
          {/* Vendeur : Paramètres = sa carte 2FA + les listes de prix coûtant
              partagées avec lui (case « Voir les prix coûtants » cochée par
              l'admin sur chaque liste — demande du propriétaire, 2026-10-20) */}
          <SubHead testid="settings-group-securite">Sécurité</SubHead>
          <TwoFactorCard />
          <VendeurPrixCoutantCard />
        </div>
      )}
      {tab === "parametres" && !isVendeur && shopSettings && (
        <div className="max-w-4xl" data-testid="admin-settings">
          {/* Listes de prix coûtant par catalogue — ADMIN SEULEMENT, tout en haut
              de Paramètres (demande du propriétaire, 2026-10-20) */}
          {user?.role === "admin" && <PrixCoutantCard />}
          {/* Rapport des codes gardés avec préfixe (collisions) + retrait rejouable
              en production après déploiement (demande du propriétaire, 2026-10-20) */}
          <CodesCollisionsCard isAdmin={user?.role === "admin"} />
          <SubHead testid="settings-group-securite">Sécurité</SubHead>
          <TwoFactorCard />
          {/* Choix 2FA par compte du personnel — admin seulement
              (demande du propriétaire, 2026-10-20) */}
          {user?.role === "admin" && <Staff2FACard />}
          <SubHead testid="settings-group-boutique">Boutique et livraison</SubHead>
          <form onSubmit={saveSettings} className="bg-surface border border-ivory/10 rounded-xl p-7 space-y-6">
            <div className="flex items-center justify-between border border-gold/20 rounded-lg p-4">
              <div>
                <p className="font-semibold">Boutique en ligne</p>
                <p className="text-xs text-ivory/70 mt-1">Fermée = les clients ne peuvent plus commander (la caisse affiche un avis).</p>
              </div>
              <div className="flex items-center gap-3">
                <span data-testid="shop-status-label" className={`text-xs uppercase tracking-widest ${shopSettings.shop_open ? "text-gold" : "text-red-400"}`}>
                  {shopSettings.shop_open ? "Ouverte" : "Fermée"}
                </span>
                <Switch
                  data-testid="shop-open-switch"
                  checked={shopSettings.shop_open}
                  onCheckedChange={(v) => setShopSettings((s) => ({ ...s, shop_open: v }))}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Livraison standard ($)</label>
                <input data-testid="settings-shipping-standard" type="number" step="0.01" min="0" required
                  value={shopSettings.shipping_standard}
                  onChange={(e) => setShopSettings((s) => ({ ...s, shipping_standard: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Livraison minimum ($) — plancher payé par le client</label>
                <input data-testid="settings-shipping-min" type="number" step="0.01" min="0" required
                  value={shopSettings.shipping_min ?? 17.95}
                  onChange={(e) => setShopSettings((s) => ({ ...s, shipping_min: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <p className="text-[11px] text-ivory/50 mt-1">Le client paie au moins ce montant pour la livraison postale (17,95 $ demandé).</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Livraison gratuite à partir de ($ de sous-total)</label>
                <input data-testid="settings-shipping-free" type="number" step="0.01" min="0" required
                  value={shopSettings.shipping_free_over}
                  onChange={(e) => setShopSettings((s) => ({ ...s, shipping_free_over: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Production express (% du sous-total)</label>
                <input data-testid="settings-express-rate" type="number" step="0.5" min="0" max="100" required
                  value={shopSettings.express_rate}
                  onChange={(e) => setShopSettings((s) => ({ ...s, express_rate: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Livraison en main propre ($ — gratuite au-dessus du seuil de livraison gratuite)</label>
                <input data-testid="settings-hand-fee" type="number" step="0.01" min="0" required
                  value={shopSettings.hand_delivery_fee ?? 15}
                  onChange={(e) => setShopSettings((s) => ({ ...s, hand_delivery_fee: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Rayon de la livraison en main propre (km autour de Léry)</label>
                <input data-testid="settings-hand-radius" type="number" step="1" min="1" max="200" required
                  value={shopSettings.hand_delivery_radius_km ?? 25}
                  onChange={(e) => setShopSettings((s) => ({ ...s, hand_delivery_radius_km: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm text-ivory/70 mb-1.5">Sports de saison — bandeaux des pages villes (mots-clés séparés par des virgules)</label>
                <input data-testid="settings-picks-keywords" type="text"
                  value={shopSettings.picks_keywords ?? ""}
                  placeholder="hockey, volleyball, basketball, scolaire…"
                  onChange={(e) => setShopSettings((s) => ({ ...s, picks_keywords: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <p className="text-[11px] text-ivory/45 mt-1.5">Les 8 choix (4 médailles + 4 trophées) des pages villes privilégient ces sports ; le reste est aléatoire à chaque visite. Vide = automatique selon le mois de l'année (hockey en hiver, soccer/baseball en été, football à la rentrée).</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Sport de saison — présélectionné sur la page Forfaits</label>
                <select data-testid="settings-season-sport"
                  value={shopSettings.season_sport ?? ""}
                  onChange={(e) => setShopSettings((s) => ({ ...s, season_sport: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none">
                  <option value="">Aucun</option>
                  {["Hockey", "Soccer", "Football", "Basketball", "Volleyball", "Natation", "Baseball", "Golf", "Danse", "Autre"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <p className="text-[11px] text-ivory/45 mt-1.5">Les 3 cartes de forfaits arrivent avec ce sport déjà choisi (miniatures du sport affichées) pour attirer l'œil dès l'arrivée. « Aucun » = automatique selon le mois de l'année.</p>
              </div>
              {/* Calendrier des saisons : 1 sport par défaut par mois, JUSQU'À 5
                  (demande client) — utilisé quand le forçage ci-dessus est « Aucun » */}
              <div className="sm:col-span-2">
                <label className="block text-sm text-ivory/70 mb-1.5">Calendrier des saisons — sport(s) de chaque mois (1 par défaut, jusqu'à 5)</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="settings-season-calendar">
                  {MONTHS_FR.map((name, i) => {
                    const m = String(i + 1);
                    const cal = shopSettings.season_calendar || {};
                    const raw = cal[m];
                    const list = (Array.isArray(raw) ? raw : [raw ?? DEFAULT_SEASON_CALENDAR[m]]).slice(0, 5);
                    const setList = (next) => setShopSettings((s) => ({
                      ...s, season_calendar: { ...(s.season_calendar || {}), [m]: next },
                    }));
                    return (
                      <div key={m} className="bg-ink border border-ivory/15 rounded-lg px-3 py-2">
                        <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-1">{name}</p>
                        {list.map((sp, k) => (
                          <div key={k} className="flex items-center gap-1 mb-1">
                            <select data-testid={`settings-season-month-${m}-${k}`} value={sp}
                              onChange={(e) => setList(list.map((x, j) => (j === k ? e.target.value : x)))}
                              className="w-full bg-transparent text-sm text-gold focus:outline-none cursor-pointer">
                              {SEASON_SPORTS.map((s) => <option key={s} value={s} className="bg-ink">{s}</option>)}
                            </select>
                            {list.length > 1 && (
                              <button type="button" data-testid={`settings-season-month-${m}-del-${k}`}
                                onClick={() => setList(list.filter((_, j) => j !== k))}
                                className="text-ivory/40 hover:text-red-400 text-sm px-0.5 shrink-0">×</button>
                            )}
                          </div>
                        ))}
                        {list.length < 5 && (
                          <button type="button" data-testid={`settings-season-month-${m}-add`}
                            onClick={() => setList([...list, SEASON_SPORTS.find((x) => !list.includes(x)) || SEASON_SPORTS[0]])}
                            className="text-[10px] uppercase tracking-widest text-gold/60 hover:text-gold mt-0.5 transition-colors duration-200">
                            + Ajouter
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-ivory/45 mt-1.5">Le 1er sport du mois est présélectionné sur la page Forfaits ; tous (jusqu'à 5) guident les 8 miniatures des pages villes. Pré-rempli avec le calendrier automatique — modifiez seulement les mois voulus.</p>
              </div>
            </div>
            <button data-testid="settings-save" type="submit" disabled={saving}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
            </button>
          </form>

          {/* Logo officiel — fichiers gérés par l'admin (ajout / effacement,
              demande du propriétaire 2026-10-20) */}
          <SubHead testid="settings-group-logo">Logo</SubHead>
          <LogoFichiersCard isAdmin={user?.role === "admin"} />


          {/* Visibilité des catalogues — activer/désactiver d'un clic */}
          <SubHead testid="settings-group-catalogues">Catalogues</SubHead>
          <div className="bg-surface border border-gold/25 rounded-xl p-7 mt-8 space-y-4" data-testid="catalogue-visibility-card">
            <div>
              <p className="font-semibold text-gold">Activer / désactiver un catalogue de notre boutique</p>
              <p className="text-xs text-ivory/70 mt-1">Désactiver un catalogue le fait disparaître de notre boutique côté client (boutique, onglets, recherche, fiches produits) sans rien supprimer.</p>
            </div>
            {/* Tout activer / Tout masquer (demande du propriétaire, 2026-10-20) */}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => toggleTousCatalogues(true)} data-testid="catalogues-tout-activer"
                className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors duration-300">
                Tout activer
              </button>
              <button type="button" onClick={() => toggleTousCatalogues(false)} data-testid="catalogues-tout-masquer"
                className="inline-flex items-center gap-1.5 border border-ivory/25 text-ivory/70 text-xs px-4 py-2 rounded-full hover:border-red-400 hover:text-red-300 transition-colors duration-300">
                Tout masquer
              </button>
            </div>
            {/* Un bouton par FAMILLE (demande du propriétaire, 2026-10-20) : active ou
                masque toute la famille d'un clic — l'étiquette montre visibles/total */}
            <div className="flex flex-wrap gap-2" data-testid="catalogues-familles">
              {[...new Set(tousNumerosCatalogues.map(catalogueFamily))].sort((a, b) => Number(a) - Number(b)).map((fam) => {
                const disabled = shopSettings.disabled_catalogues || [];
                const deFamille = tousNumerosCatalogues.filter((no) => catalogueFamily(no) === fam);
                const visibles = deFamille.filter((no) => !disabled.includes(no)).length;
                const toutVisible = visibles === deFamille.length;
                return (
                  <button key={fam} type="button" onClick={() => toggleFamilleCatalogues(fam)}
                    data-testid={`catalogues-famille-${fam}`}
                    title={toutVisible ? "Masquer toute la famille" : "Activer toute la famille"}
                    className={`inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full border transition-colors duration-300 ${
                      toutVisible ? "border-gold/50 text-gold hover:border-red-400 hover:text-red-300"
                        : visibles === 0 ? "border-ivory/15 text-ivory/50 hover:border-gold hover:text-gold"
                        : "border-amber-400/50 text-amber-300 hover:border-gold hover:text-gold"}`}>
                    {FAMILY_NAMES[fam] || fam} · {visibles}/{deFamille.length}
                  </button>
                );
              })}
            </div>
            {/* Les catalogues connus s'affichent même sans produits en base (ex. avant l'import)
                minmax(0,1fr) : la piste mobile peut rétrécir, sinon les rangées débordent */}
            <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 gap-2.5">
            {Object.keys({ ...Object.fromEntries(Object.keys(CATALOGUE_NAMES).map((k) => [k, 0])), ...catCounts })
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((no) => {
              const count = catCounts[no] || 0;
              const visible = !(shopSettings.disabled_catalogues || []).includes(no);
              return (
                <div key={no} className="flex items-center justify-between gap-4 border border-ivory/10 rounded-lg px-4 py-3 min-w-0"
                  data-testid={`catalogue-visibility-${no}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{catalogueLabel(no)}</p>
                    <p className="text-xs text-ivory/65">{count} produits</p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className={`text-xs ${visible ? "text-green-400" : "text-ivory/65"}`}>{visible ? "Visible" : "Masqué"}</span>
                    <Switch checked={visible} onCheckedChange={() => toggleCatalogue(no)} data-testid={`catalogue-toggle-${no}`} />
                  </div>
                </div>
              );
            })}
            </div>
          </div>

          {/* Interrupteurs des LIENS de la page publique « Les Catalogues »
              (mrtrophee.ca/catalogues) — demande du propriétaire : chaque carte de
              cette page peut être masquée ici, sans toucher au code ni aux produits */}
          <div className="bg-surface border border-gold/25 rounded-xl p-7 mt-8 space-y-4" data-testid="catalogue-links-card">
            <div>
              <p className="font-semibold text-gold">Catalogues — liens affichés sur la page « Les Catalogues »</p>
              <p className="text-xs text-ivory/70 mt-1">Activer / désactiver chaque catalogue affiché sur la page publique mrtrophee.ca/catalogues. Masquer un lien ne supprime rien et ne touche pas aux produits de la Boutique — réversible d'un clic.</p>
            </div>
            {/* Tout activer / Tout masquer les liens (demande du propriétaire, 2026-10-20) */}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => toggleTousLiensCatalogues(true)} data-testid="catalogue-links-tout-activer"
                className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-champagne transition-colors duration-300">
                Tout activer
              </button>
              <button type="button" onClick={() => toggleTousLiensCatalogues(false)} data-testid="catalogue-links-tout-masquer"
                className="inline-flex items-center gap-1.5 border border-ivory/25 text-ivory/70 text-xs px-4 py-2 rounded-full hover:border-red-400 hover:text-red-300 transition-colors duration-300">
                Tout masquer
              </button>
            </div>
            <div className="space-y-5">
              {translations.fr.catalogues.groups.map((g, gi) => (
                <div key={g.title}>
                  <p className="text-xs uppercase tracking-widest text-ivory/65 mb-2">{g.title}</p>
                  {/* minmax(0,1fr) : empêche les longues étiquettes d'élargir la carte sur mobile */}
                  <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 gap-2.5">
                    {g.links.map((l, k) => {
                      const visible = !(shopSettings.catalogue_disabled_links || []).includes(l.url);
                      return (
                        <div key={l.url} className="flex items-center justify-between gap-4 border border-ivory/10 rounded-lg px-4 py-3 min-w-0"
                          data-testid={`catalogue-link-visibility-${gi}-${k}`}>
                          <p className="text-sm font-medium truncate min-w-0 flex-1">{l.label}</p>
                          <div className="flex items-center gap-2.5 shrink-0">
                            <span className={`text-xs ${visible ? "text-green-400" : "text-ivory/65"}`}>{visible ? "Visible" : "Masqué"}</span>
                            <Switch checked={visible} onCheckedChange={() => toggleCatalogueLink(l.url)} data-testid={`catalogue-link-toggle-${gi}-${k}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mots-clés SEO : inscrits dans les métas et les données structurées de chaque page
              (lus par Google, Bing et les robots des IA) */}
          <SubHead testid="settings-group-seo">Référencement (SEO)</SubHead>
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="gsc-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold">Google Search Console</p>
                <p className="text-xs text-ivory/70 mt-1">
                  Vos statistiques Google (clics, positions, recherches) s'ajoutent au rapport de
                  visibilité mensuel. L'accès COMPLET permet en plus de soumettre le sitemap à Google
                  (indexation accélérée) — reconnectez pour l'obtenir si l'accès est « lecture seule ».
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* « Ping » aux moteurs de recherche (demande du propriétaire,
                    2026-10-16) : Google (Search Console) + Bing + IndexNow */}
                <button type="button" data-testid="seo-ping-btn"
                  disabled={seoPinging}
                  onClick={async () => {
                    setSeoPinging(true);
                    try {
                      const { data } = await api.post("/admin/seo/ping-moteurs", {});
                      const lignes = Object.entries(data.rapport || {})
                        .map(([k, v]) => `${k} : ${v}`).join("\n");
                      toast.success("Ping envoyé aux moteurs de recherche", { duration: 12000, description: lignes });
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    } finally {
                      setSeoPinging(false);
                    }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors inline-flex items-center gap-2 disabled:opacity-50">
                  {seoPinging && <Loader2 className="w-4 h-4 animate-spin" />}
                  Rappeler notre existence aux moteurs
                </button>
                {/* Test de communication moteurs / IA / bots (demande du
                    propriétaire, 2026-10-28) : même vérification que le passage
                    automatique de CHAQUE SOIR (courriel d'alerte seulement si
                    un point lâche) — ici, le détail s'affiche tout de suite */}
                <button type="button" data-testid="seo-verif-bots-btn"
                  disabled={seoPinging}
                  onClick={async () => {
                    setSeoPinging(true);
                    try {
                      const { data } = await api.post("/admin/seo/verif-communication", {});
                      const lignes = Object.entries(data.details || {})
                        .map(([k, v]) => `${k} : ${v}`).join("\n");
                      if (data.ok) {
                        toast.success("Tout est OK — moteurs, IA et bots voient bien le site", { duration: 15000, description: lignes });
                      } else {
                        toast.error(`${(data.problemes || []).length} problème(s) détecté(s) — alerte envoyée à info@trophee.ca`, { duration: 20000, description: lignes });
                      }
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    } finally {
                      setSeoPinging(false);
                    }
                  }}
                  className="px-4 py-2 rounded-full text-sm border border-gold/50 text-gold hover:bg-gold hover:text-ink transition-colors inline-flex items-center gap-2 disabled:opacity-50">
                  {seoPinging && <Loader2 className="w-4 h-4 animate-spin" />}
                  Tester moteurs / IA / bots
                </button>
                <span data-testid="gsc-status-label" className={`text-xs uppercase tracking-widest ${gscStatus?.connected ? (gscStatus?.scope_full ? "text-emerald-300" : "text-amber-300") : "text-amber-300/80"}`}>
                  {gscStatus?.connected
                    ? (gscStatus?.scope_full === false ? "Connecté — lecture seule"
                      : gscStatus?.scope_full ? "Connecté — accès complet" : "Connecté")
                    : "Non connecté"}
                </span>
                {gscStatus?.connected && (
                  <button type="button" data-testid="gsc-reconnect-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/admin/gsc/connect");
                        window.location.href = data.url;
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                    Reconnecter
                  </button>
                )}
                {gscStatus?.connected ? (
                  <button type="button" data-testid="gsc-disconnect-btn"
                    onClick={async () => {
                      try {
                        await api.delete("/admin/gsc/disconnect");
                        setGscStatus({ connected: false });
                        toast.success("Google Search Console déconnecté");
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="px-4 py-2 rounded-full text-sm border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">
                    Déconnecter
                  </button>
                ) : (
                  <button type="button" data-testid="gsc-connect-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/admin/gsc/connect");
                        window.location.href = data.url;
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                    Connecter Google
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Google Agenda (demande du propriétaire) : chaque demande d'appel avec
              une date souhaitée crée un événement « Rappel » dans SON agenda */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="gcal-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold">Google Agenda</p>
                <p className="text-xs text-ivory/70 mt-1">
                  Chaque demande d'appel avec une date souhaitée crée un événement « Rappel »
                  (nom, téléphone, plage horaire) dans votre Google Agenda.
                  {!gcalStatus?.connected && " Une connexion Google est requise — cliquez « Connecter Google Agenda » et cochez la permission demandée."}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col gap-0.5">
                  <span data-testid="gcal-status-label" className={`text-xs uppercase tracking-widest ${gcalStatus?.connected ? "text-emerald-300" : "text-amber-300/80"}`}>
                    {gcalStatus?.connected
                      ? `Connecté${gcalStatus?.email ? ` — ${gcalStatus.email}` : ""}`
                      : "Non connecté"}
                  </span>
                  {/* Permission de LECTURE (plages occupées) : sans elle, un créneau
                      déjà pris dans l'agenda perso ne bloque pas les réservations —
                      l'avertissement reste visible tant qu'elle manque */}
                  {gcalStatus?.connected && gcalStatus?.lecture_ok === false && (
                    <span data-testid="gcal-lecture-warning" className="text-[11px] text-amber-300/90 max-w-xs">
                      Sans la permission de lecture — reconnectez en cochant TOUTES les cases demandées par Google.
                    </span>
                  )}
                  {gcalStatus?.connected && gcalStatus?.lecture_ok && (
                    <span data-testid="gcal-lecture-ok" className="text-[11px] text-emerald-300/80">
                      Lecture des plages occupées active
                    </span>
                  )}
                </div>
                {gcalStatus?.connected ? (
                  <>
                    {gcalStatus?.lecture_ok === false && (
                      <button type="button" data-testid="gcal-reconnect-btn"
                        onClick={async () => {
                          try {
                            const { data } = await api.get("/admin/gcal/connect");
                            window.location.href = data.url;
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                        Reconnecter (activer la lecture)
                      </button>
                    )}
                    <button type="button" data-testid="gcal-disconnect-btn"
                      onClick={async () => {
                        try {
                          await api.delete("/admin/gcal/disconnect");
                          setGcalStatus({ connected: false });
                          toast.success("Google Agenda déconnecté — plus aucun événement ne sera créé");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="px-4 py-2 rounded-full text-sm border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">
                      Déconnecter
                    </button>
                  </>
                ) : (
                  <button type="button" data-testid="gcal-connect-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/admin/gcal/connect");
                        window.location.href = data.url;
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                    Connecter Google Agenda
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Rendez-vous clients (demande du propriétaire, 2026-10-20) : horaire
              d'ouverture modifiable + liste des réservations à venir. Les réservations
              s'inscrivent dans le Google Agenda connecté dans la carte ci-dessus. */}
          <RendezVousAdminCard shopSettings={shopSettings} setShopSettings={setShopSettings} />
          {/* Heures de rappel proposées (demande du propriétaire) : bornent les
              listes « De » / « À » du formulaire « Demander un appel » */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="callback-hours-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold">Heures de rappel proposées</p>
                <p className="text-xs text-ivory/70 mt-1">
                  Les visiteurs choisissent leur moment de rappel dans ces bornes — listes « De » / « À »
                  du formulaire « Demander un appel ».
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="callback-hours-start" value={cbStart} onChange={(e) => setCbStart(e.target.value)}
                  className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                  {buildCallbackHours("06:00", "22:30").map((h) => <option key={h.v} value={h.v}>{h.fr}</option>)}
                </select>
                <span className="text-xs text-ivory/60">à</span>
                <select data-testid="callback-hours-end" value={cbEnd} onChange={(e) => setCbEnd(e.target.value)}
                  className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                  {buildCallbackHours("06:30", "23:00").map((h) => <option key={h.v} value={h.v}>{h.fr}</option>)}
                </select>
                <button type="button" data-testid="callback-hours-save"
                  onClick={async () => {
                    try {
                      await api.patch("/admin/settings", { callback_hours: { start: cbStart, end: cbEnd } });
                      setShopSettings((s) => ({ ...s, callback_hours: { start: cbStart, end: cbEnd } }));
                      toast.success("Heures de rappel enregistrées — les formulaires les utilisent aussitôt");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
          {/* Ton des réponses IA du robot Messenger (demande du propriétaire,
              2026-10-08) : style + signature réglables ici */}
          <TonRobotCard />
          {/* Protection des photos : balises meta de droits d'auteur sur toutes les
              photos + vérification « Retrouver mes photos sur le web » (2026-10-08) */}
          <ProtectionPhotosCard />
          {/* Terme de paiement GLOBAL par défaut (demande du propriétaire,
              2026-10-22) : appliqué aux PDF/courriels quand NI la fiche de la
              soumission NI le dossier client ne précisent un terme. Ordre :
              fiche > dossier client > ce réglage. Réglé par l'admin seul. */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="terme-paiement-defaut-card">
              <p className="font-semibold">Terme de paiement par défaut</p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Inscrit sur les PDF et courriels des soumissions quand ni la fiche ni le dossier client
                ne précisent un terme. Un choix sur la fiche ou au dossier client a toujours priorité.
              </p>
              <div className="flex items-end gap-3 flex-wrap">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Terme appliqué par défaut</span>
                  <select data-testid="terme-paiement-defaut-select" value={payTermDef} onChange={(e) => setPayTermDef(e.target.value)}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    <option value="reception">Relâchée une fois le paiement reçu</option>
                    <option value="15j">15 jours net</option>
                    <option value="30j">30 jours net</option>
                  </select>
                </label>
                <button type="button" data-testid="terme-paiement-defaut-save"
                  onClick={async () => {
                    try {
                      await api.patch("/admin/settings", { payment_terms_default: payTermDef });
                      setShopSettings((s) => ({ ...s, payment_terms_default: payTermDef }));
                      toast.success("Terme de paiement par défaut enregistré — appliqué aux prochains documents");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer
                </button>
              </div>
            </div>
          )}
          {/* Rabais unique par TYPE ou CATALOGUE (demande du propriétaire,
              2026-10-22) : « X % dès Y unités » (ou sans seuil) appliqué à tous les
              produits du type/catalogue visé — le rabais au niveau PRODUIT (fiche)
              a priorité ; ordre de précision : produit → catégorie → grande
              catégorie → catalogue */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="discount-rules-card">
            <p className="font-semibold">Rabais unique par type ou catalogue</p>
            <p className="text-xs text-ivory/70 mt-1 mb-4">
              S'applique à tous les produits visés (prix de la boutique). Un rabais inscrit sur la fiche
              d'un produit a priorité. Le plus précis gagne : catégorie → grande catégorie → catalogue.
            </p>
            {(shopSettings?.discount_rules || []).length > 0 && (
              <ul className="space-y-2 mb-4" data-testid="discount-rules-list">
                {(shopSettings?.discount_rules || []).map((r, i) => (
                  <li key={`${r.level}-${r.key}`} className="flex items-center gap-3 text-sm bg-ink border border-ivory/10 rounded-lg px-4 py-2.5"
                    data-testid={`discount-rule-${i}`}>
                    <span className="text-[10px] uppercase tracking-widest text-ivory/45 w-36 shrink-0">
                      {r.level === "category" ? "Catégorie" : r.level === "shop_category" ? "Grande catégorie" : "Catalogue"}
                    </span>
                    <span className="text-ivory/85 flex-1 min-w-0 truncate">{r.key}</span>
                    <span className="text-gold font-bold whitespace-nowrap">
                      −{r.pct} %{r.min_qty > 0 ? ` dès ${r.min_qty} u.` : ""}
                    </span>
                    <button type="button" data-testid={`discount-rule-del-${i}`}
                      onClick={async () => {
                        const liste = (shopSettings?.discount_rules || []).filter((_, k) => k !== i);
                        try {
                          await api.patch("/admin/settings", { discount_rules: liste });
                          setShopSettings((s) => ({ ...s, discount_rules: liste }));
                          toast.success("Règle retirée");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="text-ivory/45 hover:text-red-400 transition-colors shrink-0" title="Retirer la règle">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Niveau</span>
                <select value={ruleDraft.level} onChange={(e) => setRuleDraft({ ...ruleDraft, level: e.target.value, key: "" })}
                  data-testid="discount-rule-level"
                  className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                  <option value="category">Catégorie du produit</option>
                  <option value="shop_category">Grande catégorie</option>
                  <option value="catalogue">Catalogue nº</option>
                </select>
              </label>
              <label className="block min-w-[180px]">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Type / catalogue visé</span>
                {ruleDraft.level === "category" ? (
                  <>
                    <input list="discount-rule-cats" value={ruleDraft.key}
                      onChange={(e) => setRuleDraft({ ...ruleDraft, key: e.target.value })}
                      placeholder="ex. : Coupe Métal, Rubans…" data-testid="discount-rule-key"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
                    <datalist id="discount-rule-cats">
                      {prodFacets.categories.map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </>
                ) : ruleDraft.level === "shop_category" ? (
                  <select value={ruleDraft.key} onChange={(e) => setRuleDraft({ ...ruleDraft, key: e.target.value })}
                    data-testid="discount-rule-key"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    <option value="">Choisir…</option>
                    {["Trophées", "Médailles", "Plaques", "Autres"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <select value={ruleDraft.key} onChange={(e) => setRuleDraft({ ...ruleDraft, key: e.target.value })}
                    data-testid="discount-rule-key"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    <option value="">Choisir…</option>
                    {["1", "2", "3", "4", "5", "6"].map((c) => <option key={c} value={c}>Catalogue {c}</option>)}
                  </select>
                )}
              </label>
              <label className="block w-24">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Rabais %</span>
                <input type="number" step="0.5" min="0.5" max="90" value={ruleDraft.pct}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, pct: e.target.value })}
                  data-testid="discount-rule-pct"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
              </label>
              <label className="block w-28">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Dès (unités)</span>
                <input type="number" min="0" step="1" value={ruleDraft.min_qty} placeholder="1re"
                  onChange={(e) => setRuleDraft({ ...ruleDraft, min_qty: e.target.value })}
                  data-testid="discount-rule-min-qty"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
              </label>
              <button type="button" data-testid="discount-rule-add"
                onClick={async () => {
                  const pct = parseFloat(String(ruleDraft.pct).replace(",", "."));
                  if (!ruleDraft.key.trim()) { toast.error("Choisissez le type ou le catalogue visé"); return; }
                  if (!pct || pct <= 0 || pct > 90) { toast.error("Le rabais doit être entre 0 et 90 %"); return; }
                  const cle = ruleDraft.key.trim();
                  const actuelles = (shopSettings?.discount_rules || [])
                    .filter((r) => !(r.level === ruleDraft.level && r.key.toLowerCase() === cle.toLowerCase()));
                  const liste = [...actuelles, { level: ruleDraft.level, key: cle, pct,
                                                 min_qty: Math.max(0, parseInt(ruleDraft.min_qty) || 0) }];
                  try {
                    await api.patch("/admin/settings", { discount_rules: liste });
                    setShopSettings((s) => ({ ...s, discount_rules: liste }));
                    setRuleDraft({ level: ruleDraft.level, key: "", pct: "", min_qty: "" });
                    toast.success("Règle enregistrée — appliquée aux prix de la boutique");
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }}
                className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                Ajouter la règle
              </button>
            </div>
          </div>
          {/* Délais des relances automatiques (demande du propriétaire, 2026-10-06) :
              rappel atelier « soumission sans réponse », rappel de paiement au client
              et rappel d'épreuve — de 24 h à 30 jours. S'applique aux PROCHAINS envois
              (les dossiers déjà relancés gardent leur marque). */}
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="relances-card">
            <p className="font-semibold">Relances automatiques — délais</p>
            <p className="text-xs text-ivory/70 mt-1 mb-4">
              Temps d'attente avant chaque relance. S'applique aux prochains envois.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[
                ["Soumission sans réponse (avis à l'atelier)", relQuote, setRelQuote, "relance-quote"],
                ["Paiement en attente (rappel au client)", relPay, setRelPay, "relance-payment"],
                ["Épreuve sans réponse (rappel au client)", relProof, setRelProof, "relance-proof"],
                ["Adresse non confirmée après acceptation (relance au client)", relAddr, setRelAddr, "relance-adresse"],
              ].map(([label, val, set, tid]) => (
                <label key={tid} className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">{label}</span>
                  <select data-testid={tid} value={val} onChange={(e) => set(Number(e.target.value))}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    {[[24, "24 h"], [48, "48 h"], [72, "72 h (3 jours)"], [120, "5 jours"],
                      [168, "7 jours"], [336, "14 jours"], [720, "30 jours"]].map(([v, lab]) => (
                      <option key={v} value={v}>{lab}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            {/* Intervalles des relances « PASSÉ DÛ » (demande du propriétaire,
                2026-10-27) : 1re relance N jours après l'échéance de l'entente,
                puis une relance aux N jours jusqu'au paiement */}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                ["« Passé dû » — 1re relance (jours après l'échéance)", relPdDelai, setRelPdDelai, "relance-passe-du-delai"],
                ["« Passé dû » — puis une relance aux (jours, jusqu'au paiement)", relPdInter, setRelPdInter, "relance-passe-du-intervalle"],
              ].map(([label, val, set, tid]) => (
                <label key={tid} className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">{label}</span>
                  <select data-testid={tid} value={val} onChange={(e) => set(Number(e.target.value))}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    {[[1, "1 jour"], [2, "2 jours"], [3, "3 jours"], [4, "4 jours"], [5, "5 jours"],
                      [7, "7 jours"], [10, "10 jours"], [14, "14 jours"]].map(([v, lab]) => (
                      <option key={v} value={v}>{lab}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            {/* Seuil « GROS TOURNOI » pour l'avis Google 7 j après la livraison
                (demande du propriétaire, 2026-10-27) : un dossier livré est un
                gros tournoi si pièces >= seuil OU montant >= seuil */}
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                ["Avis Google « gros tournoi » — pièces minimum", avisTournPieces, setAvisTournPieces, "relance-avis-tournoi-pieces", "pièces"],
                ["Avis Google « gros tournoi » — montant minimum", avisTournMontant, setAvisTournMontant, "relance-avis-tournoi-montant", "$"],
              ].map(([label, val, set, tid, unite]) => (
                <label key={tid} className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">{label}</span>
                  <span className="flex items-center gap-2">
                    <input type="number" min="1" data-testid={tid} value={val}
                      onChange={(e) => set(Number(e.target.value))}
                      className="w-28 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none transition-colors" />
                    <span className="text-xs text-ivory/50">{unite}</span>
                  </span>
                </label>
              ))}
            </div>
            {/* Mot personnel en tête des courriels d'adresses (demande du
                propriétaire, 2026-10-19) : relance auto ET bouton « SVP confirmer
                votre adresse » — vide = aucun mot ajouté */}
            <label className="block mb-4">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">
                Mot ajouté en tête des courriels de relance — adresses, paiement et épreuve (optionnel)
              </span>
              <textarea rows={2} maxLength={600} value={addrNote} onChange={(e) => setAddrNote(e.target.value)}
                placeholder="Ex. : Merci de confirmer rapidement — la production démarre dès réception !"
                data-testid="relance-adresse-note"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none transition-colors resize-y" />
            </label>
            {/* Aperçu des courriels de relance (demande du propriétaire, 2026-10-20) :
                le courriel TEL QUE le client le recevra, avec le mot actuel —
                données fictives d'exemple, rien n'est envoyé */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="relances-preview-row">
              <span className="text-[10px] uppercase tracking-widest text-ivory/50">Voir le courriel :</span>
              {[["adresse", "Relance adresse"], ["demande", "Demande d'adresses"],
                ["paiement", "Rappel paiement"], ["epreuve", "Rappel d'épreuve"]].map(([kind, label]) => (
                <button key={kind} type="button" data-testid={`relance-preview-${kind}`}
                  onClick={async () => {
                    try {
                      const r = await api.get(`/admin/reminder-preview/${kind}`);
                      setCourrielApercu({ titre: label, ...r.data });
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="inline-flex items-center gap-1.5 border border-ivory/20 text-ivory/70 px-3.5 py-1.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300">
                  <Eye className="w-3 h-3" /> {label}
                </button>
              ))}
            </div>
            <button type="button" data-testid="relances-save"
              onClick={async () => {
                try {
                  await api.patch("/admin/settings", { quote_reminder_hours: relQuote,
                    payment_reminder_hours: relPay, proof_reminder_hours: relProof,
                    address_reminder_hours: relAddr, address_email_note: addrNote,
                    passe_du_relance_delai: relPdDelai, passe_du_relance_intervalle: relPdInter,
                    avis_tournoi_min_pieces: avisTournPieces, avis_tournoi_min_montant: avisTournMontant });
                  setShopSettings((s) => ({ ...s, quote_reminder_hours: relQuote,
                    payment_reminder_hours: relPay, proof_reminder_hours: relProof,
                    address_reminder_hours: relAddr, address_email_note: addrNote,
                    passe_du_relance_delai: relPdDelai, passe_du_relance_intervalle: relPdInter,
                    avis_tournoi_min_pieces: avisTournPieces, avis_tournoi_min_montant: avisTournMontant }));
                  toast.success("Délais des relances enregistrés — appliqués aux prochains envois");
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              }}
              className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
              Enregistrer les délais
            </button>
          </div>
          {/* Aperçu d'un courriel de relance (demande du propriétaire, 2026-10-20) :
              iframe du HTML exact, avec le mot actuel — n'envoie rien */}
          {courrielApercu && (
            <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="courriel-apercu-modal">
              <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setCourrielApercu(null)} aria-label="Fermer" />
              <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 shadow-2xl shadow-black/60">
                <button onClick={() => setCourrielApercu(null)} data-testid="courriel-apercu-close"
                  className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
                  <X className="w-5 h-5" />
                </button>
                <h3 className="font-serif text-2xl mb-1">{courrielApercu.titre} — aperçu</h3>
                <p className="text-xs text-ivory/55 mb-1">
                  Courriel tel que le client le recevra, avec votre mot actuel. Données d'exemple — rien n'est envoyé.
                </p>
                <p className="text-xs text-ivory/70 mb-4">
                  <span className="text-ivory/45">Objet : </span>
                  <strong data-testid="courriel-apercu-subject">{courrielApercu.subject}</strong>
                </p>
                <iframe title="Aperçu du courriel de relance" srcDoc={courrielApercu.html}
                  data-testid="courriel-apercu-frame"
                  className="w-full rounded-lg border border-ivory/15 bg-white" style={{ height: 420 }} />
              </div>
            </div>
          )}
          {/* PIÈCE JOINTE FAVORITE (demande du propriétaire, 2026-10-27 :
              « garder un PDF de catalogue en favori pour le joindre en un clic
              à chaque soumission ») — proposée dans le popup d'aperçu de CHAQUE
              courriel de la fiche ; jointe au courriel si choisie */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="piece-favorite-card">
              <p className="font-semibold">Pièce jointe favorite</p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Un document (ex. catalogue PDF) proposé en un clic dans le popup « aperçu avant envoi »
                de chaque courriel — il part en pièce jointe si vous le choisissez.
              </p>
              {shopSettings?.piece_favorite_path ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm text-ivory/85 truncate" data-testid="piece-favorite-nom">
                    {shopSettings.piece_favorite_nom || "Document favori"}
                  </span>
                  <div className="flex items-center gap-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shopSettings.piece_favorite_path}`}
                      target="_blank" rel="noreferrer" data-testid="piece-favorite-voir"
                      className="text-xs text-gold hover:text-champagne border border-gold/30 rounded-full px-3 py-1.5 transition-colors">
                      Voir le fichier
                    </a>
                    <button data-testid="piece-favorite-retirer"
                      onClick={async () => {
                        try {
                          await api.patch("/admin/settings", { piece_favorite_path: "", piece_favorite_nom: "" });
                          setShopSettings((s) => ({ ...s, piece_favorite_path: "", piece_favorite_nom: "" }));
                          toast.success("Pièce jointe favorite retirée.");
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="text-xs text-red-300/90 hover:text-red-200 border border-red-400/30 rounded-full px-3 py-1.5 transition-colors">
                      Retirer
                    </button>
                  </div>
                </div>
              ) : (
                <label className="inline-flex items-center gap-2 text-xs text-gold border border-gold/30 rounded-full px-4 py-2 cursor-pointer hover:border-gold/60 transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Choisir un fichier (PDF ou image, 5 Mo max)
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.svg,.heic,.heif"
                    data-testid="piece-favorite-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      try {
                        const form = new FormData();
                        form.append("file", f);
                        const { data: up } = await api.post("/uploads/logo", form);
                        await api.patch("/admin/settings", { piece_favorite_path: up.path, piece_favorite_nom: f.name });
                        setShopSettings((s) => ({ ...s, piece_favorite_path: up.path, piece_favorite_nom: f.name }));
                        toast.success(`« ${f.name} » est maintenant la pièce jointe favorite.`);
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }} />
                </label>
              )}
            </div>
          )}
          {/* BANNIÈRE « FORFAITS TOURNOI » DESSINÉE PAR GEMINI (demande du
              propriétaire, 2026-10-27) — un clic dessine et applique l'image
              au bandeau de l'accueil ; « Retirer » revient au bandeau sobre */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="banniere-forfaits-card">
              <p className="font-semibold">Bannière « Forfaits Tournoi » — image par Gemini</p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                L'image dessinée par Gemini se fond à droite du bandeau « Saison des tournois » sur la page d'accueil.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                {shopSettings?.forfait_banner_img && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shopSettings.forfait_banner_img}`}
                    alt="Bannière Forfaits Tournoi dessinée par Gemini" data-testid="banniere-forfaits-apercu"
                    className="h-14 w-40 object-cover rounded-lg border border-gold/30 bg-black" />
                )}
                <button data-testid="banniere-forfaits-generer" disabled={banniereBusy}
                  onClick={async () => {
                    if (banniereBusy) return;
                    setBanniereBusy(true);
                    try {
                      const { data } = await api.post("/admin/themes-saison/banniere-forfaits", {});
                      setShopSettings((s) => ({ ...s, forfait_banner_img: data.path }));
                      toast.success("Bannière dessinée par Gemini — visible dès maintenant sur l'accueil.");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    finally { setBanniereBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 text-xs bg-gold text-ink font-semibold rounded-full px-4 py-2 hover:bg-champagne transition-colors disabled:opacity-60">
                  {banniereBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {shopSettings?.forfait_banner_img ? "Redessiner avec Gemini" : "Dessiner avec Gemini"}
                </button>
                {shopSettings?.forfait_banner_img && (
                  <button data-testid="banniere-forfaits-retirer"
                    onClick={async () => {
                      try {
                        await api.patch("/admin/settings", { forfait_banner_img: "" });
                        setShopSettings((s) => ({ ...s, forfait_banner_img: "" }));
                        toast.success("Image retirée — le bandeau redevient sobre.");
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="text-xs text-red-300/90 hover:text-red-200 border border-red-400/30 rounded-full px-3 py-1.5 transition-colors">
                    Retirer l'image
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Déconnexion du PERSONNEL après inactivité (demande du propriétaire,
              2026-10-16) : fenêtre glissante — chaque utilisation remet le chrono
              à zéro ; les clients ne sont jamais touchés. Réglé par l'admin seul. */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="inactivite-staff-card">
              <p className="font-semibold">Déconnexion du personnel (inactivité)</p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Admins et vendeurs sont déconnectés après ce délai sans utilisation et devront se reconnecter.
                Chaque visite remet le compteur à zéro. Les clients ne sont jamais déconnectés.
              </p>
              <div className="flex items-end gap-3 flex-wrap">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Délai d'inactivité</span>
                  <select data-testid="inactivite-staff-select" value={inactStaff} onChange={(e) => setInactStaff(Number(e.target.value))}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    {[[12, "12 heures"], [24, "24 heures"], [48, "48 heures (défaut)"], [72, "72 heures (3 jours)"],
                      [168, "7 jours"], [336, "14 jours"], [720, "30 jours"]].map(([v, lab]) => (
                      <option key={v} value={v}>{lab}</option>
                    ))}
                  </select>
                </label>
                <button type="button" data-testid="inactivite-staff-save"
                  onClick={async () => {
                    try {
                      await api.patch("/admin/settings", { staff_inactivity_hours: inactStaff });
                      setShopSettings((s) => ({ ...s, staff_inactivity_hours: inactStaff }));
                      toast.success(`Délai enregistré — déconnexion après ${inactStaff} h sans utilisation`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer
                </button>
              </div>
            </div>
          )}
          {/* Seuil du blocage AUTO anti-marteau (demande du propriétaire,
              2026-10-17) : une IP qui dépasse ce rythme est bloquée 24 h avec
              avis par courriel. Réglé par l'admin seul ; prise d'effet en ~1 min. */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-ivory/10 rounded-xl p-7 mb-6" data-testid="seuil-marteau-card">
              <p className="font-semibold flex items-center gap-2 flex-wrap">
                Blocage automatique anti-robots — sensibilité
                <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Une adresse IP qui dépasse ce nombre de requêtes en une minute est bloquée du site
                pour 24 h et vous en êtes avisé par courriel. Jamais touchés : Google/Bing, vos IP
                de confiance et toute IP déjà utilisée par un administrateur (protection permanente).
                Prise d'effet en environ 1 minute.
              </p>
              <div className="flex items-end gap-3 flex-wrap">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest text-ivory/50 mb-1.5">Requêtes par minute</span>
                  <select data-testid="seuil-marteau-select" value={seuilMarteau} onChange={(e) => setSeuilMarteau(Number(e.target.value))}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors">
                    {[[60, "60 — très vigilant"], [120, "120 — défaut"], [300, "300 — tolérant"],
                      [600, "600 — très tolérant"], [1200, "1 200"], [5000, "5 000 — presque jamais"]].map(([v, lab]) => (
                      <option key={v} value={v}>{lab}</option>
                    ))}
                  </select>
                </label>
                <button type="button" data-testid="seuil-marteau-save"
                  onClick={async () => {
                    try {
                      await api.patch("/admin/settings", { auto_block_rpm: seuilMarteau });
                      setShopSettings((s) => ({ ...s, auto_block_rpm: seuilMarteau }));
                      toast.success(`Seuil enregistré — blocage automatique au-delà de ${seuilMarteau} requêtes/minute`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer
                </button>
              </div>
            </div>
          )}
          {/* Rabais de la contre-offre AUTOMATIQUE (demande du propriétaire,
              2026-10-06) : quand un client retire sa soumission pour une raison de
              prix, ce % est proposé sur produits + gravure avant le retrait.
              Politique de prix — réservé aux administrateurs */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-6" data-testid="counter-offer-card">
              <p className="font-semibold flex items-center gap-2 flex-wrap">
                Contre-offre automatique — rabais
                <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Quand un client retire sa soumission pour une raison de prix, ce rabais (produits + gravure) lui est proposé automatiquement avant le retrait. Il pré-remplit aussi la contre-offre manuelle de l'onglet Soumissions.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <input type="number" min="1" max="90" step="1" inputMode="numeric"
                    data-testid="counter-offer-pct" value={coPct} onChange={(e) => setCoPct(e.target.value)}
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-24 text-center focus:border-gold focus:outline-none transition-colors" />
                  <span className="text-sm text-ivory/70">% (1 à 90)</span>
                </div>
                <button type="button" data-testid="counter-offer-save"
                  onClick={async () => {
                    const v = Math.round(Number(String(coPct).replace(",", ".")));
                    if (!v || v < 1 || v > 90) { toast.error("Le rabais doit être entre 1 et 90 %"); return; }
                    try {
                      await api.patch("/admin/settings", { counter_offer_pct: v });
                      setShopSettings((s) => ({ ...s, counter_offer_pct: v }));
                      setCoPct(String(v));
                      toast.success(`Rabais de la contre-offre automatique réglé à ${v} % — appliqué aux prochaines offres`);
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer le rabais
                </button>
              </div>
            </div>
          )}
          {/* Objectif de ventes du mois (demande du propriétaire, 2026-10-22) :
              ligne pointillée champagne sur la courbe des 12 mois (Rapports →
              Ventes et taxes) — 0 ou vide = pas de ligne. Réservé aux admins */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-6" data-testid="objectif-mensuel-card">
              <p className="font-semibold flex items-center gap-2 flex-wrap">
                Objectif de ventes du mois
                <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Affiche une ligne pointillée sur la courbe des 12 derniers mois (Rapports → Ventes et taxes)
                pour voir d'un coup d'œil si le mois en cours est sur la bonne voie. Montant TTC, en dollars.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <input type="number" min="0" step="100" inputMode="numeric"
                    data-testid="objectif-mensuel" value={objectifMens} onChange={(e) => setObjectifMens(e.target.value)}
                    placeholder="ex. 5000"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-32 text-center focus:border-gold focus:outline-none transition-colors" />
                  <span className="text-sm text-ivory/70">$ TTC / mois (vide = aucune ligne)</span>
                </div>
                <button type="button" data-testid="objectif-mensuel-save"
                  onClick={async () => {
                    const v = objectifMens.trim() === "" ? 0 : Number(String(objectifMens).replace(",", "."));
                    if (isNaN(v) || v < 0) { toast.error("Objectif invalide — un montant en dollars (ex. : 5000)"); return; }
                    try {
                      await api.patch("/admin/settings", { objectif_mensuel: v });
                      setShopSettings((s) => ({ ...s, objectif_mensuel: v }));
                      setObjectifMens(v ? String(v) : "");
                      toast.success(v ? `Objectif réglé à ${v.toFixed(0)} $ par mois — visible sur la courbe des Rapports`
                                      : "Ligne d'objectif retirée de la courbe");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer l'objectif
                </button>
              </div>
            </div>
          )}
          {/* Comptable + seuil « Client or » (demande du propriétaire, 2026-10-24) :
              le courriel du comptable reçoit EN UN CLIC le PDF groupé des factures
              payées d'un client (popup « factures payées ») ; le seuil règle à
              partir de quel total encaissé à vie la pastille dorée paraît */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-6" data-testid="comptable-card">
              <p className="font-semibold flex items-center gap-2 flex-wrap">
                Comptable et pastille « Client or »
                <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Le <strong className="text-ivory/90">courriel du comptable</strong> reçoit en un clic le PDF
                groupé des factures payées d'un client (bouton dans le popup des factures, onglet Ventes —
                copie à info@trophee.ca). Le <strong className="text-ivory/90">seuil « Client or »</strong> règle
                à partir de quel total encaissé à vie la pastille dorée paraît sur les cartes de vente.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input type="email" data-testid="comptable-email" value={comptableEmail}
                  onChange={(e) => setComptableEmail(e.target.value)}
                  placeholder="comptable@exemple.ca"
                  className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-72 focus:border-gold focus:outline-none transition-colors" />
                <button type="button" data-testid="comptable-email-save"
                  onClick={async () => {
                    const v = comptableEmail.trim();
                    try {
                      await api.patch("/admin/settings", { comptable_email: v });
                      setShopSettings((s) => ({ ...s, comptable_email: v }));
                      toast.success(v ? `PDF groupés des factures → ${v}` : "Courriel du comptable retiré — le bouton d'envoi demandera de le régler");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer le courriel
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <input type="number" min="0" step="500" inputMode="numeric"
                  data-testid="client-or-seuil" value={seuilOr} onChange={(e) => setSeuilOr(e.target.value)}
                  placeholder="10000"
                  className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-32 text-center focus:border-gold focus:outline-none transition-colors" />
                <span className="text-sm text-ivory/70">$ encaissés à vie pour la pastille « Client or » (vide = 10 000 $)</span>
                <button type="button" data-testid="client-or-seuil-save"
                  onClick={async () => {
                    const v = seuilOr.trim() === "" ? 0 : Number(String(seuilOr).replace(",", "."));
                    if (isNaN(v) || v < 0) { toast.error("Seuil invalide — un montant en dollars (ex. : 10000)"); return; }
                    try {
                      await api.patch("/admin/settings", { client_or_seuil: v });
                      setShopSettings((s) => ({ ...s, client_or_seuil: v }));
                      setSeuilOr(v ? String(v) : "");
                      toast.success(v ? `Pastille « Client or » dès ${v.toFixed(0)} $ encaissés à vie`
                                      : "Seuil « Client or » ramené à 10 000 $ (défaut)");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer le seuil
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <input type="number" min="1" max="28" step="1" inputMode="numeric"
                  data-testid="compta-jour" value={comptaJour} onChange={(e) => setComptaJour(e.target.value)}
                  placeholder="1"
                  className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-24 text-center focus:border-gold focus:outline-none transition-colors" />
                <span className="text-sm text-ivory/70">jour du mois de l'envoi automatique du PDF groupé au comptable (vide = le 1er)</span>
                <button type="button" data-testid="compta-jour-save"
                  onClick={async () => {
                    const v = comptaJour.trim() === "" ? 0 : Number(comptaJour);
                    if (isNaN(v) || v < 0 || v > 28) { toast.error("Jour invalide — de 1 à 28 (vide = le 1er)"); return; }
                    try {
                      await api.patch("/admin/settings", { compta_jour: v || null });
                      setShopSettings((s) => ({ ...s, compta_jour: v || null }));
                      setComptaJour(v ? String(v) : "");
                      toast.success(v ? `PDF groupé envoyé au comptable chaque ${v} du mois`
                                      : "Envoi au comptable ramené au 1er du mois (défaut)");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer le jour
                </button>
              </div>
              {/* ACCÈS COMPTABLE (demande du propriétaire, 2026-10-27) : compte
                  en lecture seule limité à la facturation (soumissions,
                  commandes, rapports) — création de facture impossible ; le
                  compte est créé au besoin (lien « choisir mon mot de passe »)
                  et le 2FA sous 30 jours s'applique comme à tout le personnel */}
              <div className="mt-6 pt-5 border-t border-ivory/10">
                <p className="text-sm font-semibold mb-1">Accès comptable (lecture seule)</p>
                <p className="text-xs text-ivory/60 mb-3">
                  Un accès limité aux sections de facturation — soumissions, commandes et rapports en
                  <strong> lecture seule</strong>, création de facture impossible. Révocable dans l'onglet Clients → Vendeurs.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <input type="text" data-testid="comptable-acces-nom" value={comptableNom}
                    onChange={(e) => setComptableNom(e.target.value)} placeholder="Nom (ex. : Comptable)"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-52 focus:border-gold focus:outline-none transition-colors" />
                  <input type="email" data-testid="comptable-acces-email" value={comptableAccesEmail}
                    onChange={(e) => setComptableAccesEmail(e.target.value)} placeholder="courriel du comptable"
                    className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-64 focus:border-gold focus:outline-none transition-colors" />
                  <button type="button" data-testid="comptable-acces-creer"
                    onClick={async () => {
                      const em = comptableAccesEmail.trim();
                      if (!em) { toast.error("Entrez le courriel du comptable"); return; }
                      try {
                        const { data } = await api.post("/admin/comptable/acces", { email: em, nom: comptableNom.trim() });
                        toast.success(data.deja
                          ? `Accès comptable déjà actif pour ${data.email} — réglages rafraîchis.`
                          : `Accès comptable créé pour ${data.email} — courriel de bienvenue envoyé (choix du mot de passe, 2FA sous 30 jours).`);
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                    Créer l'accès comptable
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Multiplicateur du prix de détail (demande du propriétaire, 2026-10-20) :
              coûtant × multiplicateur — « 3 » = ×3, « 200 % » = +200 % — arrondi au
              ,95 $ supérieur (2,50 $ × 3 = 7,50 $ → 7,95 $). Suggéré à côté de chaque
              ligne de soumission avec un bouton « Appliquer ». Politique de prix —
              réservé aux administrateurs */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-gold/25 rounded-xl p-7 mb-6" data-testid="markup-card">
              <p className="font-semibold flex items-center gap-2 flex-wrap">
                Prix de détail suggéré — multiplicateur du coûtant
                <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Dans l'onglet Soumissions, chaque ligne avec un coûtant connu affiche le prix de détail suggéré :
                coûtant × ce multiplicateur, <strong>arrondi au ,95 $ supérieur</strong>
                (ex. : coûtant 2,50 $ × 3 = 7,50 $ → <strong>7,95 $</strong>). Entrez un chiffre
                (« 3 » = ×3) ou un pourcentage (« 200 % » = coûtant + 200 %, donc ×3 aussi).
                <strong>Minimum ×2,5 — jamais plus bas</strong>, sauf autorisation explicite d'un administrateur.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input type="text" inputMode="decimal" data-testid="markup-multiplier"
                  value={markupMult} onChange={(e) => setMarkupMult(e.target.value)}
                  placeholder="3 ou 200 %"
                  className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-32 text-center focus:border-gold focus:outline-none transition-colors" />
                <span className="text-sm text-ivory/70">× ou % (défaut : ×3)</span>
                <button type="button" data-testid="markup-save"
                  onClick={async () => {
                    const brut = String(markupMult).trim().replace(",", ".");
                    const estPct = brut.endsWith("%");
                    const n = parseFloat(estPct ? brut.slice(0, -1) : brut.replace(/^[x×]\s*/i, ""));
                    if (!isFinite(n) || n <= 0 || (estPct ? n > 100000 : n > 1000)) {
                      toast.error("Entrez un multiplicateur valide (ex. : 3 ou 200 %)"); return;
                    }
                    const v = estPct ? `${Math.round(n * 100) / 100} %` : String(Math.round(n * 100) / 100);
                    const sauver = async (forcer) => {
                      await api.patch("/admin/settings", { markup_multiplier: v, forcer_multiplicateur: forcer });
                      setShopSettings((s) => ({ ...s, markup_multiplier: v }));
                      setMarkupMult(v);
                      toast.success(`Multiplicateur réglé à ${estPct ? v : "×" + v} — arrondi au ,95 $ supérieur`);
                    };
                    // Plancher ×2,5 (règle du propriétaire) : sous ce seuil, le serveur
                    // refuse sauf autorisation explicite d'un administrateur
                    try { await sauver(false); } catch (err) {
                      const detail = err.response?.data?.detail || "";
                      if (err.response?.status === 400 && detail.includes("×2,5")
                          && window.confirm(`${detail}\n\nAutoriser ce facteur en tant qu'administrateur ?`)) {
                        try { await sauver(true); } catch (err2) { toast.error(formatApiError(err2.response?.data?.detail)); }
                      } else if (!detail.includes("×2,5")) { toast.error(formatApiError(detail)); }
                    }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-gold text-ink font-semibold hover:bg-champagne transition-colors">
                  Enregistrer le multiplicateur
                </button>
              </div>
            </div>
          )}
          {/* Code d'accès au rapport Formation (demande du propriétaire,
              2026-10-06) : la compilation Formation en bas des Rapports est
              confidentielle — réservée aux administrateurs ET protégée par ce
              code, établi ici, jamais lisible par un vendeur */}
          {user?.role === "admin" && (
            <div className="bg-surface border border-orange-500/25 rounded-xl p-7 mb-6" data-testid="test-code-card">
              <p className="font-semibold flex items-center gap-2">
                Code d'accès au rapport Formation
                <span className="text-[9px] uppercase tracking-widest border border-orange-500/40 text-orange-300 rounded-full px-2 py-0.5">admins seulement</span>
              </p>
              <p className="text-xs text-ivory/70 mt-1 mb-4">
                Le rapport Formation (bas de la page Rapports) demande ce code. Laissez vide pour retirer le code (le rapport devient inaccessible tant qu'aucun code n'est établi).
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input type="text" value={testCode} onChange={(e) => setTestCode(e.target.value)}
                  data-testid="test-report-code-input" placeholder="ex. atelier-2026" autoComplete="off"
                  maxLength={40}
                  className="bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm w-64 max-w-full focus:border-orange-300 focus:outline-none transition-colors" />
                <button type="button" data-testid="test-report-code-save"
                  onClick={async () => {
                    try {
                      await api.patch("/admin/settings", { test_report_code: testCode });
                      setShopSettings((s) => ({ ...s, test_report_code: testCode }));
                      toast.success(testCode.trim()
                        ? "Code du rapport Formation enregistré — il sera demandé à l'ouverture du rapport"
                        : "Code retiré — le rapport Formation est verrouillé jusqu'à l'établissement d'un nouveau code");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}
                  className="px-4 py-2 rounded-full text-sm bg-orange-500/90 text-ink font-semibold hover:bg-orange-400 transition-colors">
                  Enregistrer le code
                </button>
              </div>
            </div>
          )}
          {/* Activation/désactivation des pages et sections du site */}
          <SubHead testid="settings-group-site">Site et accueil</SubHead>
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8" data-testid="sections-card">
            <p className="font-semibold">Pages et sections du site</p>
            <p className="text-xs text-ivory/70 mt-1 mb-5">
              Désactivez n'importe quelle page ou section : elle disparaît du menu, du pied de page et
              de la page d'accueil, et les visiteurs voient un avis courtois. Rien n'est supprimé —
              réactivez en un clic.
            </p>
            {["Pages", "Page d'accueil"].map((grp) => (
              <div key={grp} className="mb-5" data-testid={`sections-group-${grp === "Pages" ? "pages" : "accueil"}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-gold/70 mb-2.5">{grp}</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {SECTION_DEFS.filter((s) => s.group === grp).map((s) => {
                    const enabled = !(shopSettings.disabled_sections || []).includes(s.key);
                    return (
                      <label key={s.key} data-testid={`section-toggle-${s.key}`}
                        className="flex items-center justify-between gap-3 border border-ivory/10 rounded-lg px-4 py-3 cursor-pointer select-none hover:border-gold/30 transition-colors duration-300">
                        <span className={`text-sm ${enabled ? "text-ivory/85" : "text-ivory/65 line-through"}`}>{s.label}</span>
                        <Switch
                          data-testid={`section-switch-${s.key}`}
                          checked={enabled}
                          onCheckedChange={(v) => setShopSettings((st) => {
                            const cur = st.disabled_sections || [];
                            return { ...st, disabled_sections: v ? cur.filter((k) => k !== s.key) : [...cur, s.key] };
                          })}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            <button type="button" data-testid="sections-save" disabled={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  const { data } = await api.patch("/admin/settings", { disabled_sections: shopSettings.disabled_sections || [] });
                  setShopSettings((s) => ({ ...s, ...data, express_rate: data.express_rate * 100 }));
                  toast.success("Visibilité des sections enregistrée — en ligne immédiatement");
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                finally { setSaving(false); }
              }}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer la visibilité
            </button>
          </div>

          <SubHead testid="settings-group-prix">Prix et rabais</SubHead>
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8 space-y-5" data-testid="tier-template-card">
            <div>
              <p className="font-semibold">Rabais automatiques par quantité</p>
              <p className="text-xs text-ivory/70 mt-1">Gabarit de rabais en % appliqué d'un coup à un catalogue ou à toute la boutique. Modifiable ensuite produit par produit dans sa fiche (max 5 paliers).</p>
            </div>
            <div className="space-y-3">
              {tierTemplate.map((tr, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`tier-template-row-${i}`}>
                  <div className="relative w-28 shrink-0">
                    <input data-testid={`tier-template-qty-${i}`} type="number" min="1" step="1" value={tr.min_qty}
                      onChange={(e) => setTierTemplate((prev) => prev.map((x, k) => (k === i ? { ...x, min_qty: e.target.value } : x)))}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">+</span>
                  </div>
                  <span className="text-ivory/65 text-sm shrink-0">→</span>
                  <div className="relative flex-1 min-w-0">
                    <input data-testid={`tier-template-pct-${i}`} type="number" step="0.5" min="0.5" max="99" value={tr.pct}
                      onChange={(e) => setTierTemplate((prev) => prev.map((x, k) => (k === i ? { ...x, pct: e.target.value } : x)))}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">% de rabais</span>
                  </div>
                  <button type="button" data-testid={`tier-template-remove-${i}`}
                    onClick={() => setTierTemplate((prev) => prev.filter((_, k) => k !== i))}
                    className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {tierTemplate.length < 5 && (
                <button type="button" data-testid="tier-template-add"
                  onClick={() => setTierTemplate((prev) => [...prev, { min_qty: "", pct: "" }])}
                  className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                  <Plus className="w-3.5 h-3.5" /> Ajouter un palier
                </button>
              )}
              <button type="button" data-testid="tier-template-save" onClick={saveTierTemplate} disabled={saving}
                className="inline-flex items-center gap-2 text-xs border border-ivory/25 text-ivory/80 px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-50">
                Enregistrer comme gabarit
              </button>
            </div>
            <div className="border-t border-ivory/10 pt-5 flex flex-wrap items-center gap-3">
              <Select value={tierScope} onValueChange={setTierScope}>
                <SelectTrigger data-testid="tier-template-scope" className="bg-ink border-ivory/15 w-72"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-gold/20 text-ivory">
                  <SelectItem value="1.0">Catalogue nº 1 — Awards of Distinction</SelectItem>
                  <SelectItem value="4.0">Catalogue nº 4 — Caldwell</SelectItem>
                  <SelectItem value="catalogues">Tous les produits de catalogues</SelectItem>
                  <SelectItem value="tous">Tous les produits de la boutique</SelectItem>
                </SelectContent>
              </Select>
              <button type="button" data-testid="tier-template-apply" onClick={applyTierTemplate} disabled={applyingTiers}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 text-sm">
                {applyingTiers && <Loader2 className="w-4 h-4 animate-spin" />} Appliquer maintenant
              </button>
            </div>
            <p className="text-xs text-ivory/65">Liste de paliers vide = retire les rabais automatiques des produits visés. N'affecte pas les prix fixes par quantité définis sur certains produits. Les produits modifiés manuellement (badge « modifié man. ») ne sont jamais touchés.</p>
          </div>

          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8 space-y-5" data-testid="fee-template-card">
            <div>
              <p className="font-semibold">Prix des inscriptions (tous les produits)</p>
              <p className="text-xs text-ivory/70 mt-1">Change d'un coup le prix de la gravure laser et/ou de l'impression couleur pour toute la boutique. Les produits modifiés manuellement (badge « modifié man. ») ne sont jamais touchés.</p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative w-56">
                <input data-testid="fee-gravure-input" type="number" step="0.25" min="0" value={feeGravure}
                  onChange={(e) => setFeeGravure(e.target.value)}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">$ gravure / unité</span>
              </div>
              <div className="relative w-56">
                <input data-testid="fee-impression-input" type="number" step="0.25" min="0" value={feeImpression}
                  onChange={(e) => setFeeImpression(e.target.value)}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">$ impression / unité</span>
              </div>
              <button type="button" data-testid="fee-apply" onClick={applyFees} disabled={applyingFees}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 text-sm">
                {applyingFees && <Loader2 className="w-4 h-4 animate-spin" />} Appliquer à toute la boutique
              </button>
            </div>
            <p className="text-xs text-ivory/65">Laisser un champ vide pour ne pas changer ce type d'inscription. Modifiable ensuite produit par produit dans sa fiche.</p>
          </div>

          <SubHead testid="settings-group-sauvegarde">Sauvegarde</SubHead>
          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8" data-testid="backup-card">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-64">
                <p className="font-semibold">Sauvegarde complète hebdomadaire automatique</p>
                <p className="text-xs text-ivory/70 mt-1">Chaque lundi à 4 h (heure du Québec), une sauvegarde est créée et conservée en ligne — et si le serveur était éteint ou redémarrait à ce moment précis, elle part automatiquement au prochain réveil dans la semaine (rattrapage) : <strong className="text-ivory/90">toutes les données</strong> (produits, commandes, clients sans mots de passe, codes rabais, prospects, zones des catalogues, paramètres), le <strong className="text-ivory/90">code source du site</strong> et la <strong className="text-ivory/90">liste complète des photos/vidéos avec liens directs</strong> — de quoi réinstaller l'application au complet en cas de piratage (guide LISEZ-MOI inclus). Les photos/vidéos elles-mêmes : bouton « Archive complète » ci-dessous (téléchargées en parties). L'avis par courriel contient un lien de téléchargement direct (valide 14 jours).</p>
              </div>
              <label className="flex items-center gap-3 text-sm text-ivory/70 select-none" data-testid="backup-email-toggle-label">
                <Switch data-testid="backup-email-switch" checked={shopSettings?.backup_email_enabled !== false} onCheckedChange={toggleBackupEmail} />
                Avis par courriel (lundi 4 h)
              </label>
              <button data-testid="backup-now" onClick={sendBackupNow} disabled={backupSending}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300 disabled:opacity-60">
                {backupSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {backupSending ? "Construction en cours…" : "Créer la sauvegarde complète maintenant"}
              </button>
              <button data-testid="backup-download" onClick={downloadBackup} disabled={backupDownloading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs bg-gold text-ink font-semibold hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {backupDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Télécharger la dernière sauvegarde{backupStatus?.size_kb ? ` (${Math.round(backupStatus.size_kb / 1024)} Mo)` : ""}
              </button>
              <input ref={restoreInputRef} type="file" accept=".zip" className="hidden" data-testid="backup-restore-input" onChange={restoreBackup} />
              <button data-testid="backup-restore" onClick={() => restoreInputRef.current?.click()} disabled={backupRestoring}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs border border-ivory/25 text-ivory/70 hover:border-gold/50 hover:text-gold transition-colors duration-300 disabled:opacity-60">
                {backupRestoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Restaurer depuis un ZIP
              </button>
            </div>
            {backupStatus?.build_status === "running" && (
              <div className="mt-4" data-testid="backup-progress">
                <div className="flex justify-between text-[11px] text-ivory/60 mb-1.5">
                  <span>{backupStatus.build_done || 0} / {backupStatus.build_total || "…"} photos et vidéos rassemblées</span>
                  <span>{backupStatus.build_total ? Math.round(((backupStatus.build_done || 0) / backupStatus.build_total) * 100) : 0} %</span>
                </div>
                <div className="h-2 rounded-full bg-ink overflow-hidden border border-ivory/10">
                  <div className="h-full bg-gold transition-all duration-500"
                    style={{ width: `${backupStatus.build_total ? Math.round(((backupStatus.build_done || 0) / backupStatus.build_total) * 100) : 5}%` }} />
                </div>
                <p className="text-[11px] text-ivory/50 mt-2">Vous pouvez quitter cette page — la construction continue sur le serveur et l'avis partira par courriel.</p>
              </div>
            )}
            {backupStatus?.build_status === "error" && (
              <p className="mt-3 text-xs text-red-400" data-testid="backup-error-info">
                La dernière construction a échoué ({backupStatus.build_error || "erreur inconnue"}) — relancez avec le bouton ci-dessus ; si ça se répète, contactez-nous.
              </p>
            )}
            {backupStatus?.last_error && (
              <p className="mt-3 text-xs text-amber-400" data-testid="backup-last-error">
                La sauvegarde automatique de cette semaine a échoué{backupStatus.last_error_at ? ` le ${new Date(backupStatus.last_error_at).toLocaleString("fr-CA", { timeZone: TZ_MONTREAL })}` : ""} : {backupStatus.last_error} — une nouvelle tentative aura lieu demain ; vous pouvez aussi la relancer avec le bouton ci-dessus.
              </p>
            )}
            {backupStatus?.build_status === "done" && backupStatus?.sent_at && !backupSending && (
              <p className="mt-3 text-xs text-emerald-400" data-testid="backup-done-info">
                Dernière sauvegarde : {new Date(backupStatus.sent_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL })} — {Math.round((backupStatus.size_kb || 0) / 1024)} Mo
                {backupStatus?.stats?.media_included ? ` · ${backupStatus.stats.media_included} photos/vidéos` : " · photos/vidéos : liste avec liens directs"}
                {backupStatus?.stats?.code_files != null && ` · ${backupStatus.stats.code_files} fichiers de code`}
                {backupStatus.emailed ? " · avis envoyé par courriel" : ""}
              </p>
            )}
            {/* Archive médias complète : TOUTES les photos et vidéos, en parties infonuagiques */}
            <div className="mt-5 pt-5 border-t border-ivory/10" data-testid="media-backup-row">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-64">
                  <p className="text-sm font-semibold text-ivory/90">Archive complète photos et vidéos (tout, sans limite)</p>
                  <p className="text-xs text-ivory/60 mt-1">Rassemble <strong>tous</strong> les fichiers du site (18 000+ images, vidéos, PDF) en parties de ~80 Mo envoyées dans le nuage — le disque du serveur est limité. Téléchargement ensuite ici : <strong className="text-ivory/85">en un seul lien</strong> (tout fusionné en un fichier, peut être long) <strong className="text-ivory/85">ou partie par partie</strong>.</p>
                </div>
                <button data-testid="media-backup-start" onClick={startMediaArchive}
                  disabled={mediaArchive?.status === "running"}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300 disabled:opacity-60">
                  {mediaArchive?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
                  {mediaArchive?.status === "running" ? "Construction en cours…" : "Créer l'archive complète"}
                </button>
              </div>
              {mediaArchive?.status === "running" && (
                <div className="mt-4" data-testid="media-backup-progress">
                  <div className="flex justify-between text-[11px] text-ivory/60 mb-1.5">
                    <span>{mediaArchive.done || 0} / {mediaArchive.total || "…"} fichiers · partie {mediaArchive.part || 1}</span>
                    <span>{mediaArchive.total ? Math.round(((mediaArchive.done || 0) / mediaArchive.total) * 100) : 0} %</span>
                  </div>
                  <div className="h-2 rounded-full bg-ink overflow-hidden border border-ivory/10">
                    <div className="h-full bg-gold transition-all duration-500"
                      style={{ width: `${mediaArchive.total ? Math.round(((mediaArchive.done || 0) / mediaArchive.total) * 100) : 5}%` }} />
                  </div>
                  <p className="text-[11px] text-ivory/50 mt-2">Vous pouvez quitter cette page — la construction continue sur le serveur.</p>
                </div>
              )}
              {mediaArchive?.status === "done" && (mediaArchive.parts || []).length > 0 && (
                <div className="mt-4" data-testid="media-backup-parts">
                  <p className="text-xs text-emerald-400 mb-2">
                    Archive prête : {mediaArchive.done} fichiers · {mediaArchive.size_mb} Mo au total
                    {mediaArchive.skipped ? ` (${mediaArchive.skipped} fichiers illisibles sautés)` : ""} —
                    téléchargez chaque partie :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {/* OPTION « UN SEUL LIEN » (demande du propriétaire,
                        2026-10-24) : toutes les parties fusionnées en UN seul
                        ZIP téléchargé d'un coup (fusion en flux sur le serveur,
                        rien d'écrit au disque) — peut être long ; les parties
                        restent dispo en secours */}
                    <a href={`${API_BASE}/api/admin/backup/media/download-all`}
                      data-testid="media-backup-download-all"
                      title="Tout télécharger en un seul fichier ZIP (peut être long — les parties restent disponibles en secours)"
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] bg-gold text-ink font-semibold hover:bg-champagne transition-colors duration-300">
                      <Download className="w-3 h-3" /> Un seul lien — tout en un fichier ({mediaArchive.size_mb} Mo)
                    </a>
                    {mediaArchive.parts.map((p) => (
                      <a key={p.no} href={`${API_BASE}/api/admin/backup/media/download/${p.no}`}
                        data-testid={`media-backup-part-${p.no}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] bg-gold/10 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                        <Download className="w-3 h-3" /> Partie {p.no} · {p.size_mb} Mo · {p.files} fichiers
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {mediaArchive?.status === "error" && (
                <p className="mt-3 text-xs text-red-400" data-testid="media-backup-error">
                  La construction a échoué : {mediaArchive.error} — réessayez.
                </p>
              )}
            </div>
          </div>

          <div className="bg-surface border border-ivory/10 rounded-xl p-7 mt-8" data-testid="about-ai-card">
            <p className="font-semibold mb-1">Page À propos — rédaction IA</p>
            <p className="text-xs text-ivory/70 mb-5">Claude rédige un texte « À propos » optimisé pour le référencement (SEO) à partir de vos 21 services. Révisez le brouillon ci-dessous, ajustez-le au besoin, puis publiez : il remplace le texte par défaut de la page À propos.</p>
            {shopSettings?.about_page?.updated_at && (
              <p className="text-xs text-gold mb-4" data-testid="about-ai-published">
                Texte personnalisé en ligne — publié le {new Date(shopSettings.about_page.updated_at).toLocaleDateString("fr-CA", { timeZone: TZ_MONTREAL })} par {shopSettings.about_page.updated_by}.
              </p>
            )}
            <div className="flex flex-wrap gap-3 mb-5">
              <button data-testid="about-ai-generate" onClick={generateAbout} disabled={aboutBusy}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-xs hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {aboutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Rédiger par l'IA (Claude)
              </button>
              {aboutDraft && (
                <button data-testid="about-ai-publish" onClick={publishAbout} disabled={aboutBusy}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold/10 transition-colors duration-300 disabled:opacity-60">
                  Publier sur la page À propos
                </button>
              )}
              {(aboutDraft || shopSettings?.about_page) && (
                <button data-testid="about-ai-reset" onClick={resetAbout} disabled={aboutBusy}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs border border-ivory/20 text-ivory/60 hover:border-red-400/50 hover:text-red-300 transition-colors duration-300 disabled:opacity-60">
                  Revenir au texte par défaut
                </button>
              )}
            </div>
            {aboutDraft && (
              <div className="grid md:grid-cols-2 gap-4" data-testid="about-ai-draft">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-ivory/65 mb-2">Français</label>
                  <textarea data-testid="about-ai-fr" rows={10} value={aboutDraft.fr}
                    onChange={(e) => setAboutDraft({ ...aboutDraft, fr: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm leading-relaxed focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-ivory/65 mb-2">English</label>
                  <textarea data-testid="about-ai-en" rows={10} value={aboutDraft.en}
                    onChange={(e) => setAboutDraft({ ...aboutDraft, en: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm leading-relaxed focus:border-gold focus:outline-none" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* photo plein écran : fermer ramène exactement où on en était (rien n'est rechargé) */}
      {photoPreview && (
        <div data-testid="admin-photo-preview" onClick={() => setPhotoPreview(null)}
          className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 cursor-zoom-out">
          <img src={photoPreview.src} alt={photoPreview.name} data-testid="admin-photo-preview-image"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          <ExpandableText text={photoPreview.name} testId="photo-preview-name"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 block text-center text-sm text-ivory/80 bg-ink/70 px-4 py-2 rounded-full max-w-[90vw]" />
          <button type="button" data-testid="admin-photo-preview-close" onClick={() => setPhotoPreview(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <Dialog open={promoCreateOpen} onOpenChange={setPromoCreateOpen}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Ajouter un code rabais</DialogTitle>
            <DialogDescription className="text-ivory/70 text-sm">
              Nom du code, rabais, portée, condition, dates de début et de fin.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={savePromo} data-testid="promo-create-form" className="space-y-4">
            <PromoFormFields form={promoForm} setForm={setPromoForm} idPrefix="promo" />
            <button type="submit" data-testid="promo-create" disabled={savingPromo}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 text-sm">
              {savingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer le code rabais
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPromo} onOpenChange={(open) => !open && setEditingPromo(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Modifier le code {editingPromo?.code}</DialogTitle>
            <DialogDescription className="text-ivory/70 text-sm">
              Renommez le code, changez le rabais, les dates de début/fin ou la condition. L'historique d'utilisations est conservé.
            </DialogDescription>
          </DialogHeader>
          {editingPromo && (
            <form onSubmit={updatePromo} data-testid="promo-edit-form" className="space-y-4">
              <PromoFormFields form={editingPromo} setForm={setEditingPromo} idPrefix="promo-edit" />
              <label className="flex items-center gap-3 text-sm text-ivory/70 select-none">
                <Switch data-testid="promo-edit-active" checked={editingPromo.active !== false}
                  onCheckedChange={(v) => setEditingPromo({ ...editingPromo, active: v })} />
                Code actif (désactivez pour y mettre fin sans le supprimer)
              </label>
              <button type="submit" data-testid="promo-edit-save" disabled={savingPromoEdit}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60 text-sm">
                {savingPromoEdit && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer les modifications
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!soraPost} onOpenChange={(open) => !open && setSoraPost(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Vidéo Sora (OpenAI)</DialogTitle>
            <DialogDescription className="text-ivory/60">
              Une vidéo générée par Sora illustrera l'article <span className="text-gold">« {soraPost?.title} »</span> sur la page du blogue. La génération prend 2 à 5 minutes.
            </DialogDescription>
          </DialogHeader>
          {soraPost && (
            <div className="space-y-4" data-testid="sora-dialog">
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Description de la vidéo <span className="text-gold">*</span></label>
                <textarea data-testid="sora-prompt" rows={4} maxLength={1000} value={soraForm.prompt}
                  onChange={(e) => setSoraForm((f) => ({ ...f, prompt: e.target.value }))}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Format</label>
                  <Select value={soraForm.size} onValueChange={(v) => setSoraForm((f) => ({ ...f, size: v }))}>
                    <SelectTrigger data-testid="sora-size" className="bg-ink border-ivory/15"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-gold/20 text-ivory">
                      <SelectItem value="1280x720">Paysage (1280×720)</SelectItem>
                      <SelectItem value="1792x1024">Écran large (1792×1024)</SelectItem>
                      <SelectItem value="1024x1792">Portrait (1024×1792)</SelectItem>
                      <SelectItem value="1024x1024">Carré (1024×1024)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Durée</label>
                  <Select value={String(soraForm.duration)} onValueChange={(v) => setSoraForm((f) => ({ ...f, duration: parseInt(v) }))}>
                    <SelectTrigger data-testid="sora-duration" className="bg-ink border-ivory/15"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-gold/20 text-ivory">
                      <SelectItem value="4">4 secondes</SelectItem>
                      <SelectItem value="8">8 secondes</SelectItem>
                      <SelectItem value="12">12 secondes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <button data-testid="sora-generate" disabled={soraLoading} onClick={generateSora}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {soraLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} Générer la vidéo
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{editingPost?.id ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
          </DialogHeader>
          {editingPost && (
            <form onSubmit={savePost} className="space-y-4" data-testid="post-form">
              <div className="border border-gold/25 rounded-lg p-4 bg-gold/5">
                <p className="text-xs text-gold uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Rédaction par IA
                </p>
                <div className="flex gap-2">
                  <input data-testid="ai-blog-topic" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="Sujet (ex. : trophées pour tournoi de hockey)"
                    className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
                  <button type="button" data-testid="ai-blog-write" disabled={aiLoading} onClick={aiWriteBlog}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 whitespace-nowrap shrink-0">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Rédiger
                  </button>
                </div>
                <label className="flex items-start gap-2 text-xs text-ivory/70 mt-2 cursor-pointer" data-testid="ai-blog-guide-label">
                  <input type="checkbox" data-testid="ai-blog-guide" checked={aiGuide}
                    onChange={(e) => setAiGuide(e.target.checked)} className="accent-[#d4af37] mt-0.5" />
                  <span><strong className="text-gold">Mode « Guide à citer »</strong> — article de référence plus long (chiffres, listes, questions fréquentes) conçu pour que d'autres sites le citent : chaque citation est un backlink vers mrtrophee.ca.</span>
                </label>
                <p className="text-[11px] text-ivory/65 mt-2">Claude rédige titre, résumé et contenu en FR + EN — relisez toujours avant de publier.</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Titre <span className="text-gold">*</span></label>
                <input data-testid="post-form-title" required minLength={3} value={editingPost.title}
                  onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Titre (anglais)</label>
                <input data-testid="post-form-title-en" value={editingPost.title_en}
                  onChange={(e) => setEditingPost({ ...editingPost, title_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Résumé (carte + référencement)</label>
                <textarea data-testid="post-form-excerpt" rows={2} maxLength={400} value={editingPost.excerpt}
                  onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Résumé (anglais)</label>
                <textarea data-testid="post-form-excerpt-en" rows={2} maxLength={400} value={editingPost.excerpt_en}
                  onChange={(e) => setEditingPost({ ...editingPost, excerpt_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none resize-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Mots-clés ciblés de l'article (référencement longue traîne)</label>
                <input data-testid="post-form-keywords" value={editingPost.seo_keywords || ""}
                  onChange={(e) => setEditingPost({ ...editingPost, seo_keywords: e.target.value })}
                  placeholder="Ex. : comment choisir un trophée de hockey, prix trophée personnalisé Québec"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <p className="text-xs text-ivory/65 mt-1">Séparés par des virgules. Diffusés EN TÊTE des mots-clés de la page de l'article (Google, Bing, IA). Rédigés automatiquement par l'IA, modifiables ici.</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Mots-clés ciblés (anglais)</label>
                <input data-testid="post-form-keywords-en" value={editingPost.seo_keywords_en || ""}
                  onChange={(e) => setEditingPost({ ...editingPost, seo_keywords_en: e.target.value })}
                  placeholder="Ex.: how to choose a hockey trophy, custom trophy price Quebec"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Contenu <span className="text-gold">*</span></label>
                <textarea data-testid="post-form-content" required minLength={10} rows={10} value={editingPost.content}
                  onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <p className="text-xs text-ivory/65 mt-1">Les sauts de ligne sont respectés à l'affichage.</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Contenu (anglais)</label>
                <textarea data-testid="post-form-content-en" rows={6} value={editingPost.content_en}
                  onChange={(e) => setEditingPost({ ...editingPost, content_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Image à la une</label>
                <div className="flex items-center gap-3">
                  {editingPost.image && (
                    <img src={editingPost.image} alt="" data-testid="post-form-image-preview"
                      className="w-14 h-14 rounded-lg object-contain bg-[#f4f2ec] border border-ivory/15 shrink-0" />
                  )}
                  <input data-testid="post-form-image" value={editingPost.image} onChange={(e) => setEditingPost({ ...editingPost, image: e.target.value })}
                    placeholder="https://… ou téléversez un fichier"
                    className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <input ref={postImageInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                    data-testid="post-form-image-file" onChange={uploadPostImage} />
                  <button type="button" data-testid="post-form-image-upload" disabled={uploadingImage}
                    onClick={() => postImageInput.current?.click()}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-3 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60 whitespace-nowrap shrink-0">
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="post-form-published" checked={editingPost.published}
                  onCheckedChange={(v) => setEditingPost({ ...editingPost, published: v })} />
                <span className="text-sm text-ivory/70">Publié (visible sur le site)</span>
              </div>
              <button data-testid="post-form-save" type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={nlOpen} onOpenChange={setNlOpen}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Envoi à l'info-lettre</DialogTitle>
          </DialogHeader>
          <form onSubmit={sendNewsletter} className="space-y-4" data-testid="newsletter-form">
            <p className="text-xs text-ivory/70">
              Envoyé aux {customers.filter((c) => c.newsletter).length} abonné(s) (clients ayant coché « info-lettre » à l'inscription). Un avis de désabonnement est inclus automatiquement.
            </p>
            {/* ILLUSTRATION DE SAISON dessinée par Gemini (demande du
                propriétaire, 2026-10-27) — en tête du courriel, aux couleurs
                du thème en vigueur ; « Retirer » revient au courriel sobre */}
            <div data-testid="newsletter-img-section">
              <label className="block text-sm text-ivory/70 mb-1.5">Illustration du haut (couleurs de la saison)</label>
              <div className="flex items-center gap-3 flex-wrap">
                {shopSettings?.infolettre_img && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${shopSettings.infolettre_img}`}
                    alt="Illustration d'info-lettre dessinée par Gemini" data-testid="newsletter-img-apercu"
                    className="h-12 w-32 object-cover rounded-lg border border-gold/30 bg-black" />
                )}
                <button type="button" data-testid="newsletter-img-generer" disabled={nlImgBusy}
                  onClick={async () => {
                    if (nlImgBusy) return;
                    setNlImgBusy(true);
                    try {
                      const { data } = await api.post("/admin/infolettre/image", {});
                      setShopSettings((s) => ({ ...s, infolettre_img: data.path }));
                      toast.success(data.theme
                        ? `Illustration dessinée par Gemini aux couleurs de « ${data.theme} ».`
                        : "Illustration dessinée par Gemini (or classique).");
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    finally { setNlImgBusy(false); }
                  }}
                  className="inline-flex items-center gap-2 text-xs bg-gold text-ink font-semibold rounded-full px-4 py-2 hover:bg-champagne transition-colors disabled:opacity-60">
                  {nlImgBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {shopSettings?.infolettre_img ? "Redessiner avec Gemini" : "Illustrer avec Gemini"}
                </button>
                {shopSettings?.infolettre_img && (
                  <button type="button" data-testid="newsletter-img-retirer"
                    onClick={async () => {
                      try {
                        await api.patch("/admin/settings", { infolettre_img: "" });
                        setShopSettings((s) => ({ ...s, infolettre_img: "" }));
                        toast.success("Illustration retirée — le courriel part sobre.");
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    className="text-xs text-red-300/90 hover:text-red-200 border border-red-400/30 rounded-full px-3 py-1.5 transition-colors">
                    Retirer
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-1.5">Objet</label>
              <input data-testid="newsletter-subject" required minLength={3} maxLength={150} value={nlSubject}
                onChange={(e) => setNlSubject(e.target.value)} placeholder="Ex. Nouveautés du mois — et un code rabais!"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-1.5">Message</label>
              <textarea data-testid="newsletter-message" required minLength={10} maxLength={10000} rows={7} value={nlMessage}
                onChange={(e) => setNlMessage(e.target.value)} placeholder="Bonjour, voici nos promotions du mois…"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none resize-y" />
            </div>
            <button data-testid="newsletter-send" type="submit" disabled={nlSending}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {nlSending && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer à tous les abonnés
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingCustomer} onOpenChange={(open) => !open && setEditingCustomer(null)}>        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Fiche client</DialogTitle>
          </DialogHeader>
          {editingCustomer && (
            <form onSubmit={saveCustomer} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Nom complet</label>
                  <input data-testid="customer-form-name" required minLength={2} value={editingCustomer.name}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Téléphone</label>
                  <input data-testid="customer-form-phone" value={editingCustomer.phone || ""}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: formatPhone(e.target.value) })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Courriel</label>
                <input data-testid="customer-form-email" required type="email" value={editingCustomer.email}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Adresse</label>
                <input data-testid="customer-form-address" value={editingCustomer.address1 || ""}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, address1: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-sm text-ivory/70 mb-1.5">Ville</label>
                  <input data-testid="customer-form-city" value={editingCustomer.city || ""}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, city: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Prov.</label>
                  <input data-testid="customer-form-province" maxLength={2} value={editingCustomer.province || ""}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, province: e.target.value.toUpperCase() })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Code postal</label>
                  <input data-testid="customer-form-postal" maxLength={7} value={editingCustomer.postal_code || ""}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, postal_code: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
              </div>
              <label data-testid="customer-form-newsletter" className="flex items-center gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={!!editingCustomer.newsletter}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, newsletter: e.target.checked })}
                  className="w-4 h-4 accent-[#d4af37]" />
                <span className="text-sm text-ivory/70">Abonné à l'info-lettre</span>
              </label>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Nouveau mot de passe <span className="text-ivory/65 text-xs">(laisser vide pour ne pas changer — le mot de passe actuel n'est jamais affiché)</span></label>
                <input data-testid="customer-form-password" type="password" minLength={6} autoComplete="new-password"
                  value={editingCustomer.new_password || ""}
                  onChange={(e) => setEditingCustomer({ ...editingCustomer, new_password: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <button data-testid="customer-form-save" type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          )}
          {/* Historique des ventes du client (demande du propriétaire) : soumissions
              + commandes avec étapes datées, regroupées dans la fiche */}
          {editingCustomer && <CustomerHistory customerId={editingCustomer.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Commande {detail?.order_number}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6" data-testid="order-detail-dialog">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-ink rounded-lg p-4 border border-ivory/10">
                  <p className="text-xs uppercase tracking-widest text-ivory/70 mb-2">Client</p>
                  <p className="font-semibold">{custName(detail.customer)}</p>
                  {detail.customer.company && <p className="text-ivory/70">{detail.customer.company}</p>}
                  {(detail.customer.email || detail.user_email) && <p className="text-ivory/70">{detail.customer.email || detail.user_email}</p>}
                  {detail.customer.phone && <p className="text-ivory/70">{detail.customer.phone}</p>}
                  {detail.user_email && detail.user_email !== detail.customer.email && (
                    <p className="text-ivory/70 text-xs">Compte : {detail.user_email}</p>
                  )}
                </div>
                <div className="bg-ink rounded-lg p-4 border border-ivory/10">
                  <p className="text-xs uppercase tracking-widest text-ivory/70 mb-2">Livraison</p>
                  {detail.delivery_method === "ramassage" ? (
                    <p className="text-gold" data-testid="admin-pickup-badge">Ramassage à l'atelier — Léry</p>
                  ) : detail.delivery_method === "main_propre" ? (
                    <>
                      <p className="text-gold" data-testid="admin-hand-badge">Livraison en main propre — par Mr Trophée</p>
                      <p>{custAddress(detail.customer)}</p>
                      <p className="text-ivory/70">{detail.customer.city} {detail.customer.postal_code}</p>
                    </>
                  ) : (
                    <>
                      <p>{custAddress(detail.customer)}</p>
                      <p className="text-ivory/70">{detail.customer.city} {detail.customer.postal_code}</p>
                    </>
                  )}
                  <p className="text-gold mt-1">Souhaitée : {detail.customer.delivery_date}</p>
                </div>
              </div>
              {/* Lien du reçu Stripe (demande du propriétaire, 2026-10-06) :
                  preuve du paiement par carte, conservé sur la commande */}
              {detail.receipt_url && (
                <p className="text-sm" data-testid="order-receipt-row">
                  <a href={detail.receipt_url} target="_blank" rel="noreferrer"
                    data-testid="order-receipt-link"
                    className="inline-flex items-center gap-1.5 text-gold hover:text-champagne underline underline-offset-2 transition-colors">
                    Reçu Stripe du paiement par carte
                  </a>
                </p>
              )}
              {detail.customer.notes && (
                <p className="text-sm text-ivory/60 italic border-l-2 border-gold/40 pl-3">« {detail.customer.notes} »</p>
              )}
              <div className="space-y-3">
                {/* Correction des prix sur commande payée (demande du propriétaire) :
                    recalcule ligne, sous-total, taxes et total — tracé dans l'historique */}
                {!["en_attente_paiement", "annulee", "remboursee"].includes(detail.status) && (
                  <div className="flex justify-end">
                    <button type="button"
                      data-testid="order-price-edit-toggle"
                      onClick={() => { setPriceEdit((v) => !v); setPriceDrafts({}); }}
                      className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                      <Pencil className="w-3 h-3" /> {priceEdit ? "Annuler la correction" : "Corriger les prix"}
                    </button>
                  </div>
                )}
                {detail.items.map((i, k) => (
                  <div key={k} className="flex justify-between gap-4 text-sm border-b border-ivory/10 pb-3">
                    <div>
                      <p>{i.qty} × {i.name} <span className="text-ivory/65">({i.size_label})</span></p>
                      <p className="text-ivory/70 text-xs">{i.inscription_label}</p>
                      {/* Gravure ligne par ligne (demande du propriétaire 2026-10-10) */}
                      <GravureLignes texte={i.inscription_text} testid={`order-detail-gravure-${k}`} />
                      {priceEdit && (
                        <div className="mt-2 flex items-center gap-2">
                          <label className="text-[10px] uppercase tracking-widest text-ivory/55">Prix unitaire</label>
                          <input type="number" min="0" step="0.01" inputMode="decimal"
                            data-testid={`order-price-input-${k}`}
                            defaultValue={i.unit_price}
                            onChange={(e) => setPriceDrafts((d) => ({ ...d, [k]: e.target.value }))}
                            className="w-28 bg-surface border border-gold/30 rounded-lg px-2.5 py-1.5 text-xs focus:border-gold focus:outline-none" />
                          <span className="text-[10px] text-ivory/55">× {i.qty}</span>
                        </div>
                      )}
                      {i.logo_path && (
                        <a
                          data-testid={`order-logo-${k}`}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${i.logo_path}`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-gold hover:text-champagne inline-flex items-center gap-1 mt-1"
                        >
                          <FileImage className="w-3.5 h-3.5" /> Logo : {i.logo_name}
                        </a>
                      )}
                    </div>
                    <p className="text-champagne whitespace-nowrap">{fmt(i.line_total)}</p>
                  </div>
                ))}
                {detail.express && (
                  <p className="text-right text-sm text-gold" data-testid="admin-express-fee">
                    Production express : {fmt(detail.express_fee || 0)}
                  </p>
                )}
                {priceEdit && (
                  <div className="flex justify-end pt-1">
                    <button type="button" onClick={saveOrderPrices} disabled={priceBusy}
                      data-testid="order-price-save"
                      className="inline-flex items-center gap-2 bg-gold text-ink text-xs font-bold px-5 py-2.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                      {priceBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Enregistrer les prix corrigés (taxes recalculées)
                    </button>
                  </div>
                )}
                {detail.shipping_fee != null && (
                  <>
                    {detail.subtotal != null && (
                      <p className="text-right text-sm text-ivory/60" data-testid="admin-subtotal">
                        Sous-total : {fmt(detail.subtotal)}
                      </p>
                    )}
                    {detail.promo && (
                      <p className="text-right text-sm text-gold" data-testid="admin-promo-discount">
                        Rabais ({detail.promo.code}) : −{fmt(detail.promo.discount)}
                      </p>
                    )}
                    <p className="text-right text-sm text-ivory/60" data-testid="admin-shipping-fee">
                      {detail.delivery_method === "ramassage" ? "Ramassage à l'atelier : Gratuit"
                        : detail.delivery_method === "main_propre" ? `Livraison en main propre : ${detail.shipping_fee === 0 ? "Gratuite" : fmt(detail.shipping_fee)}`
                        : `Livraison : ${detail.shipping_fee === 0 ? "Gratuite" : fmt(detail.shipping_fee)}`}
                    </p>
                  </>
                )}
                {detail.taxes && (
                  <div className="text-right text-sm text-ivory/60" data-testid="admin-taxes">
                    {detail.taxes.tps > 0 && <p>TPS (5 %) : {fmt(detail.taxes.tps)}</p>}
                    {detail.taxes.tvq > 0 && <p>TVQ (9,975 %) : {fmt(detail.taxes.tvq)}</p>}
                    {detail.taxes.tvh > 0 && <p>TVH : {fmt(detail.taxes.tvh)}</p>}
                  </div>
                )}
                {(detail.refunded_total || 0) > 0 && (
                  <p className="text-right text-sm text-red-300" data-testid="admin-refunded-total">
                    Remboursé : −{fmt(detail.refunded_total)}
                  </p>
                )}
                <p className="text-right font-serif text-xl text-gold" data-testid="admin-total">Total : {fmt(detail.total)}</p>
              </div>
              <div className="flex flex-wrap items-end gap-4 border-t border-ivory/10 pt-5">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs uppercase tracking-widest text-ivory/70 mb-2">Numéro de suivi</label>
                  <div className="flex gap-2">
                    <Select value={carrier} onValueChange={setCarrier}>
                      <SelectTrigger data-testid="carrier-select" className="w-[150px] bg-ink border-ivory/15">
                        <SelectValue placeholder="Transporteur" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-gold/20 text-ivory">
                        <SelectItem value="postes_canada">Postes Canada</SelectItem>
                        <SelectItem value="ups">UPS</SelectItem>
                        <SelectItem value="fedex">FedEx</SelectItem>
                        <SelectItem value="purolator">Purolator</SelectItem>
                        <SelectItem value="dhl">DHL</SelectItem>
                        <SelectItem value="gls" data-testid="carrier-option-gls">GLS</SelectItem>
                        <SelectItem value="ics" data-testid="carrier-option-ics">ICS Courier</SelectItem>
                        <SelectItem value="autre" data-testid="carrier-option-autre">Autre (nom à inscrire)</SelectItem>
                      </SelectContent>
                    </Select>
                    {carrier === "autre" && (
                      <input
                        data-testid="carrier-other-input"
                        value={carrierOther}
                        onChange={(e) => setCarrierOther(e.target.value)}
                        placeholder="Nom du transporteur *"
                        maxLength={60}
                        className="w-40 bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm focus:border-gold focus:outline-none"
                      />
                    )}
                    <input
                      data-testid="tracking-input"
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder="Ex. : 1Z999AA10123456784"
                      className="flex-1 bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none"
                    />
                    <button
                      data-testid="tracking-save"
                      onClick={saveTracking}
                      className="px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      Enregistrer
                    </button>
                  </div>
                  <p className="text-xs text-ivory/60 mt-2">Lien de suivi cliquable inclus automatiquement dans le courriel « expédiée ».</p>
                  {/* Lien de vérification créé avec le nº de suivi (demande du propriétaire) */}
                  {tracking.trim() && trackingUrlFor(carrier, tracking.trim()) && (
                    <a href={trackingUrlFor(carrier, tracking.trim())} target="_blank" rel="noreferrer"
                      data-testid="tracking-link-preview"
                      className="inline-block mt-1 text-xs text-gold hover:text-champagne underline underline-offset-2 transition-colors">
                      Vérifier ce numéro chez le transporteur ↗
                    </a>
                  )}
                  {(detail.tracking_url || trackingUrlFor(detail.tracking_carrier, detail.tracking_number)) && !tracking.trim() && (
                    <a href={detail.tracking_url || trackingUrlFor(detail.tracking_carrier, detail.tracking_number)}
                      target="_blank" rel="noreferrer" data-testid="tracking-link-saved"
                      className="inline-block mt-1 text-xs text-gold hover:text-champagne underline underline-offset-2 transition-colors">
                      Suivre {detail.tracking_number} ↗
                    </a>
                  )}
                  {/* Prix COÛTANT de l'envoi — visible par l'ADMIN SEULEMENT (demande
                      du propriétaire) : jamais montré au client ni aux vendeurs */}
                  {user?.role === "admin" && (
                    <div className="mt-3">
                      <label className="block text-xs uppercase tracking-widest text-amber-300/80 mb-1.5">
                        Prix coûtant de l'envoi ($) — admin seulement
                      </label>
                      <input
                        data-testid="ship-cost-input"
                        value={shipCost}
                        inputMode="decimal"
                        onChange={(e) => setShipCost(e.target.value.replace(/[^0-9.,]/g, ""))}
                        placeholder="ex. 8.02"
                        className="w-40 bg-ink border border-amber-400/30 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none"
                      />
                    </div>
                  )}
                  {/* Étiquette Stallion Express (demande du propriétaire) : création
                      + impression depuis la fiche, suivi automatique ensuite */}
                  {detail.delivery_method === "livraison" && (
                    <div className="mt-3" data-testid={`stallion-zone-${detail.order_number}`}>
                      <StallionLabel kind="order" docId={detail.order_number}
                        prefill={{ name: `${detail.customer?.first_name || ""} ${detail.customer?.last_name || ""}`.trim(),
                                   address1: detail.customer?.address1 || "", city: detail.customer?.city || "",
                                   province: detail.customer?.province || "QC",
                                   postal_code: detail.customer?.postal_code || "",
                                   phone: detail.customer?.phone || "", email: detail.customer?.email || "" }}
                        stallion={detail.stallion}
                        onSaved={(s) => setDetail((d) => ({ ...d, stallion: s }))} />
                    </div>
                  )}
                  {/* Signature de livraison (main propre / ramassage, demande du
                      propriétaire 2026-10-08) : preuve dans la fiche de commande */}
                  {detail.delivery_signature && (
                    <div className="mt-3 rounded-lg border border-gold/25 bg-gold/5 p-3" data-testid="order-signature">
                      {detail.delivery_signature.skipped ? (
                        <p className="text-xs text-amber-300/90">
                          Livrée SANS signature — raison : {detail.delivery_signature.reason}
                        </p>
                      ) : (
                        <div className="flex items-center gap-3">
                          <img src={`/api/files/${detail.delivery_signature.path}`} alt="Signature du client"
                            className="h-12 rounded bg-white px-2 py-1 border border-ivory/15" />
                          {detail.delivery_signature.photo_path && (
                            <img src={`/api/files/${detail.delivery_signature.photo_path}`} alt="Photo de livraison"
                              className="h-12 w-12 object-cover rounded border border-ivory/15" />
                          )}
                          <p className="text-xs text-ivory/80">
                            Reçu par <strong>{detail.delivery_signature.name}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {["payee", "en_production"].includes(detail.status) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid="refund-button"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-red-400/40 text-red-300 text-sm hover:bg-red-400/10 transition-colors duration-300"
                      >
                        <RotateCcw className="w-4 h-4" /> Rembourser
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-surface border-gold/20 text-ivory">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl">Rembourser {detail.order_number} ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-ivory/60">
                          Entrez un montant partiel ou laissez le montant complet. Le remboursement est envoyé via Stripe. Cette action est irréversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-3 py-2">
                        <div className="flex justify-between text-sm text-ivory/60">
                          <span>Total de la commande</span><span>{fmt(detail.total)}</span>
                        </div>
                        {(detail.refunded_total || 0) > 0 && (
                          <div className="flex justify-between text-sm text-red-300" data-testid="refund-already">
                            <span>Déjà remboursé</span><span>−{fmt(detail.refunded_total)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold text-gold">
                          <span>Reste remboursable</span>
                          <span data-testid="refund-remaining">{fmt(detail.total - (detail.refunded_total || 0))}</span>
                        </div>
                        <div className="relative">
                          <input
                            data-testid="refund-amount" type="number" step="0.01" min="0.01"
                            max={detail.total - (detail.refunded_total || 0)}
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            placeholder={String(Math.round((detail.total - (detail.refunded_total || 0)) * 100) / 100)}
                            className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">$ (vide = complet)</span>
                        </div>
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-ivory/20 text-ivory hover:bg-ivory/5" onClick={() => setRefundAmount("")}>Annuler</AlertDialogCancel>
                        <AlertDialogAction data-testid="refund-confirm" onClick={refundOrder} className="bg-red-500 text-white hover:bg-red-400">
                          Confirmer le remboursement
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{editing?.id ? "Modifier le produit" : "Nouveau produit"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveProduct} className="space-y-4">
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Nom
                  <TranslateBtn fromKey="name" toKey="name_en" target="en">Traduire en anglais</TranslateBtn>
                </label>
                <input data-testid="product-form-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Numéro de produit (optionnel)</label>
                <input data-testid="product-form-sku" value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                  placeholder="Ex. : TR-1042"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Nº de catalogue (optionnel)</label>
                <input data-testid="product-form-catalogue" value={editing.catalogue_no || ""} onChange={(e) => setEditing({ ...editing, catalogue_no: e.target.value })}
                  placeholder="Ex. : 3.0"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Groupe de modèles (optionnel)</label>
                <input data-testid="product-form-group" value={editing.group || ""} onChange={(e) => setEditing({ ...editing, group: e.target.value })}
                  placeholder="Ex. : Trophée Étoile — regroupe les modèles pareils"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <p className="text-xs text-ivory/65 mt-1.5">Les produits du même groupe se modifient ensemble via « Modification en lot ».</p>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Catégorie boutique (grande)</label>
                <Select value={editing.shop_category || ""} onValueChange={(v) => setEditing({ ...editing, shop_category: v })}>
                  <SelectTrigger data-testid="product-form-shop-category" className="bg-ink border-ivory/15"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-surface border-gold/20 text-ivory">
                    {["Trophées", "Médailles", "Plaques", "Autres"].map((c) => (
                      <SelectItem key={c} value={c} data-testid={`shopcat-${c.toLowerCase()}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Sous-catégorie boutique</label>
                <input list="product-form-shop-subcats" data-testid="product-form-shop-subcategory"
                  value={editing.shop_subcategory || ""} onChange={(e) => setEditing({ ...editing, shop_subcategory: e.target.value })}
                  placeholder="Ex. : Coupe Métal, Rubans, Bois de noyer…"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                <datalist id="product-form-shop-subcats">
                  {(prodFacets.shop_subcategories || []).map((s) => <option key={s} value={s} />)}
                </datalist>
                <p className="text-xs text-ivory/65 mt-1.5">Reclasser un produit = choisir sa catégorie boutique ici, ou en lot via « Modification en lot ».</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Page du catalogue (optionnel)</label>
                  <input data-testid="product-form-catalogue-page" type="number" min="1" step="1" value={editing.catalogue_page ?? ""}
                    onChange={(e) => setEditing({ ...editing, catalogue_page: e.target.value })}
                    placeholder="Ex. : 12"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Catégorie (anglais)</label>
                  <input data-testid="product-form-category-en" value={editing.category_en || ""} onChange={(e) => setEditing({ ...editing, category_en: e.target.value })}
                    placeholder="Ex. : Trophies"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Catégorie</label>
                  <input list="product-form-categories" data-testid="product-form-category" required
                    value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    placeholder="Ex. : Trophées"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <datalist id="product-form-categories">
                    {prodFacets.categories.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Prix de base ($)</label>
                  <input data-testid="product-form-price" type="number" step="0.01" min="0.5" required value={editing.base_price}
                    onChange={(e) => setEditing({ ...editing, base_price: e.target.value })}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                {user?.role === "admin" && (
                <div>
                  <label className="block text-sm text-amber-300/80 mb-1.5">Prix coûtant ($, optionnel) — admin seulement</label>
                  <input data-testid="product-form-cost" type="number" step="0.01" min="0" value={editing.cost_price ?? ""}
                    onChange={(e) => setEditing({ ...editing, cost_price: e.target.value })}
                    placeholder="Visible ici seulement"
                    className="w-full bg-ink border border-amber-400/30 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  {/* Paliers de coûtant par quantité (demande du propriétaire, 2026-10-20) :
                      lus de la liste de prix — la quantité de la caisse est indiquée
                      (« 1 caisse de 24 un. ») pour bâtir ses propres prix par quantité */}
                  {Array.isArray(editing.cost_tiers) && editing.cost_tiers.length > 0 && (
                    <div className="mt-2" data-testid="product-cost-tiers">
                      <p className="text-[11px] text-amber-300/70 mb-1">
                        Coûtants par quantité{editing.cost_source ? ` (liste « ${editing.cost_source} »)` : ""} :
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {editing.cost_tiers.map((t, i) => (
                          <span key={i} data-testid={`product-cost-tier-${i}`}
                            className="text-[10px] border border-amber-400/30 text-amber-200/90 rounded-full px-2.5 py-1">
                            {t.label} : <strong>{Number(t.cost ?? 0).toFixed(2)} $</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
              <div className="border border-ivory/10 rounded-lg p-4">
                <p className="text-sm text-gold mb-3">Prix des inscriptions ($ / unité)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-ivory/70 mb-1.5">Gravure laser</label>
                    <input data-testid="product-form-fee-gravure" type="number" step="0.01" min="0" required
                      value={editing.inscription_fees?.gravure ?? 0}
                      onChange={(e) => setEditing({ ...editing, inscription_fees: { ...editing.inscription_fees, gravure: e.target.value } })}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-ivory/70 mb-1.5">Impression couleur</label>
                    <input data-testid="product-form-fee-impression" type="number" step="0.01" min="0" required
                      value={editing.inscription_fees?.impression ?? 0}
                      onChange={(e) => setEditing({ ...editing, inscription_fees: { ...editing.inscription_fees, impression: e.target.value } })}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm text-ivory/70 mb-1.5">Inscription arrière</label>
                    <input data-testid="product-form-fee-arriere" type="number" step="0.01" min="0"
                      value={editing.inscription_fees?.arriere ?? 0}
                      onChange={(e) => setEditing({ ...editing, inscription_fees: { ...editing.inscription_fees, arriere: e.target.value } })}
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                    <p className="text-[10px] text-ivory/40 mt-1">0 = non offerte pour ce produit</p>
                  </div>
                </div>
              </div>
              <div className="border border-ivory/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gold">Grandeurs — prix de vente et coûtant</p>
                  <button type="button" data-testid="size-add" onClick={addSize}
                    className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </button>
                </div>
                <div className="space-y-3">
                  {(editing.sizes || []).map((s, i) => (
                    <div key={s.id || i} className="flex flex-wrap items-center gap-3" data-testid={`size-row-${i}`}>
                      <input data-testid={`size-label-${i}`} required value={s.label} placeholder="Ex. : Petite — 25 cm"
                        onChange={(e) => updateSize(i, { label: e.target.value })}
                        className="flex-1 min-w-[140px] bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                      <div className="relative w-32 shrink-0">
                        <input data-testid={`size-price-${i}`} type="number" step="0.01" min="0" required
                          value={s.price === null || s.price === undefined || s.price === ""
                            ? Math.round(((parseFloat(editing.base_price) || 0) + (parseFloat(s.extra) || 0)) * 100) / 100
                            : s.price}
                          onChange={(e) => updateSize(i, { price: e.target.value })}
                          title="Prix de VENTE de cette grandeur (pas un supplément)"
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">$ vente</span>
                      </div>
                      {user?.role === "admin" && (
                        <div className="relative w-32 shrink-0">
                          <input data-testid={`size-cost-${i}`} type="number" step="0.01" min="0"
                            value={s.cost ?? ""} placeholder="coûtant"
                            onChange={(e) => updateSize(i, { cost: e.target.value })}
                            title="Prix COÛTANT de cette grandeur — visible par l'admin seulement"
                            className="w-full bg-ink border border-amber-400/30 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-300/70 text-xs pointer-events-none">$ coût.</span>
                        </div>
                      )}
                      {(editing.sizes?.length || 0) > 1 && (
                        <button type="button" data-testid={`size-remove-${i}`} onClick={() => removeSize(i)}
                          className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-ivory/65 mt-3">Inscrivez le <strong>prix de vente</strong> de chaque grandeur (pas un supplément) — le coûtant est visible par l'admin seulement, jamais par le client.</p>
              </div>
              <div className="border border-ivory/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gold">Finis (couleurs) et suppléments</p>
                  <button type="button" data-testid="finish-add" onClick={addFinish}
                    className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Plus className="w-3.5 h-3.5" /> Ajouter
                  </button>
                </div>
                {(editing.finishes || []).length === 0 ? (
                  <p className="text-xs text-ivory/65" data-testid="finish-empty">Aucun fini — le produit est offert en une seule finition.</p>
                ) : (
                  <div className="space-y-3">
                    {(editing.finishes || []).map((f, i) => (
                      <div key={f.id || i} className="flex items-center gap-3" data-testid={`finish-row-${i}`}>
                        <input data-testid={`finish-label-${i}`} required value={f.label} placeholder="Ex. : Or antique"
                          onChange={(e) => updateFinish(i, { label: e.target.value })}
                          className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <input data-testid={`finish-label-en-${i}`} value={f.label_en || ""} placeholder="EN (ex. : Antique Gold)"
                          onChange={(e) => updateFinish(i, { label_en: e.target.value })}
                          className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <div className="relative w-32 shrink-0">
                          <input data-testid={`finish-extra-${i}`} type="number" step="0.01" min="0" required value={f.extra}
                            onChange={(e) => updateFinish(i, { extra: e.target.value })}
                            className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">+$</span>
                        </div>
                        <button type="button" data-testid={`finish-remove-${i}`} onClick={() => removeFinish(i)}
                          className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-ivory/65 mt-3">Le supplément s'ajoute au prix de base pour ce fini (0 $ si aucun). Laissez vide pour un produit à finition unique.</p>
              </div>
              {/* Rabais UNIQUE du produit (demande du propriétaire, 2026-10-22) :
                  « X % dès Y unités » ou juste « X % » (seuil vide = dès la 1re).
                  Prioritaire sur les règles par type/catalogue (Paramètres) */}
              <div className="border border-gold/25 bg-gold/5 rounded-lg p-4" data-testid="product-discount-rule-card">
                <p className="text-sm text-gold mb-1">Rabais unique du produit</p>
                <p className="text-xs text-ivory/65 mb-3">
                  Un seul rabais, simple : ex. « 10 % dès 12 unités ». Vide = aucun rabais au niveau produit
                  (une règle par type ou par catalogue, réglée dans Paramètres, peut s'appliquer).
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative w-32">
                    <input type="number" step="0.5" min="0" max="90" value={editing.discount_rule?.pct ?? ""}
                      onChange={(e) => setEditing({ ...editing, discount_rule: { ...(editing.discount_rule || {}), pct: e.target.value } })}
                      placeholder="aucun"
                      data-testid="product-discount-pct"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">% de rabais</span>
                  </div>
                  <div className="relative w-40">
                    <input type="number" min="0" step="1" value={editing.discount_rule?.min_qty ?? ""}
                      onChange={(e) => setEditing({ ...editing, discount_rule: { ...(editing.discount_rule || {}), min_qty: e.target.value } })}
                      placeholder="dès la 1re"
                      data-testid="product-discount-min-qty"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">unités min.</span>
                  </div>
                </div>
              </div>
              {/* Case « visibilité au client » des ANCIENS paliers (demande du
                  propriétaire, 2026-10-22) : par défaut les paliers sont retirés de
                  l'affichage ET du calcul (données conservées en réserve ci-dessous) ;
                  cochée = visibles et appliqués comme avant. Catalogue 5.0 (vrac) :
                  toujours visibles — les paliers SONT le prix de vente */}
              <label className="flex items-center justify-between border border-ivory/10 rounded-lg p-4 select-none cursor-pointer" data-testid="product-form-show-tiers-label">
                <div>
                  <p className="text-sm text-gold">Paliers de rabais visibles au client</p>
                  <p className="text-xs text-ivory/65 mt-0.5">
                    {String(editing.catalogue_no || "") === "5.0"
                      ? "Catalogue 5.0 (produits en vrac) : les paliers SONT le prix de vente — toujours visibles."
                      : "Par défaut les paliers sont cachés et non appliqués. Cochez pour les réactiver sur CE produit."}
                  </p>
                </div>
                <Switch data-testid="product-form-show-tiers" checked={!!editing.show_tiers_client}
                  disabled={String(editing.catalogue_no || "") === "5.0"}
                  onCheckedChange={(v) => setEditing({ ...editing, show_tiers_client: v })} />
              </label>
              {editing.show_tiers_client && (<>
              <div className="border border-ivory/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gold">Prix par quantité (tout inclus, max 5)</p>
                  {(editing.price_tiers?.length || 0) < 5 && (
                    <button type="button" data-testid="price-tier-add" onClick={addPriceTier}
                      className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                      <Plus className="w-3.5 h-3.5" /> Ajouter
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {(editing.price_tiers || []).map((tr, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`price-tier-row-${i}`}>
                      <div className="relative w-28 shrink-0">
                        <input data-testid={`price-tier-qty-${i}`} type="number" min="1" step="1" value={tr.min_qty}
                          onChange={(e) => updatePriceTier(i, { min_qty: e.target.value })}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">+</span>
                      </div>
                      <span className="text-ivory/65 text-sm shrink-0">→</span>
                      <div className="relative flex-1 min-w-0">
                        <input data-testid={`price-tier-price-${i}`} type="number" step="0.01" min="0.01" value={tr.price}
                          onChange={(e) => updatePriceTier(i, { price: e.target.value })}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">$ / unité</span>
                      </div>
                      <button type="button" data-testid={`price-tier-remove-${i}`} onClick={() => removePriceTier(i)}
                        className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-ivory/65 mt-3">Prix unitaire de base par palier de quantité — le supplément de grandeur s'ajoute ensuite. Sans palier, les rabais % automatiques s'appliquent.</p>
              </div>
              <div className="border border-ivory/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gold">Rabais automatiques par quantité (%, max 5)</p>
                  {(editing.tiers?.length || 0) < 5 && (
                    <button type="button" data-testid="discount-tier-add" onClick={addDiscountTier}
                      className="inline-flex items-center gap-1.5 text-xs border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300">
                      <Plus className="w-3.5 h-3.5" /> Ajouter
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {(editing.tiers || []).map((tr, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`discount-tier-row-${i}`}>
                      <div className="relative w-28 shrink-0">
                        <input data-testid={`discount-tier-qty-${i}`} type="number" min="1" step="1" value={tr.min_qty}
                          onChange={(e) => updateDiscountTier(i, { min_qty: e.target.value })}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">+</span>
                      </div>
                      <span className="text-ivory/65 text-sm shrink-0">→</span>
                      <div className="relative flex-1 min-w-0">
                        <input data-testid={`discount-tier-pct-${i}`} type="number" step="0.5" min="0.5" max="99" value={tr.pct}
                          onChange={(e) => updateDiscountTier(i, { pct: e.target.value })}
                          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">% de rabais</span>
                      </div>
                      <button type="button" data-testid={`discount-tier-remove-${i}`} onClick={() => removeDiscountTier(i)}
                        className="text-ivory/65 hover:text-red-400 transition-colors duration-300 shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-ivory/65 mt-3">Rabais en % sur le prix de base selon la quantité (ex. : 10+ → −10 %). Vide = aucun rabais. Les prix fixes par quantité ci-dessus ont priorité s'ils sont définis.</p>
              </div>
              </>)}
              {!editing.show_tiers_client && (editing.tiers?.length > 0 || editing.price_tiers?.length > 0) && (
                <p className="text-xs text-ivory/45 border border-ivory/10 rounded-lg p-3" data-testid="product-tiers-reserve-note">
                  Ce produit a des paliers en réserve ({(editing.tiers?.length || 0) + (editing.price_tiers?.length || 0)}) —
                  cachés et non appliqués tant que la case « Paliers de rabais visibles au client » n'est pas cochée.
                </p>
              )}
              <div className="border border-ivory/10 rounded-lg p-4">
                <p className="text-sm text-gold mb-3">Inventaire</p>
                <div className="relative w-40">
                  <input data-testid="product-form-stock" type="number" min="0" step="1" value={editing.stock ?? ""}
                    onChange={(e) => setEditing({ ...editing, stock: e.target.value })}
                    placeholder="Illimité"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 text-xs pointer-events-none">en stock</span>
                </div>
                <p className="text-xs text-ivory/65 mt-3">Vide = illimité. Le produit se désactive automatiquement quand l'inventaire atteint 0.</p>
              </div>
              <label className="flex items-center justify-between border border-ivory/10 rounded-lg p-4 select-none cursor-pointer" data-testid="product-form-featured-label">
                <div>
                  <p className="text-sm text-gold">Mise en vedette</p>
                  <p className="text-xs text-ivory/65 mt-0.5">Affiche ce produit dans « Nos coups de cœur » sur la page d'accueil.</p>
                </div>
                <Switch data-testid="product-form-featured" checked={!!editing.featured}
                  onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
              </label>
              <div className="flex items-center justify-between border border-gold/25 rounded-lg p-3 bg-gold/5">
                <p className="text-xs text-ivory/70">Laissez Claude rédiger l'accroche et les descriptions (FR + EN) à partir du nom du produit.</p>
                <button type="button" data-testid="ai-product-write" disabled={aiLoading} onClick={aiWriteProduct}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2.5 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50 whitespace-nowrap shrink-0 ml-3">
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Générer avec Claude
                </button>
              </div>
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Accroche
                  <TranslateBtn fromKey="tagline" toKey="tagline_en" target="en">Traduire en anglais</TranslateBtn>
                </label>
                <input data-testid="product-form-tagline" value={editing.tagline || ""} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Description
                  <TranslateBtn fromKey="description" toKey="description_en" target="en">Traduire en anglais</TranslateBtn>
                </label>
                <textarea data-testid="product-form-description" rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Image du produit</label>
                <div className="flex items-center gap-3">
                  {editing.image && (
                    <img src={editing.image} alt="" data-testid="product-form-image-preview"
                      className="w-14 h-14 rounded-lg object-contain bg-[#f4f2ec] border border-ivory/15 shrink-0" />
                  )}
                  <input data-testid="product-form-image" value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                    placeholder="https://… ou téléversez un fichier"
                    className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <input ref={imageInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                    data-testid="product-form-image-file" onChange={uploadImage} />
                  <button type="button" data-testid="product-form-image-upload" disabled={uploadingImage}
                    onClick={() => imageInput.current?.click()}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-3 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60 whitespace-nowrap shrink-0">
                    {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Téléverser
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Photos supplémentaires du même code (optionnel, max 10)</label>
                {(editing.images || []).length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-3" data-testid="product-form-images-list">
                    {(editing.images || []).map((u, i) => (
                      <div key={i} className="relative group" data-testid={`product-form-image-extra-${i}`}>
                        <img src={u} alt="" className="w-16 h-16 rounded-lg object-contain bg-[#f4f2ec] border border-ivory/15" />
                        <button type="button" data-testid={`product-form-image-extra-remove-${i}`}
                          onClick={() => setEditing((prev) => ({ ...prev, images: prev.images.filter((_, k) => k !== i) }))}
                          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-ink border border-ivory/25 text-ivory/60 hover:text-red-400 hover:border-red-400 flex items-center justify-center transition-colors duration-300">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={extraImageInput} type="file" multiple accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                  data-testid="product-form-images-file" onChange={uploadExtraImage} />
                <button type="button" data-testid="product-form-images-upload" disabled={uploadingImage}
                  onClick={() => extraImageInput.current?.click()}
                  className="inline-flex items-center gap-2 border border-dashed border-ivory/25 text-ivory/60 px-4 py-2.5 rounded-lg text-sm hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-60">
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Ajouter des photos (sélection multiple possible)
                </button>
                <p className="text-xs text-ivory/65 mt-1.5">Elles s'affichent en galerie sous la photo principale de la fiche produit.</p>
              </div>
              {/* Photos dédiées : ruban (médailles) et insertion (s'il y a lieu) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border border-ivory/10 rounded-lg p-4">
                  <label className="block text-sm text-ivory/70 mb-1.5">Photo du ruban (médailles)</label>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-ink border border-ivory/20 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                      {editing.image_ruban ? (
                        <img src={editing.image_ruban} alt="" className="w-full h-full object-cover" data-testid="product-form-ruban-preview" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-ivory/30" />
                      )}
                    </div>
                    <input value={editing.image_ruban || ""} data-testid="product-form-ruban-url"
                      onChange={(e) => setEditing({ ...editing, image_ruban: e.target.value })}
                      placeholder="URL ou Choisir →"
                      className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                    <button type="button" onClick={() => rubanInput.current?.click()} disabled={uploadingImage}
                      data-testid="product-form-ruban-upload"
                      className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-2 rounded-lg text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 shrink-0">
                      <Upload className="w-3.5 h-3.5" /> Choisir
                    </button>
                  </div>
                </div>
                <div className="border border-ivory/10 rounded-lg p-4">
                  <label className="block text-sm text-ivory/70 mb-1.5">Photo de l'insertion (s'il y a lieu)</label>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-ink border border-ivory/20 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                      {editing.image_insert ? (
                        <img src={editing.image_insert} alt="" className="w-full h-full object-cover" data-testid="product-form-insert-preview" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-ivory/30" />
                      )}
                    </div>
                    <input value={editing.image_insert || ""} data-testid="product-form-insert-url"
                      onChange={(e) => setEditing({ ...editing, image_insert: e.target.value })}
                      placeholder="URL ou Choisir →"
                      className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
                    <button type="button" onClick={() => insertInput.current?.click()} disabled={uploadingImage}
                      data-testid="product-form-insert-upload"
                      className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-2 rounded-lg text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-50 shrink-0">
                      <Upload className="w-3.5 h-3.5" /> Choisir
                    </button>
                  </div>
                </div>
              </div>
              <input ref={rubanInput} type="file" accept="image/*" className="hidden" data-testid="product-form-ruban-input" onChange={uploadSlotImage("image_ruban", rubanInput)} />
              <input ref={insertInput} type="file" accept="image/*" className="hidden" data-testid="product-form-insert-input" onChange={uploadSlotImage("image_insert", insertInput)} />
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Nom (anglais)
                  <TranslateBtn fromKey="name_en" toKey="name" target="fr">Traduire en français</TranslateBtn>
                </label>
                <input data-testid="product-form-name-en" value={editing.name_en || ""} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Accroche (anglais)
                  <TranslateBtn fromKey="tagline_en" toKey="tagline" target="fr">Traduire en français</TranslateBtn>
                </label>
                <input data-testid="product-form-tagline-en" value={editing.tagline_en || ""} onChange={(e) => setEditing({ ...editing, tagline_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="flex items-center justify-between text-sm text-ivory/70 mb-1.5">Description (anglais)
                  <TranslateBtn fromKey="description_en" toKey="description" target="fr">Traduire en français</TranslateBtn>
                </label>
                <textarea data-testid="product-form-description-en" rows={2} value={editing.description_en || ""} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <button data-testid="product-form-save" type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
